import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import flags from 'country-flag-icons/react/3x2';
import {
  Camera, Upload, Save, Share2, Phone, Mail, Globe,
  Linkedin, Twitter, Instagram, Github, Edit3, Eye,
  X, Check, User, MapPin, Briefcase, Lock, LogIn, AlertCircle,
  Plus, Trash2, ArrowLeft, Users, ExternalLink, RefreshCw,
  Download, FileText, Calendar, Video, Music, ShoppingCart,
  Link as LinkIcon, Youtube, Facebook, MessageCircle, Sun, Moon,
  ChevronUp, ChevronDown, GripVertical, Settings
} from 'lucide-react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { arrayMove } from '@dnd-kit/sortable';
import * as XLSX from 'xlsx';
import i18n from './i18n';
import BulkUserImport from './BulkUserImport';

const API_ENDPOINT = '/api';
const APP_VERSION = require('../package.json').version; // Automatically read from package.json
const GITHUB_URL = 'https://github.com/niklasmetzger/VCARD3.0';

// Try to read branch info from active-branch.json (generated at build time)
let GIT_BRANCH = null;
try {
  const branchInfo = require('./active-branch.json');
  GIT_BRANCH = branchInfo.branch;
} catch (e) {
  // active-branch.json doesn't exist yet (first run before build)
  GIT_BRANCH = null;
}
const vcardTheme = require('./theme/vcard');
const minimalTheme = require('./theme/minimal');
const mintTheme = require('./theme/mint');
const THEME_FILES = { vcard: vcardTheme, minimal: minimalTheme, mint: mintTheme };

const THEME_PRESETS = {
  vcard: [
    { name: "indigo", gradient: "from-indigo-600 to-purple-600", button: "bg-indigo-600 hover:bg-indigo-700", link: "text-indigo-600 bg-indigo-50 border-indigo-100 hover:bg-indigo-100", text: "text-indigo-600" },
    { name: "blue", gradient: "from-blue-600 to-cyan-600", button: "bg-blue-600 hover:bg-blue-700", link: "text-blue-600 bg-blue-50 border-blue-100 hover:bg-blue-100", text: "text-blue-600" },
    { name: "rose", gradient: "from-rose-500 to-orange-500", button: "bg-rose-600 hover:bg-rose-700", link: "text-rose-600 bg-rose-50 border-rose-100 hover:bg-rose-100", text: "text-rose-600" },
    { name: "emerald", gradient: "from-emerald-500 to-teal-500", button: "bg-emerald-600 hover:bg-emerald-700", link: "text-emerald-600 bg-emerald-50 border-emerald-100 hover:bg-emerald-100", text: "text-emerald-600" },
    { name: "slate", gradient: "from-slate-700 to-slate-900", button: "bg-slate-800 hover:bg-slate-900", link: "text-slate-700 bg-border-subtle border-slate-200 hover:bg-slate-200", text: "text-slate-800" }
  ],
  minimal: [
    { name: "mono-dark", gradient: "from-neutral-900 to-neutral-700", button: "bg-neutral-900 hover:bg-neutral-800", link: "text-neutral-900 bg-neutral-100 border-neutral-200 hover:bg-neutral-200", text: "text-neutral-900" },
    { name: "mono-mid", gradient: "from-neutral-700 to-neutral-500", button: "bg-neutral-700 hover:bg-neutral-600", link: "text-neutral-700 bg-neutral-50 border-neutral-200 hover:bg-neutral-100", text: "text-neutral-700" },
    { name: "mono-light", gradient: "from-neutral-300 to-neutral-200", button: "bg-neutral-200 hover:bg-neutral-300 text-neutral-900", link: "text-neutral-700 bg-white border-neutral-200 hover:bg-neutral-100", text: "text-neutral-700" }
  ],
  mint: [
    { name: "mint-fresh", gradient: "from-emerald-400 to-teal-400", button: "bg-emerald-400 hover:bg-emerald-500", link: "text-teal-900 bg-emerald-50 border-emerald-100 hover:bg-emerald-100", text: "text-teal-900" },
    { name: "mint-petrol", gradient: "from-slate-900 to-indigo-950", button: "bg-slate-950 hover:bg-slate-900", link: "text-slate-900 bg-slate-100 border-slate-200 hover:bg-slate-200", text: "text-slate-900" },
    { name: "mint-nightfall", gradient: "from-blue-900 to-slate-900", button: "bg-blue-900 hover:bg-blue-950", link: "text-blue-900 bg-blue-50 border-blue-100 hover:bg-blue-100", text: "text-blue-900" }
  ]
};

const buildI18nOptions = (defaultValue, options = {}) => {
  const next = { ...options };
  if (defaultValue !== undefined) {
    next.defaultValue = defaultValue;
  }
  return next;
};

const translateText = (key, defaultValue, options = {}) => (
  i18n.t(key, buildI18nOptions(defaultValue, options))
);

const useAppTranslation = () => {
  const { t, i18n: i18nInstance } = useTranslation();

  const translateKey = useCallback((key, defaultValue, options = {}) => (
    t(key, buildI18nOptions(defaultValue, options))
  ), [t]);

  return {
    t: translateKey,
    i18n: i18nInstance
  };
};

const APP_THEME_OVERRIDE_TOKENS = [
  { key: 'main', labelKey: 'settings.appTheme.tokens.main', defaultLabel: 'Main Background' },
  { key: 'card', labelKey: 'settings.appTheme.tokens.card', defaultLabel: 'Card Background' },
  { key: 'surface', labelKey: 'settings.appTheme.tokens.surface', defaultLabel: 'Surface Background' },
  { key: 'action', labelKey: 'settings.appTheme.tokens.action', defaultLabel: 'Primary Action' },
  { key: 'actionHover', labelKey: 'settings.appTheme.tokens.actionHover', defaultLabel: 'Primary Action Hover' },
  { key: 'confirm', labelKey: 'settings.appTheme.tokens.confirm', defaultLabel: 'Secondary Action' },
  { key: 'confirmHover', labelKey: 'settings.appTheme.tokens.confirmHover', defaultLabel: 'Secondary Action Hover' },
  { key: 'textPrimary', labelKey: 'settings.appTheme.tokens.textPrimary', defaultLabel: 'Primary Text' },
  { key: 'textSecondary', labelKey: 'settings.appTheme.tokens.textSecondary', defaultLabel: 'Secondary Text' },
  { key: 'textMuted', labelKey: 'settings.appTheme.tokens.textMuted', defaultLabel: 'Muted Text' },
  { key: 'border', labelKey: 'settings.appTheme.tokens.border', defaultLabel: 'Border' },
  { key: 'inputBg', labelKey: 'settings.appTheme.tokens.inputBg', defaultLabel: 'Input Background' }
];

const normalizeThemeVariantClient = (variant) => {
  const normalized = typeof variant === 'string' ? variant.trim().toLowerCase() : '';
  if (normalized === 'nagarro') return 'mint';
  if (normalized === 'vcard' || normalized === 'minimal' || normalized === 'mint' || normalized === 'custom') {
    return normalized;
  }
  return 'vcard';
};

// Define applyThemeCssVars outside component to ensure it's always available
// This function sets all CSS custom properties from the selected theme file
const applyThemeCssVars = (variant, overrides = null) => {
  const root = document.documentElement;
  const normalizedVariant = normalizeThemeVariantClient(variant);
  const theme = THEME_FILES[normalizedVariant] || vcardTheme;
  const colors = theme.colors || {};

  // Helper to convert camelCase to kebab-case
  const toKebabCase = (str) => str.replace(/([A-Z])/g, (match) => '-' + match).toLowerCase();

  // Set all color CSS variables from theme
  Object.keys(colors).forEach((colorKey) => {
    const colorValue = colors[colorKey];
    if (colorValue && typeof colorValue === 'object' && colorValue.light !== undefined) {
      const cssVarName = `--color-${toKebabCase(colorKey)}`;
      root.style.setProperty(`${cssVarName}-light`, colorValue.light);
      root.style.setProperty(`${cssVarName}-dark`, colorValue.dark);
    }
  });

  // Set texture variables
  const textures = theme.textures?.main || {};
  root.style.setProperty('--texture-main-light', textures.light ? `url(${textures.light})` : 'none');
  root.style.setProperty('--texture-main-dark', textures.dark ? `url(${textures.dark})` : 'none');
  root.style.setProperty('--texture-main-size', textures.size || '540px 540px');
  root.style.setProperty('--texture-main-blend-light', textures.blendLight || 'multiply');
  root.style.setProperty('--texture-main-blend-dark', textures.blendDark || 'overlay');
  root.style.setProperty('--texture-main-opacity-light', textures.opacityLight ?? 0.08);
  root.style.setProperty('--texture-main-opacity-dark', textures.opacityDark ?? 0.1);

  // Apply per-organisation app color overrides after base theme vars
  if (overrides && typeof overrides === 'object') {
    const allowedKeys = new Set(APP_THEME_OVERRIDE_TOKENS.map((t) => t.key));
    Object.keys(overrides).forEach((rawKey) => {
      const token = String(rawKey || '').replace(/[^a-zA-Z0-9_-]/g, '');
      if (!allowedKeys.has(token)) return;
      if (!token) return;
      const value = overrides[rawKey];
      if (!value || typeof value !== 'object') return;
      if (typeof value.light === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value.light.trim())) {
        root.style.setProperty(`--color-${toKebabCase(token)}-light`, value.light.trim());
      }
      if (typeof value.dark === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value.dark.trim())) {
        root.style.setProperty(`--color-${toKebabCase(token)}-dark`, value.dark.trim());
      }
    });
  }
};

// Guard click handlers to avoid dead buttons when a callback is accidentally missing.
const withSafeHandler = (handler, fallback = null, actionName = 'action') => {
  return (...args) => {
    if (typeof handler === 'function') {
      return handler(...args);
    }
    if (typeof fallback === 'function') {
      return fallback(...args);
    }
    console.warn(`[UI] Missing handler for ${actionName}`);
  };
};

// --- QR PAYLOAD STORAGE KEYS ---
const QR_STORAGE_KEY = 'vcard:lastQrPayload';

// Build a vCard string for QR code encoding (simplified for reliable scanning)
// When scanned, this will add the contact directly to the phone
const buildQrPayload = (shortCode, data) => {
  const { personal = {}, contact = {} } = data || {};

  // Use short code for QR URL (always use short code for simpler QR)
  const cardUrl = typeof window !== 'undefined' && shortCode
    ? `${window.location.origin}/${shortCode}`
    : '';
  return buildVCardString({ personal, contact, cardUrl });
};

const saveQrPayloadToStorage = (payload) => {
  try {
    if (!payload) return;
    const record = {
      payload,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(QR_STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    console.warn('Failed to save QR payload', e);
  }
};

const loadQrPayloadFromStorage = () => {
  try {
    const raw = localStorage.getItem(QR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.payload || null;
  } catch (e) {
    console.warn('Failed to load QR payload', e);
    return null;
  }
};

// Helper function to sanitize text content
const sanitizeText = (text) => {
  if (!text) return '';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
};

const collectContactPhones = (contact = {}) => {
  const seen = new Set();
  return [
    {
      value: contact.phone,
      label: contact.phoneLabel || translateText('common.phoneLabels.mobile', 'Mobil')
    },
    {
      value: contact.phoneSecondary,
      label: contact.phoneSecondaryLabel || translateText('common.phoneLabels.phone', 'Telefon')
    }
  ]
    .map((entry) => ({
      value: sanitizeText(entry.value || '').trim(),
      label: sanitizeText(entry.label || '').trim()
    }))
    .filter((entry) => {
      if (!entry.value) return false;
      if (seen.has(entry.value)) return false;
      seen.add(entry.value);
      return true;
    });
};

const COUNTRY_OPTIONS = [
  { value: 'DE', labelKey: 'common.countries.DE', defaultLabel: 'Germany' },
  { value: 'AT', labelKey: 'common.countries.AT', defaultLabel: 'Austria' },
  { value: 'CH', labelKey: 'common.countries.CH', defaultLabel: 'Switzerland' },
  { value: 'NL', labelKey: 'common.countries.NL', defaultLabel: 'Netherlands' },
  { value: 'BE', labelKey: 'common.countries.BE', defaultLabel: 'Belgium' },
  { value: 'LU', labelKey: 'common.countries.LU', defaultLabel: 'Luxembourg' },
  { value: 'FR', labelKey: 'common.countries.FR', defaultLabel: 'France' },
  { value: 'ES', labelKey: 'common.countries.ES', defaultLabel: 'Spain' },
  { value: 'IT', labelKey: 'common.countries.IT', defaultLabel: 'Italy' },
  { value: 'PL', labelKey: 'common.countries.PL', defaultLabel: 'Poland' },
  { value: 'CZ', labelKey: 'common.countries.CZ', defaultLabel: 'Czech Republic' },
  { value: 'DK', labelKey: 'common.countries.DK', defaultLabel: 'Denmark' },
  { value: 'SE', labelKey: 'common.countries.SE', defaultLabel: 'Sweden' },
  { value: 'NO', labelKey: 'common.countries.NO', defaultLabel: 'Norway' },
  { value: 'FI', labelKey: 'common.countries.FI', defaultLabel: 'Finland' },
  { value: 'GB', labelKey: 'common.countries.GB', defaultLabel: 'United Kingdom' },
  { value: 'IE', labelKey: 'common.countries.IE', defaultLabel: 'Ireland' },
  { value: 'US', labelKey: 'common.countries.US', defaultLabel: 'United States' },
  { value: 'CA', labelKey: 'common.countries.CA', defaultLabel: 'Canada' },
  { value: 'AU', labelKey: 'common.countries.AU', defaultLabel: 'Australia' }
];

const getCountryLabel = (value = '') => {
  const normalized = sanitizeText(value || '').trim();
  if (!normalized) return '';
  const match = COUNTRY_OPTIONS.find((option) => {
    const translatedLabel = translateText(option.labelKey, option.defaultLabel);
    return option.value === normalized || translatedLabel === normalized || option.defaultLabel === normalized;
  });
  return match ? translateText(match.labelKey, match.defaultLabel) : normalized;
};

const normalizeContactData = (contact = {}) => {
  const normalized = {
    email: '',
    phone: '',
    phoneLabel: translateText('common.phoneLabels.mobile', 'Mobil'),
    phoneSecondary: '',
    phoneSecondaryLabel: translateText('common.phoneLabels.phone', 'Telefon'),
    address: '',
    addressStreet: '',
    addressPostalCode: '',
    addressCity: '',
    addressCountry: 'DE',
    showRouteButton: true,
    website: '',
    ...contact
  };

  if (!normalized.addressCountry) {
    normalized.addressCountry = 'DE';
  }

  if (!normalized.addressStreet && !normalized.addressPostalCode && !normalized.addressCity && normalized.address) {
    normalized.addressStreet = normalized.address;
  }

  if (typeof normalized.showRouteButton !== 'boolean') {
    normalized.showRouteButton = true;
  }

  return normalized;
};

const buildAddressData = (contact = {}, personal = {}) => {
  const normalized = normalizeContactData(contact);
  const street = sanitizeText(normalized.addressStreet || '').trim();
  const postalCode = sanitizeText(normalized.addressPostalCode || '').trim();
  const city = sanitizeText(normalized.addressCity || '').trim();
  const country = getCountryLabel(normalized.addressCountry || '');
  const hasStructuredAddress = Boolean(street || postalCode || city);
  const cityLine = [postalCode, city].filter(Boolean).join(' ').trim();

  if (hasStructuredAddress) {
    const displayLines = [street, cityLine, country].filter(Boolean);
    return {
      street,
      postalCode,
      city,
      country,
      display: displayLines.join(', '),
      routeQuery: displayLines.join(', '),
      summary: [city, country].filter(Boolean).join(', ')
    };
  }

  const legacyAddress = sanitizeText(normalized.address || personal.location || '').trim();
  return {
    street: legacyAddress,
    postalCode: '',
    city: '',
    country: '',
    display: legacyAddress,
    routeQuery: legacyAddress,
    summary: sanitizeText(personal.location || '').trim()
  };
};

const mergeCardDataWithDefaults = (cardData = {}, settings) => {
  const defaultTemplate = getDefaultTemplate(settings);
  const mergedContact = normalizeContactData({
    ...defaultTemplate.contact,
    ...(cardData.contact || {})
  });

  return {
    ...defaultTemplate,
    ...cardData,
    personal: {
      ...defaultTemplate.personal,
      ...(cardData.personal || {})
    },
    contact: mergedContact,
    social: {
      ...defaultTemplate.social,
      ...(cardData.social || {})
    },
    theme: {
      ...defaultTemplate.theme,
      ...(cardData.theme || {})
    },
    images: {
      ...defaultTemplate.images,
      ...(cardData.images || {})
    },
    links: cardData.links || [],
    privacy: {
      ...defaultTemplate.privacy,
      ...(cardData.privacy || {})
    }
  };
};

const escapeVCardValue = (value, maxLen = 500) => sanitizeText(value || '')
  .substring(0, maxLen)
  .replace(/\r\n/g, '\n')
  .replace(/\r/g, '\n')
  .replace(/\\/g, '\\\\')
  .replace(/\n/g, '\\n')
  .replace(/;/g, '\\;')
  .replace(/,/g, '\\,');

const buildVCardString = ({ personal = {}, contact = {}, cardUrl = '' } = {}) => {
  const firstName = escapeVCardValue(personal.firstName || '', 40);
  const lastName = escapeVCardValue(personal.lastName || '', 40);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const company = escapeVCardValue(personal.company || '', 80);
  const title = escapeVCardValue(personal.title || '', 120);
  const email = escapeVCardValue(contact.email || '', 120);
  const website = escapeVCardValue(contact.website || '', 200);
  const bio = escapeVCardValue(personal.bio || '', 1000);
  const addressData = buildAddressData(contact, personal);
  const address = escapeVCardValue(addressData.display || '', 300);
  const phones = collectContactPhones(contact).map((entry, index) => ({
    value: escapeVCardValue(entry.value, 50),
    label: escapeVCardValue(
      entry.label || translateText(index === 0 ? 'common.phoneLabels.mobile' : 'common.phoneLabels.phone', index === 0 ? 'Mobil' : 'Telefon'),
      40
    )
  }));

  const lines = ['BEGIN:VCARD', 'VERSION:3.0'];

  if (fullName) {
    lines.push(`FN:${fullName}`);
    lines.push(`N:${lastName};${firstName};;;`);
  }

  if (company) {
    lines.push(`ORG:${company}`);
  }

  if (title) {
    lines.push(`TITLE:${title}`);
  }

  if (address) {
    lines.push(`ADR;TYPE=WORK:;;${escapeVCardValue(addressData.street || addressData.display || '', 120)};${escapeVCardValue(addressData.city || '', 80)};;${escapeVCardValue(addressData.postalCode || '', 20)};${escapeVCardValue(addressData.country || '', 80)}`);
  }

  phones.forEach((phone, index) => {
    const phoneType = index === 0 ? 'CELL' : 'WORK';
    lines.push(`TEL;TYPE=${phoneType};LABEL=${phone.label}:${phone.value}`);
  });

  if (email) {
    lines.push(`EMAIL;TYPE=WORK:${email}`);
  }

  if (website) {
    lines.push(`URL:${website}`);
  } else if (cardUrl) {
    lines.push(`URL:${escapeVCardValue(cardUrl, 250)}`);
  }

  if (bio) {
    lines.push(`NOTE:${bio}`);
  }

  lines.push('END:VCARD');
  return lines.join('\n');
};

// Helper function to sanitize HTML content (for bio)
const sanitizeHTML = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u'],
    ALLOWED_ATTR: []
  });
};

// --- ICONS MAPPING ---
const ICON_MAP = {
  link: LinkIcon,
  download: Download,
  file: FileText,
  calendar: Calendar,
  video: Video,
  music: Music,
  shop: ShoppingCart,
  youtube: Youtube,
  facebook: Facebook,
  whatsapp: MessageCircle,
  globe: Globe
};

// --- DATA TEMPLATE ---
const getDefaultTemplate = (settings) => ({
  personal: {
    firstName: translateText('template.defaults.firstName', 'New'),
    lastName: translateText('template.defaults.lastName', 'User'),
    title: translateText('template.defaults.role', 'Role Title'),
    company: settings?.default_organisation || translateText('template.defaults.company', 'My Organisation'),
    bio: translateText('template.defaults.bio', 'Welcome to the team.'),
    location: translateText('template.defaults.location', 'London, UK')
  },
    contact: {
      email: "",
      phone: "",
      phoneLabel: translateText('common.phoneLabels.mobile', 'Mobil'),
      phoneSecondary: "",
      phoneSecondaryLabel: translateText('common.phoneLabels.phone', 'Telefon'),
      address: "",
      showRouteButton: true,
      website: "",
    },
  social: { linkedin: "", twitter: "", instagram: "", github: "" },
  theme: { color: "indigo", style: "modern" },
  images: { avatar: null, banner: null },
  links: [],
  privacy: {
    requireInteraction: false,  // OFF by default
    clientSideObfuscation: true,  // ON by default
    blockRobots: true  // ON by default
  }
});

// --- STYLES ---
// Helper functions to get color classes from settings
const getThemeGradient = (colorName, settings) => {
  if (!settings?.theme_colors) return "linear-gradient(135deg, #4f46e5, #7c3aed)";
  const color = settings.theme_colors.find(c => c.name === colorName);
  return color?.gradientStyle || "linear-gradient(135deg, #4f46e5, #7c3aed)";
};

const getButtonColor = (colorName, settings) => {
  if (!settings?.theme_colors) return "#4f46e5";
  const color = settings.theme_colors.find(c => c.name === colorName);
  return color?.buttonStyle || "#4f46e5";
};

const getLinkColor = (colorName, settings) => {
  if (!settings?.theme_colors) return "#4f46e5";
  const color = settings.theme_colors.find(c => c.name === colorName);
  return color?.linkStyle || "#4f46e5";
};

const getTextColor = (colorName, settings) => {
  if (!settings?.theme_colors) return "#4f46e5";
  const color = settings.theme_colors.find(c => c.name === colorName);
  return color?.textStyle || "#4f46e5";
};

// --- COLOR GENERATION UTILITIES ---
const TAILWIND_COLORS = ['indigo', 'blue', 'rose', 'emerald', 'slate', 'purple', 'cyan', 'teal', 'orange', 'pink', 'violet', 'fuchsia', 'amber', 'lime', 'green', 'yellow', 'red'];

const COMPLEMENTARY_MAP = {
  indigo: 'purple',
  blue: 'cyan',
  rose: 'orange',
  emerald: 'teal',
  slate: 'slate',
  purple: 'indigo',
  cyan: 'blue',
  teal: 'emerald',
  orange: 'rose',
  pink: 'fuchsia',
  violet: 'purple',
  fuchsia: 'pink',
  amber: 'orange',
  lime: 'green',
  green: 'emerald',
  yellow: 'amber',
  red: 'rose'
};

const getComplementaryColor = (baseColor) => {
  return COMPLEMENTARY_MAP[baseColor] || 'purple';
};

const getTailwindShades = () => {
  return [400, 500, 600, 700, 800];
};

// Tailwind color to hex mapping (for common shades)
const TAILWIND_TO_HEX = {
  indigo: { 400: '#818cf8', 500: '#6366f1', 600: '#4f46e5', 700: '#4338ca', 800: '#3730a3' },
  blue: { 400: '#60a5fa', 500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8', 800: '#1e40af' },
  rose: { 400: '#fb7185', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c', 800: '#9f1239' },
  emerald: { 400: '#34d399', 500: '#10b981', 600: '#059669', 700: '#047857', 800: '#065f46' },
  slate: { 400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b' },
  purple: { 400: '#a78bfa', 500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9', 800: '#5b21b6' },
  cyan: { 400: '#22d3ee', 500: '#06b6d4', 600: '#0891b2', 700: '#0e7490', 800: '#155e75' },
  teal: { 400: '#2dd4bf', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e', 800: '#115e59' },
  orange: { 400: '#fb923c', 500: '#f97316', 600: '#ea580c', 700: '#c2410c', 800: '#9a3412' },
  pink: { 400: '#f472b6', 500: '#ec4899', 600: '#db2777', 700: '#be185d', 800: '#9f1239' },
  violet: { 400: '#a78bfa', 500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9', 800: '#5b21b6' },
  fuchsia: { 400: '#f0abfc', 500: '#d946ef', 600: '#c026d3', 700: '#a21caf', 800: '#86198f' },
  amber: { 400: '#fbbf24', 500: '#f59e0b', 600: '#d97706', 700: '#b45309', 800: '#92400e' },
  lime: { 400: '#a3e635', 500: '#84cc16', 600: '#65a30d', 700: '#4d7c0f', 800: '#365314' },
  green: { 400: '#4ade80', 500: '#22c55e', 600: '#16a34a', 700: '#15803d', 800: '#166534' },
  yellow: { 400: '#facc15', 500: '#eab308', 600: '#ca8a04', 700: '#a16207', 800: '#854d0e' },
  red: { 400: '#f87171', 500: '#ef4444', 600: '#dc2626', 700: '#b91c1c', 800: '#991b1b' }
};

const getTailwindColorHex = (colorName, shade = 600) => {
  return TAILWIND_TO_HEX[colorName]?.[shade] || '#4f46e5';
};

// Utility to darken a hex color
const darkenHex = (hex, percent) => {
  // Remove # if present
  hex = hex.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Darken by percent
  const factor = 1 - (percent / 100);
  const newR = Math.max(0, Math.floor(r * factor));
  const newG = Math.max(0, Math.floor(g * factor));
  const newB = Math.max(0, Math.floor(b * factor));

  // Convert back to hex
  const toHex = (n) => {
    const hex = n.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};


// Extract base color from existing gradient class (for migration)
const extractBaseColorFromGradient = (gradient) => {
  if (!gradient) return null;
  const match = gradient.match(/from-(\w+)-(\d+)/);
  if (match) {
    return { baseColor: match[1], shade: parseInt(match[2]) };
  }
  return null;
};

// Modal Component
function Modal({ isOpen, onClose, type = 'info', title, message, onConfirm, confirmText = 'OK', cancelText = 'Cancel', inputLabel, inputPlaceholder, inputValue, onInputChange }) {
  if (!isOpen) return null;

  const typeStyles = {
    info: { icon: AlertCircle, iconColor: 'text-info-text dark:text-info-text-dark', bgColor: 'bg-info-bg dark:bg-info-bg-dark', borderColor: 'border-info-border dark:border-info-border-dark' },
    success: { icon: Check, iconColor: 'text-success-text dark:text-success-text-dark', bgColor: 'bg-success-bg dark:bg-success-bg-dark', borderColor: 'border-success-border dark:border-success-border-dark' },
    error: { icon: AlertCircle, iconColor: 'text-error-text dark:text-error-text-dark', bgColor: 'bg-error-bg dark:bg-error-bg-dark', borderColor: 'border-error-border dark:border-error-border-dark' },
    confirm: { icon: AlertCircle, iconColor: 'text-text-muted dark:text-text-muted-dark', bgColor: 'bg-surface dark:bg-surface-dark', borderColor: 'border-border dark:border-border-dark' }
  };

  const style = typeStyles[type] || typeStyles.info;
  const Icon = style.icon;
  const hasInput = inputLabel || inputPlaceholder;

  const handleConfirm = () => {
    if (onConfirm) {
      const result = onConfirm();
      if (result === false) {
        return;
      }
    }
    // onClose will handle calling the onClose callback
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && hasInput) {
      handleConfirm();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-card dark:bg-card-dark rounded-card shadow-2xl max-w-sm w-full p-6 animate-in fade-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`w-12 h-12 rounded-full ${style.bgColor} flex items-center justify-center mx-auto mb-4`}>
          <Icon className={`w-6 h-6 ${style.iconColor}`} />
        </div>
        {title && <h3 className="text-xl font-bold text-text-primary dark:text-text-primary-dark text-center mb-2">{title}</h3>}
        {message && <p className="text-text-secondary dark:text-text-secondary-dark text-center mb-6">{message}</p>}
        {hasInput && (
          <div className="mb-6">
            {inputLabel && <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2">{inputLabel}</label>}
            <input
              type="text"
              value={inputValue || ''}
              onChange={(e) => onInputChange && onInputChange(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={inputPlaceholder}
              className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
              autoFocus
            />
          </div>
        )}
        <div className="flex gap-3">
          {(type === 'confirm' || hasInput) && (
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-full font-medium text-text-secondary dark:text-text-secondary-dark bg-surface dark:bg-surface-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`flex-1 px-4 py-2.5 rounded-full font-bold text-white transition-colors ${type === 'error' ? 'bg-error dark:bg-error-dark hover:bg-error-hover dark:hover:bg-error-hover-dark' :
              type === 'success' ? 'bg-success dark:bg-success-dark hover:bg-success-hover dark:hover:bg-success-hover-dark' :
                type === 'confirm' ? 'bg-confirm dark:bg-confirm-dark hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark text-confirm-text dark:text-confirm-text-dark' :
                  'bg-info dark:bg-info-dark hover:bg-info-hover dark:hover:bg-info-hover-dark'
              }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function VersionBadge() {
  const [isOutdated, setIsOutdated] = useState(false);
  const [isAhead, setIsAhead] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Determine if we're on a non-release branch
  // Show branch name if it's not main, master, or a version tag pattern (e.g., v0.3.1)
  const isReleaseBranch = !GIT_BRANCH ||
    GIT_BRANCH === 'main' ||
    GIT_BRANCH === 'master' ||
    /^v?\d+\.\d+/.test(GIT_BRANCH) ||
    GIT_BRANCH === 'HEAD'; // Detached HEAD state (common in CI/Docker)

  const showBranch = GIT_BRANCH && !isReleaseBranch;

  useEffect(() => {
    // Check GitHub for latest version
    const checkVersion = async () => {
      try {
        const response = await fetch('https://api.github.com/repos/niklasmetzger/VCARD3.0/releases/latest', {
          headers: {
            'Accept': 'application/vnd.github.v3+json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          const latestVersion = data.tag_name?.replace(/^v/, '') || data.tag_name; // Remove 'v' prefix if present
          const currentVersion = APP_VERSION;

          // SemVer-compliant version comparison
          // Follows SemVer precedence rules: pre-release versions have lower precedence than stable versions
          const compareVersions = (v1, v2) => {
            // Parse versions into base version and pre-release identifier
            const parseVersion = (version) => {
              const dashIndex = version.indexOf('-');
              if (dashIndex === -1) {
                return {
                  base: version,
                  prerelease: null
                };
              }
              return {
                base: version.substring(0, dashIndex),
                prerelease: version.substring(dashIndex + 1)
              };
            };

            const parsed1 = parseVersion(v1);
            const parsed2 = parseVersion(v2);

            // Compare base versions numerically
            const base1 = parsed1.base.split('.').map(Number);
            const base2 = parsed2.base.split('.').map(Number);

            for (let i = 0; i < Math.max(base1.length, base2.length); i++) {
              const part1 = base1[i] || 0;
              const part2 = base2[i] || 0;
              if (part1 < part2) return -1;
              if (part1 > part2) return 1;
            }

            // Base versions are equal, now check pre-release identifiers
            // Rule: A version without a pre-release identifier has higher precedence
            if (parsed1.prerelease === null && parsed2.prerelease === null) {
              return 0; // Both are stable, equal
            }
            if (parsed1.prerelease === null) {
              return 1; // v1 is stable, v2 is pre-release, v1 > v2
            }
            if (parsed2.prerelease === null) {
              return -1; // v1 is pre-release, v2 is stable, v1 < v2
            }

            // Both have pre-release identifiers, compare lexicographically
            const prerelease1 = parsed1.prerelease.split('.');
            const prerelease2 = parsed2.prerelease.split('.');

            for (let i = 0; i < Math.max(prerelease1.length, prerelease2.length); i++) {
              const part1 = prerelease1[i];
              const part2 = prerelease2[i];

              if (part1 === undefined) return -1; // v1 has fewer parts, v1 < v2
              if (part2 === undefined) return 1; // v2 has fewer parts, v1 > v2

              // Try numeric comparison first, fall back to string comparison
              const num1 = Number(part1);
              const num2 = Number(part2);

              if (!isNaN(num1) && !isNaN(num2)) {
                // Both are numeric
                if (num1 < num2) return -1;
                if (num1 > num2) return 1;
              } else {
                // At least one is non-numeric, compare as strings
                if (part1 < part2) return -1;
                if (part1 > part2) return 1;
              }
            }

            return 0; // Pre-release identifiers are equal
          };

          if (latestVersion) {
            const comparison = compareVersions(currentVersion, latestVersion);
            if (comparison < 0) {
              setIsOutdated(true);
            } else if (comparison > 0) {
              setIsAhead(true);
            }
          }
        }
      } catch (error) {
        console.error('Failed to check version:', error);
        // Silently fail - don't show error to user
      } finally {
        setIsChecking(false);
      }
    };

    checkVersion();
  }, []);

  // Build the display string: "v0.3.1" or "v0.3.1 (feature-branch)"
  const versionDisplay = showBranch
    ? `v${APP_VERSION} (${GIT_BRANCH})`
    : `v${APP_VERSION}`;

  // Build the tooltip
  const tooltip = (!isOutdated && !isAhead && !showBranch)
    ? "You are on the latest release"
    : (isAhead || showBranch)
      ? "You are on an unreleased development version - things may break"
      : isOutdated
        ? "Update available on GitHub"
        : "View on GitHub";

  return (
    <div className="fixed bottom-4 left-4 z-50">
      <a
        href={GITHUB_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`text-xs font-medium transition-all bg-card dark:bg-card-dark border-2 border-border dark:border-border-dark px-2 py-1 rounded shadow-sm hover:shadow-md ${showBranch
          ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:border-amber-300 dark:hover:border-amber-700'
          : isOutdated
            ? 'text-error-text dark:text-error-text-dark hover:bg-error-bg dark:hover:bg-error-bg-dark hover:border-error-border dark:hover:border-error-border-dark'
            : isAhead
              ? 'text-info-text dark:text-info-text-dark hover:bg-info-bg dark:hover:bg-info-bg-dark hover:border-info-border dark:hover:border-info-border-dark'
              : 'text-text-primary dark:text-text-primary-dark hover:text-action dark:hover:text-action-dark hover:border-success-border dark:hover:border-success-border-dark'
          }`}
        title={tooltip}
      >
        {versionDisplay}
      </a>
    </div>
  );
}

function ForgotPasswordModal({ isOpen, onClose }) {
  const { t } = useAppTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const res = await fetch(`${API_ENDPOINT}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await res.json();

      if (res.ok) {
        setIsSuccess(true);
      } else {
        setError(data.error || t('auth.forgotPassword.errors.sendFailed', 'Failed to send reset email'));
      }
    } catch (err) {
      setError(t('common.errors.genericRetry', 'An error occurred. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card dark:bg-card-dark rounded-card shadow-2xl max-w-sm w-full p-6 animate-in fade-in duration-200" onClick={e => e.stopPropagation()}>
        <div className="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mx-auto mb-4">
          <Mail className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <h3 className="text-xl font-bold text-text-primary dark:text-text-primary-dark text-center mb-2">
          {t('auth.forgotPassword.title', 'Reset Password')}
        </h3>

        {isSuccess ? (
          <div className="text-center">
            <p className="text-text-secondary dark:text-text-secondary-dark mb-6">
              {t(
                'auth.forgotPassword.success',
                'If an account exists for {{email}}, you will receive password reset instructions shortly.',
                { email }
              )}
            </p>
            <button onClick={onClose} className="w-full py-2.5 rounded-full font-bold text-white bg-confirm dark:bg-confirm-dark hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark transition-colors">
              {t('common.actions.close', 'Close')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p className="text-text-secondary dark:text-text-secondary-dark text-center mb-6 text-sm">
              {t(
                'auth.forgotPassword.description',
                "Enter your email address and we'll send you a link to reset your password."
              )}
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2">
                {t('common.fields.email', 'Email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.forgotPassword.emailPlaceholder', 'Ex. user@example.com')}
                className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
                required
                autoFocus
              />
            </div>
            {error && <div className="mb-4 text-sm text-error-text dark:text-error-text-dark text-center">{error}</div>}
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-full font-medium text-text-secondary dark:text-text-secondary-dark bg-surface dark:bg-surface-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors">
                {t('common.actions.cancel', 'Cancel')}
              </button>
              <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2.5 rounded-full font-bold text-white bg-action dark:bg-action-dark hover:bg-action-hover dark:hover:bg-action-hover-dark transition-colors disabled:opacity-50">
                {isSubmitting
                  ? t('auth.forgotPassword.actions.sending', 'Sending...')
                  : t('auth.forgotPassword.actions.sendLink', 'Send Link')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ChangePasswordModal({ isOpen, onClose, apiCall, userId = null, isOwnerChangingUser = false }) {
  const { t } = useAppTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(t('auth.changePassword.errors.passwordMismatch', 'New passwords do not match'));
      return;
    }
    if (newPassword.length < 8) {
      setError(t('auth.changePassword.errors.passwordTooShort', 'Password must be at least 8 characters'));
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const endpoint = isOwnerChangingUser && userId
        ? `${API_ENDPOINT}/admin/users/${userId}/password`
        : `${API_ENDPOINT}/auth/change-password`;

      const body = isOwnerChangingUser && userId
        ? { newPassword }
        : { currentPassword, newPassword };

      const res = await apiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify(body)
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(t('auth.changePassword.success.updated', 'Password updated successfully'));
        setTimeout(() => {
          onClose();
          setSuccess('');
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        }, 1500);
      } else {
        setError(data.error || t('auth.changePassword.errors.updateFailed', 'Failed to update password'));
      }
    } catch (err) {
      setError(t('common.errors.genericRetry', 'An error occurred. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card dark:bg-card-dark rounded-card shadow-2xl max-w-sm w-full p-6 animate-in fade-in duration-200" onClick={e => e.stopPropagation()}>
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-text-primary dark:text-text-primary-dark">
            {t('auth.changePassword.title', 'Change Password')}
          </h3>
          {isOwnerChangingUser && (
            <p className="text-sm text-text-muted dark:text-text-muted-dark">
              {t('auth.changePassword.userSubtitle', 'Changing password for user')}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isOwnerChangingUser && (
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-1">
                {t('auth.changePassword.fields.currentPassword', 'Current Password')}
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
                required
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-1">
              {t('auth.changePassword.fields.newPassword', 'New Password')}
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-1">
              {t('auth.changePassword.fields.confirmPassword', 'Confirm New Password')}
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
              required
              minLength={8}
            />
          </div>

          {error && <div className="text-sm text-error-text dark:text-error-text-dark text-center">{error}</div>}
          {success && <div className="text-sm text-green-500 text-center">{success}</div>}

          <div className="flex gap-3 mt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-full font-medium text-text-secondary dark:text-text-secondary-dark bg-surface dark:bg-surface-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors">
              {t('common.actions.cancel', 'Cancel')}
            </button>
            <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2.5 rounded-full font-bold text-white bg-confirm dark:bg-confirm-dark hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark transition-colors disabled:opacity-50">
              {isSubmitting
                ? t('auth.changePassword.actions.updating', 'Updating...')
                : t('common.actions.update', 'Update')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordRoute() {
  const { t } = useAppTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, success, error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setMessage(t('auth.resetPassword.errors.passwordMismatch', 'Passwords do not match'));
      setStatus('error');
      return;
    }
    if (password.length < 8) {
      setMessage(t('auth.resetPassword.errors.passwordTooShort', 'Password must be at least 8 characters'));
      setStatus('error');
      return;
    }

    setIsSubmitting(true);
    setStatus('idle');
    setMessage('');

    try {
      const res = await fetch(`${API_ENDPOINT}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password })
      });

      const data = await res.json();

      if (res.ok) {
        setStatus('success');
        setMessage(t('auth.resetPassword.success', 'Your password has been reset successfully.'));
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setStatus('error');
        setMessage(data.error || t('auth.resetPassword.errors.failed', 'Failed to reset password'));
      }
    } catch (err) {
      setStatus('error');
      setMessage(t('common.errors.genericRetry', 'An error occurred. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface dark:bg-main-dark flex items-center justify-center p-4">
      <div className="bg-card dark:bg-card-dark max-w-sm w-full rounded-page shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4 text-indigo-600 dark:text-indigo-400"><Lock className="w-8 h-8" /></div>
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark">
            {t('auth.resetPassword.title', 'Reset Password')}
          </h1>
        </div>

        {status === 'success' ? (
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600 dark:text-green-400">
              <Check className="w-6 h-6" />
            </div>
            <p className="text-text-primary dark:text-text-primary-dark mb-4">{message}</p>
            <p className="text-sm text-text-muted dark:text-text-muted-dark">
              {t('auth.resetPassword.redirecting', 'Redirecting to login...')}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2">
                {t('auth.resetPassword.fields.newPassword', 'New Password')}
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={t('auth.resetPassword.placeholders.minPassword', 'Minimum 8 characters')}
                className="w-full px-5 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2">
                {t('auth.resetPassword.fields.confirmPassword', 'Confirm Password')}
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder={t('auth.resetPassword.placeholders.repeatPassword', 'Repeat password')}
                className="w-full px-5 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
                required
                minLength={8}
              />
            </div>

            {status === 'error' && <div className="text-error-text dark:text-error-text-dark text-sm text-center">{message}</div>}

            <button type="submit" disabled={isSubmitting} className="w-full py-3.5 rounded-full bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark font-bold hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark transition-colors disabled:opacity-50">
              {isSubmitting
                ? t('auth.resetPassword.actions.resetting', 'Resetting...')
                : t('auth.resetPassword.actions.submit', 'Set New Password')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Note: useParams() doesn't work at App level (Routes are children), so we extract params from location.pathname
  const [view, setView] = useState(() => {
    // Initialize view based on current path - don't default to 'loading' for public routes
    const initialPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const pathParts = initialPath.substring(1).split('/').filter(p => p);
    const isShortCode = pathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(pathParts[0]);
    const isOrgScoped = pathParts.length === 2 && pathParts[0] && pathParts[1];
    const isPublicRoute = isShortCode || isOrgScoped || (pathParts.length === 1 && pathParts[0] && !initialPath.startsWith('/people') && !initialPath.startsWith('/login') && !initialPath.startsWith('/setup') && !initialPath.startsWith('/settings') && !initialPath.startsWith('/users') && !initialPath.startsWith('/profile') && !initialPath.startsWith('/organizations') && !initialPath.startsWith('/departments') && !initialPath.startsWith('/cards') && initialPath !== '/');
    return isPublicRoute ? 'public-loading' : 'loading';
  });
  const [data, setData] = useState(() => getDefaultTemplate(null));
  const [currentSlug, setCurrentSlug] = useState('');
  const [isPublicLoading, setIsPublicLoading] = useState(false);
  const [cardList, setCardList] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null); // 'owner' | 'admin' | 'member' | 'department_lead'
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState(() => localStorage.getItem('selectedOrganisationId') || '');
  const authCheckInFlightRef = useRef(null);
  const lastAuthCheckRef = useRef({ timestamp: 0, result: null });
  const [csrfToken, setCsrfToken] = useState('');
  const [error, setError] = useState('');
  const [modal, setModal] = useState({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null, onClose: null, confirmText: 'OK', cancelText: 'Cancel' });
  const [humanCheck, setHumanCheck] = useState({ isOpen: false, a: 0, b: 0, input: '', retryAfterSeconds: 60, error: '' });
  const [createCardModal, setCreateCardModal] = useState({ isOpen: false, slug: '', userId: null });
  const [targetUserIdForNewCard, setTargetUserIdForNewCard] = useState(null);
  const [actionSelectionModal, setActionSelectionModal] = useState({ isOpen: false });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [changePasswordModal, setChangePasswordModal] = useState({ isOpen: false, userId: null, isOwnerChangingUser: false });
  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'member', departmentId: '' });
  const [newInvitation, setNewInvitation] = useState({ email: '', role: 'member' });
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [userLanguage, setUserLanguage] = useState('en');
  const [setupStatus, setSetupStatus] = useState(null);
  const [setupData, setSetupData] = useState({ organisationName: '', adminEmail: '', adminPassword: '' });
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSuccessSetup, setIsSuccessSetup] = useState(false);
  const [isSuccessCreateUser, setIsSuccessCreateUser] = useState(false);
  const [isSuccessInvite, setIsSuccessInvite] = useState(false);
  const [ownerPeopleViewMode, setOwnerPeopleViewMode] = useState(() => localStorage.getItem('ownerPeopleViewMode') || 'cards');
  const [ownerFilterFirstName, setOwnerFilterFirstName] = useState('');
  const [ownerFilterLastName, setOwnerFilterLastName] = useState('');
  const [ownerFilterEmail, setOwnerFilterEmail] = useState('');
  const [ownerFilterRole, setOwnerFilterRole] = useState('all');
  const [ownerFilterCards, setOwnerFilterCards] = useState('all');
  const [appleWalletLoadingKey, setAppleWalletLoadingKey] = useState('');

  const isOrgAdmin = userRole === 'owner' || userRole === 'admin';
  const canManagePeople = isOrgAdmin || userRole === 'department_lead';

  // Demo mode state management
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoResetInterval, setDemoResetInterval] = useState(60);

  // Dark mode state management
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) {
      const isDark = stored === 'true';
      // Sync with document class immediately
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return isDark;
    }
    // Use system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    return prefersDark;
  });

  // Apply dark mode class to document whenever state changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    const nextLanguage = userLanguage === 'de' ? 'de' : 'en';
    document.documentElement.lang = nextLanguage;
    if (i18n.language !== nextLanguage) {
      i18n.changeLanguage(nextLanguage).catch((error) => {
        console.error('Failed to change language', error);
      });
    }
  }, [userLanguage]);


  // Listen for system preference changes (only if no manual preference set)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const stored = localStorage.getItem('darkMode');
      if (stored === null) {
        setDarkMode(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (canManagePeople) {
      localStorage.setItem('ownerPeopleViewMode', ownerPeopleViewMode);
    }
  }, [ownerPeopleViewMode, userRole]);

  // Check for demo mode on app load
  useEffect(() => {
    fetch(`${API_ENDPOINT}/demo/status`)
      .then(res => res.json())
      .then(data => {
        if (data.demoMode) {
          setIsDemoMode(true);
          setDemoResetInterval(data.resetInterval || 60);
        }
      })
      .catch(err => {
        // Silently fail - demo endpoint may not exist if demo mode is off
      });
  }, []);

  const toggleDarkMode = () => {
    const currentDark = document.documentElement.classList.contains('dark');
    const newValue = !currentDark;

    if (newValue) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Save to localStorage
    localStorage.setItem('darkMode', newValue.toString());

    // Update React state
    setDarkMode(newValue);

    setTimeout(() => {
      const sampleEl = document.querySelector('.bg-main');
      if (sampleEl) {
        void sampleEl.offsetHeight;
      }
    }, 100);
  };

  // Demo Mode Banner Component
  const DemoModeBanner = () => {
    if (!isDemoMode) return null;

    return (
      <div className="sticky top-0 left-0 right-0 z-50 bg-amber-100 dark:bg-amber-900 border-b-2 border-amber-400 dark:border-amber-700 px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-3">
          <span className="text-2xl">🛠️</span>
          <span className="font-semibold text-amber-900 dark:text-amber-100">
            Demo Mode
          </span>
          <span className="text-sm text-amber-700 dark:text-amber-300">
            All changes reset every {demoResetInterval} minutes
          </span>
        </div>
      </div>
    );
  };

  // Helper functions to show modals
  const showAlert = (message, type = 'info', title = '', onClose = null) => {
    setModal({ isOpen: true, type, title, message, onConfirm: null, onClose, confirmText: 'OK', cancelText: 'Cancel' });
  };

  const showConfirm = (message, onConfirm, title = 'Confirm', confirmText = 'Confirm', cancelText = 'Cancel') => {
    setModal({ isOpen: true, type: 'confirm', title, message, onConfirm, onClose: null, confirmText, cancelText });
  };

  const closeHumanCheck = () => {
    setHumanCheck({ isOpen: false, a: 0, b: 0, input: '', retryAfterSeconds: 60, error: '' });
  };

  const triggerRateLimitProtection = useCallback((retryAfterSeconds = 60) => {
    const verifiedUntil = Number(sessionStorage.getItem('humanVerifiedUntil') || '0');
    if (verifiedUntil > Date.now()) {
      showAlert(
        `Too many requests. Please wait about ${retryAfterSeconds} seconds before trying again.`,
        'info',
        'Too Many Requests'
      );
      return;
    }

    const a = Math.floor(Math.random() * 7) + 2;
    const b = Math.floor(Math.random() * 7) + 2;
    setHumanCheck({
      isOpen: true,
      a,
      b,
      input: '',
      retryAfterSeconds,
      error: ''
    });
  }, []);

  const closeModal = () => {
    const currentModal = modal;
    setModal(prev => ({ ...prev, isOpen: false }));
    // Call onClose callback after state update
    if (currentModal.onClose) {
      setTimeout(() => currentModal.onClose(), 0);
    }
  };

  const THEME_PRESETS = {
    vcard: [
      { name: "indigo", gradient: "from-indigo-600 to-purple-600", button: "bg-indigo-600 hover:bg-indigo-700", link: "text-indigo-600 bg-indigo-50 border-indigo-100 hover:bg-indigo-100", text: "text-indigo-600" },
      { name: "blue", gradient: "from-blue-600 to-cyan-600", button: "bg-blue-600 hover:bg-blue-700", link: "text-blue-600 bg-blue-50 border-blue-100 hover:bg-blue-100", text: "text-blue-600" },
      { name: "rose", gradient: "from-rose-500 to-orange-500", button: "bg-rose-600 hover:bg-rose-700", link: "text-rose-600 bg-rose-50 border-rose-100 hover:bg-rose-100", text: "text-rose-600" },
      { name: "emerald", gradient: "from-emerald-500 to-teal-500", button: "bg-emerald-600 hover:bg-emerald-700", link: "text-emerald-600 bg-emerald-50 border-emerald-100 hover:bg-emerald-100", text: "text-emerald-600" },
      { name: "slate", gradient: "from-slate-700 to-slate-900", button: "bg-slate-800 hover:bg-slate-900", link: "text-slate-700 bg-border-subtle border-slate-200 hover:bg-slate-200", text: "text-slate-800" }
    ],
    minimal: [
      { name: "mono-dark", gradient: "from-neutral-900 to-neutral-700", button: "bg-neutral-900 hover:bg-neutral-800", link: "text-neutral-900 bg-neutral-100 border-neutral-200 hover:bg-neutral-200", text: "text-neutral-900" },
      { name: "mono-mid", gradient: "from-neutral-700 to-neutral-500", button: "bg-neutral-700 hover:bg-neutral-600", link: "text-neutral-700 bg-neutral-50 border-neutral-200 hover:bg-neutral-100", text: "text-neutral-700" },
      { name: "mono-light", gradient: "from-neutral-300 to-neutral-200", button: "bg-neutral-200 hover:bg-neutral-300 text-neutral-900", link: "text-neutral-700 bg-white border-neutral-200 hover:bg-neutral-100", text: "text-neutral-700" }
    ],
    mint: [
      { name: "mint-fresh", gradient: "from-emerald-400 to-teal-400", button: "bg-emerald-400 hover:bg-emerald-500", link: "text-teal-900 bg-emerald-50 border-emerald-100 hover:bg-emerald-100", text: "text-teal-900" },
      { name: "mint-petrol", gradient: "from-slate-900 to-indigo-950", button: "bg-slate-950 hover:bg-slate-900", link: "text-slate-900 bg-slate-100 border-slate-200 hover:bg-slate-200", text: "text-slate-900" },
      { name: "mint-nightfall", gradient: "from-blue-900 to-slate-900", button: "bg-blue-900 hover:bg-blue-950", link: "text-blue-900 bg-blue-50 border-blue-100 hover:bg-blue-100", text: "text-blue-900" }
    ]
  };

  const [settings, setSettings] = useState({
    default_organisation: 'My Organisation',
    theme_variant: 'vcard',
    theme_colors: THEME_PRESETS.vcard,
    app_theme_overrides: {}
  });

  // Fetch CSRF token
  const fetchCsrfToken = async () => {
    try {
      const res = await fetch(`${API_ENDPOINT}/csrf-token`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setCsrfToken(data.csrfToken);
        return data.csrfToken;
      }
    } catch (e) {
      console.error('Failed to fetch CSRF token:', e);
    }
    return '';
  };

  // Helper function to make authenticated API calls
  const apiCall = useCallback(async (url, options = {}) => {
    const method = (options.method || 'GET').toUpperCase();

    const makeRequest = async (forcedCsrfToken = null) => {
      let finalUrl = url;
      const activeOrganisationId = selectedOrganisationId || localStorage.getItem('selectedOrganisationId') || '';
      if (
        userRole === 'admin' &&
        activeOrganisationId &&
        typeof url === 'string' &&
        url.startsWith(`${API_ENDPOINT}/admin/`) &&
        !url.startsWith(`${API_ENDPOINT}/admin/organisations`) &&
        !url.includes('organisationId=')
      ) {
        const separator = url.includes('?') ? '&' : '?';
        finalUrl = `${url}${separator}organisationId=${encodeURIComponent(activeOrganisationId)}`;
      }

      const headers = {
        'Content-Type': 'application/json',
        ...options.headers
      };

      const tokenForRequest = forcedCsrfToken ?? csrfToken;
      if (tokenForRequest && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        headers['X-CSRF-Token'] = tokenForRequest;
      }

      return fetch(finalUrl, {
        ...options,
        headers,
        credentials: 'include'
      });
    };

    let response = null;
    let tokenForWrite = csrfToken;

    if (!tokenForWrite && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      tokenForWrite = await fetchCsrfToken();
    }

    response = await makeRequest(tokenForWrite);

    if (response.status === 403 && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const errorData = await response.clone().json().catch(() => ({}));
      if ((errorData?.error || '').toLowerCase().includes('csrf')) {
        const refreshedToken = await fetchCsrfToken();
        if (refreshedToken) {
          response = await makeRequest(refreshedToken);
        }
      }
    }

    if (response.status === 429) {
      const retryAfterSeconds = parseInt(response.headers.get('retry-after') || response.headers.get('Retry-After') || '60', 10);
      triggerRateLimitProtection(Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : 60);
    }
    return response;
  }, [csrfToken, selectedOrganisationId, triggerRateLimitProtection, userRole]);

  const buildScopedApiUrl = useCallback((url) => {
    const activeOrganisationId = selectedOrganisationId || localStorage.getItem('selectedOrganisationId') || '';
    if (userRole !== 'admin' || !activeOrganisationId || !url.startsWith(API_ENDPOINT)) {
      return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}organisationId=${encodeURIComponent(activeOrganisationId)}`;
  }, [selectedOrganisationId, userRole]);

  useEffect(() => {
    if (selectedOrganisationId) {
      localStorage.setItem('selectedOrganisationId', selectedOrganisationId);
    } else {
      localStorage.removeItem('selectedOrganisationId');
    }
  }, [selectedOrganisationId]);

  const fetchOrganisations = useCallback(async (roleOverride = null) => {
    try {
      const effectiveRole = roleOverride || userRole;
      if (effectiveRole !== 'admin') return [];
      const res = await fetch(`${API_ENDPOINT}/admin/organisations`, {
        credentials: 'include'
      });
      if (!res.ok) {
        console.error('Failed to fetch organisations:', res.status);
        return [];
      }
      const payload = await res.json();
      const orgs = payload.organisations || [];
      setOrganisations(orgs);
      setSelectedOrganisationId((prevSelected) => {
        if (orgs.length === 0) {
          localStorage.removeItem('selectedOrganisationId');
          return '';
        }
        const storedSelected = localStorage.getItem('selectedOrganisationId') || '';
        const preferredSelected = prevSelected || storedSelected;
        const hasSelected = preferredSelected && orgs.some((org) => org.id === preferredSelected);
        const nextSelected = hasSelected ? preferredSelected : orgs[0].id;
        localStorage.setItem('selectedOrganisationId', nextSelected);
        return nextSelected;
      });
      return orgs;
    } catch (e) {
      console.error('Failed to fetch organisations:', e);
      return [];
    }
  }, [userRole]);

  const fetchUserPreferences = useCallback(async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/user/preferences`);
      if (!res.ok) {
        setUserLanguage('en');
        return { language: 'en', activeOrganisationId: null };
      }
      const prefs = await res.json();
      const language = prefs?.language === 'de' ? 'de' : 'en';
      setUserLanguage(language);
      return { language, activeOrganisationId: prefs?.activeOrganisationId || null };
    } catch (e) {
      setUserLanguage('en');
      return { language: 'en', activeOrganisationId: null };
    }
  }, [apiCall]);

  const saveUserLanguage = async (language) => {
    const validLanguage = language === 'de' ? 'de' : 'en';
    const res = await apiCall(`${API_ENDPOINT}/user/preferences`, {
      method: 'POST',
      body: JSON.stringify({ language: validLanguage })
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to save language');
    }
    setUserLanguage(validLanguage);
  };

  const saveActiveOrganisationPreference = useCallback(async (organisationId) => {
    const res = await apiCall(`${API_ENDPOINT}/user/preferences`, {
      method: 'POST',
      body: JSON.stringify({ activeOrganisationId: organisationId || null })
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to save active organisation');
    }
  }, [apiCall]);

  // Check setup status
  const checkSetupStatus = async () => {
    try {
      const res = await fetch(`${API_ENDPOINT}/setup/status`, {
        credentials: 'include'
      });
      if (res.status === 429) {
        const retryAfterSeconds = parseInt(res.headers.get('retry-after') || res.headers.get('Retry-After') || '60', 10);
        triggerRateLimitProtection(Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : 60);
        return { rateLimited: true, retryAfterSeconds: Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : 60 };
      }
      if (res.ok) {
        const data = await res.json();
        setSetupStatus(data);
        return data;
      }
    } catch (e) {
      console.error('Failed to check setup status:', e);
    }
    return null;
  };

  // Router Logic - handle route changes and set view based on route
  useEffect(() => {
    const rawPath = location.pathname || '/';
    const path = rawPath.length > 1 ? rawPath.replace(/\/+$/, '') : rawPath;
    // Handle editor route (/people/edit/:slug) - extract slug from pathname since useParams() doesn't work at App level
    if (path.startsWith('/people/edit/')) {
      // Extract slug from pathname (remove '/people/edit/' prefix)
      const slug = path.replace('/people/edit/', '');
      // Only load if this is a different slug or we're not already in editor view
      if (currentSlug !== slug || view !== 'admin-editor') {
        setCurrentSlug(slug);
        fetchCsrfToken();
        checkAuth().then((authResult) => {
          if (authResult.isAuthenticated) {
            // After auth check, load the card data
            const matchingCard = authResult.cardList.find(card => card.slug === slug);
            handleEdit(slug, matchingCard?.userId || null);
          } else {
            // If auth fails, redirect to login (explicit redirect for unauthorized access)
            navigate('/login');
          }
        }).catch(() => {
          // If auth fails, redirect to login
          navigate('/login');
        });
      }
      return;
    }

    // Skip public card route handling here - PublicCardRoute component handles it
    // This prevents duplicate fetches
    // Public routes: /:orgSlug/:cardSlug or /:slug (short code or legacy)
    const pathParts = path.substring(1).split('/').filter(p => p);
    const isShortCode = pathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(pathParts[0]);
    const isOrgScoped = pathParts.length === 2 && pathParts[0] && pathParts[1];
    const isPublicRoute = isShortCode || isOrgScoped || (pathParts.length === 1 && pathParts[0] && !path.startsWith('/people') && !path.startsWith('/login') && !path.startsWith('/setup') && !path.startsWith('/settings') && !path.startsWith('/users') && !path.startsWith('/profile') && !path.startsWith('/organizations') && !path.startsWith('/departments') && !path.startsWith('/cards') && path !== '/');

    if (isPublicRoute) {
      // Don't interfere with public card routes - let PublicCardRoute handle it
      // Only set initial state if view is still 'loading' (first render)
      // Once PublicCardRoute takes over (view is 'public-loading' or 'public-card'), don't touch it
      if (view === 'loading') {
        setView('public-loading');
      } else if (view === 'public-loading' || view === 'public-card' || view === '404') {
        // PublicCardRoute is managing state - don't interfere
      }
      return;
    }

    // Set view based on route
    if (path === '/setup') {
      // Check if setup is already complete - if so, redirect to login
      fetchCsrfToken();
      checkSetupStatus().then((status) => {
        if (status?.rateLimited) {
          setView('admin-login');
          document.title = t('auth.login.title', 'Login');
          return;
        }
        if (status && status.setupComplete && status.userCount > 0) {
          // Setup already complete, redirect to login
          navigate('/login');
        } else {
          // Setup not complete, show wizard
          setView('setup-wizard');
          document.title = t('auth.setup.title', 'Initial Setup');
        }
      }).catch(() => {
        // If check fails, show wizard anyway
        setView('setup-wizard');
        document.title = t('auth.setup.title', 'Initial Setup');
      });
      return;
    } else if (path === '/login') {
      // Login page - check if already authenticated
      setView('loading');
      fetchCsrfToken();
      checkAuth().then((authResult) => {
        if (authResult.isAuthenticated) {
          // If authenticated, redirect to dashboard (explicit redirect)
          navigate('/people');
        } else {
          // Not authenticated, show login page
          setView('admin-login');
          document.title = t('auth.login.title', 'Login');
        }
      }).catch(() => {
        // Not authenticated, show login page
        setView('admin-login');
        document.title = t('auth.login.title', 'Login');
      });
    } else if (path === '/settings') {
      setView('admin-settings');
      fetchCsrfToken();
      checkAuth().then((authResult) => {
        if (!authResult.isAuthenticated) {
          navigate('/login');
          return;
        }
        if (authResult.userData?.role !== 'owner' && authResult.userData?.role !== 'admin') {
          navigate('/people');
        }
      });
    } else if (path === '/users') {
      setView('user-management');
      fetchCsrfToken();
      checkAuth().then((authResult) => {
        if (!authResult.isAuthenticated) {
          navigate('/login');
          return;
        }
        if (authResult.userData?.role !== 'owner' && authResult.userData?.role !== 'admin') {
          navigate('/people');
        }
      });
    } else if (path === '/profile') {
      setView('profile-management');
      fetchCsrfToken();
      checkAuth().then((authResult) => {
        if (!authResult.isAuthenticated) {
          navigate('/login');
        }
      });
    } else if (path === '/departments') {
      setView('department-management');
      fetchCsrfToken();
      checkAuth().then((authResult) => {
        if (!authResult.isAuthenticated) {
          navigate('/login');
          return;
        }
        if (authResult.userData?.role !== 'owner' && authResult.userData?.role !== 'admin') {
          navigate('/people');
        }
      });
    } else if (path === '/organizations') {
      setView('organisation-management');
      fetchCsrfToken();
      checkAuth().then((authResult) => {
        if (!authResult.isAuthenticated) {
          navigate('/login');
          return;
        }
        if (authResult.userData?.role !== 'admin') {
          navigate('/people');
          return;
        }
        fetchOrganisations('admin');
      });
    } else if (path === '/admin' || path === '/' || path === '/people') {
      // Check demo mode status first (will be known from setup/status response)
      fetchCsrfToken();
      checkSetupStatus().then((status) => {
        // Check if demo mode is active (from status response or isDemoMode state)
        const demoModeActive = status?.demoMode || isDemoMode;

        if (demoModeActive) {
          // Demo mode: skip setup, go directly to auth check
          if (path === '/' || path === '/admin') {
            // Redirect root/admin to /people (explicit redirect)
            navigate('/people');
            return; // Let next effect run handle /people
          }
          // Set view to loading while checking auth
          setView('loading');
          checkAuth().then((authResult) => {
            if (typeof window !== 'undefined' && window.location.pathname !== path) {
              return;
            }
            if (authResult.isAuthenticated) {
              // Demo mode: always show dashboard for demo user (owner role)
              setView('admin-dashboard');
              document.title = t('dashboard.meta.adminTitle', 'Admin Dashboard');
            } else {
              // This shouldn't happen in demo mode, but fallback to login just in case
              navigate('/login');
            }
          }).catch((e) => {
            console.error('Auth check failed:', e);
            navigate('/login');
          });
          return;
        }

        // Normal mode: continue with regular auth flow
        // (status already checked above, setupComplete must be true to get here)
        if (status?.rateLimited) {
          if (path === '/' || path === '/admin') {
            navigate('/login');
          } else {
            setView('admin-login');
            document.title = t('auth.login.title', 'Login');
          }
        } else if (status === null) {
          // If check failed (server not running, network error, etc.), default to setup wizard
          navigate('/setup');
          document.title = t('auth.setup.title', 'Initial Setup');
        } else if (!status.setupComplete || status.userCount === 0) {
          // Setup not complete or no users exist, show setup wizard
          navigate('/setup');
          document.title = t('auth.setup.title', 'Initial Setup');
        } else {
          // Setup complete, check authentication
          if (path === '/' || path === '/admin') {
            // Redirect root/admin to /people (explicit redirect)
            navigate('/people');
            return; // Let next effect run handle /people
          }
          // Set view to loading while checking auth
          setView('loading');
          checkAuth().then((authResult) => {
            if (typeof window !== 'undefined' && window.location.pathname !== path) {
              return;
            }
            if (authResult.isAuthenticated) {
              // Owners and members both see dashboard overview on /people.
              setView('admin-dashboard');
              document.title = t('dashboard.meta.adminTitle', 'Admin Dashboard');
            } else {
              // Not authenticated, redirect to login (explicit redirect)
              navigate('/login');
            }
          }).catch((e) => {
            console.error('Auth check failed:', e);
            navigate('/login');
          });
        }
      }).catch((e) => {
        // If promise rejects, default to setup wizard
        console.error('Setup status check failed:', e);
        setView(isAuthenticated ? view : 'admin-login');
        document.title = isAuthenticated ? document.title : t('auth.login.title', 'Login');
      });
    }
  }, [currentSlug, fetchOrganisations, isDemoMode, location.pathname, navigate]);


  // checkAuth: Only updates state, never navigates
  // Returns { isAuthenticated: boolean, userData: object | null, cardList: array }
  const checkAuth = async ({ force = false } = {}) => {
    const now = Date.now();
    if (!force) {
      if (authCheckInFlightRef.current) {
        return authCheckInFlightRef.current;
      }
      const cached = lastAuthCheckRef.current;
      if (cached.result && now - cached.timestamp < 3000) {
        return cached.result;
      }
    }

    const authPromise = (async () => {
      try {
        if (!csrfToken) {
          await fetchCsrfToken();
        }
        const userRes = await apiCall(`${API_ENDPOINT}/auth/me`);
        if (userRes.ok) {
          const userData = await userRes.json();
          setUserRole(userData.role);
          setCurrentUserId(userData.id);
          setCurrentUserEmail(userData.email);
          const preferences = await fetchUserPreferences();

          let resolvedAdminOrganisationId = preferences?.activeOrganisationId || selectedOrganisationId || localStorage.getItem('selectedOrganisationId') || '';

          if (userData.role === 'admin') {
            const storedSelectionOwner = localStorage.getItem('selectedOrganisationOwner');
            if (storedSelectionOwner !== userData.id) {
              const fallbackOrganisationId = preferences?.activeOrganisationId || userData.organisationId || '';
              setSelectedOrganisationId(fallbackOrganisationId);
              if (fallbackOrganisationId) {
                localStorage.setItem('selectedOrganisationId', fallbackOrganisationId);
              } else {
                localStorage.removeItem('selectedOrganisationId');
              }
              resolvedAdminOrganisationId = fallbackOrganisationId;
            } else if (!resolvedAdminOrganisationId && userData.organisationId) {
              resolvedAdminOrganisationId = userData.organisationId;
              localStorage.setItem('selectedOrganisationId', userData.organisationId);
              setSelectedOrganisationId(userData.organisationId);
            } else if (resolvedAdminOrganisationId && selectedOrganisationId !== resolvedAdminOrganisationId) {
              setSelectedOrganisationId(resolvedAdminOrganisationId);
            }
            localStorage.setItem('selectedOrganisationOwner', userData.id);
          } else {
            localStorage.removeItem('selectedOrganisationOwner');
          }

          if (userData.role === 'admin' && !resolvedAdminOrganisationId) {
            setCardList([]);
            setIsAuthenticated(true);
            await fetchOrganisations(userData.role);
            return { isAuthenticated: true, userData, cardList: [] };
          }

          const res = await apiCall(buildScopedApiUrl(`${API_ENDPOINT}/admin/cards`));
          if (res.ok) {
            const list = await res.json();
            setCardList(list);
            setIsAuthenticated(true);
            if (userData.role === 'admin') {
              await fetchOrganisations(userData.role);
            }
            const canUseOrgSettings = userData.role === 'owner'
              || userData.role === 'department_lead'
              || (userData.role === 'admin' && resolvedAdminOrganisationId);
            if (canUseOrgSettings) {
              fetchSettings(resolvedAdminOrganisationId || null);
            } else if (userData.role === 'member') {
              try {
                const memberSettingsRes = await apiCall(`${API_ENDPOINT}/member/settings`);
                if (memberSettingsRes.ok) {
                  const settingsData = await memberSettingsRes.json();
                  setSettings({
                    ...settingsData,
                    theme_variant: 'mint',
                    theme_colors: initializeColorDataForSettings(settingsData.theme_colors || []),
                    app_theme_overrides: settingsData.app_theme_overrides && typeof settingsData.app_theme_overrides === 'object'
                      ? settingsData.app_theme_overrides
                      : {}
                  });
                } else {
                  setSettings((prev) => ({ ...prev, theme_variant: 'mint', app_theme_overrides: {} }));
                }
              } catch (_) {
                setSettings((prev) => ({ ...prev, theme_variant: 'mint', app_theme_overrides: {} }));
              }
              applyThemeCssVars('mint');
              document.body.classList.remove('theme-vcard', 'theme-minimal', 'theme-mint', 'theme-nagarro', 'theme-custom');
              document.body.classList.add('theme-mint');
            }

            return { isAuthenticated: true, userData, cardList: list };
          }

          if (userData.role === 'admin' && (res.status === 400 || res.status === 404)) {
            setCardList([]);
            setIsAuthenticated(true);
            await fetchOrganisations(userData.role);
            return { isAuthenticated: true, userData, cardList: [] };
          }

          setIsAuthenticated(false);
          setUserRole(null);
          setUserLanguage('en');
          return { isAuthenticated: false, userData: null, cardList: [] };
        }

        setIsAuthenticated(false);
        setUserRole(null);
        setUserLanguage('en');
        return { isAuthenticated: false, userData: null, cardList: [] };
      } catch (e) {
        setIsAuthenticated(false);
        setUserRole(null);
        setUserLanguage('en');
        return { isAuthenticated: false, userData: null, cardList: [] };
      }
    })();

    authCheckInFlightRef.current = authPromise;
    try {
      const result = await authPromise;
      lastAuthCheckRef.current = {
        timestamp: Date.now(),
        result
      };
      return result;
    } finally {
      if (authCheckInFlightRef.current === authPromise) {
        authCheckInFlightRef.current = null;
      }
    }
  };

  const fetchPublicCard = async (slug) => {
    setIsPublicLoading(true);
    setView('public-loading'); // Ensure view is set to loading while fetching
    try {
      // Fetch settings first so colors are available
      await fetchPublicSettings();
      const res = await fetch(`${API_ENDPOINT}/cards/${slug}`);
      if (res.ok) {
        const json = await res.json();
        setData(mergeCardDataWithDefaults(json, settings));
        setView('public-card');
        document.title = json.personal?.name ? `${json.personal.name} - VCARD Card` : "VCARD Card";
      } else {
        setError('Card not found');
        setView('404');
        document.title = "Card Not Found";
      }
    } catch (e) {
      console.error('Error fetching public card:', e);
      setError('Connection failed');
      setView('404');
      document.title = "Error Loading Card";
    } finally {
      setIsPublicLoading(false);
    }
  };

  const fetchCardByShortCode = useCallback(async (shortCode) => {
    setIsPublicLoading(true);
    try {
      // Fetch card first to get org slug, then fetch settings for that org
      const res = await fetch(`${API_ENDPOINT}/cards/short/${shortCode}`);
      if (res.ok) {
        const cardData = await res.json();
        // Fetch settings for the organization that owns this card
        const orgSlug = cardData._orgSlug || 'default';
        await fetchPublicSettings(orgSlug);

        const mergedData = {
          ...mergeCardDataWithDefaults(cardData, settings),
          _shortCode: cardData._shortCode // Preserve short code from backend
        };
        // Set isPublicLoading to false FIRST, then data, then view
        // This prevents renderAdminViews from showing loading when view='public-card'
        setIsPublicLoading(false);
        setData(mergedData);
        setView('public-card');
        const name = `${cardData.personal?.firstName || ''} ${cardData.personal?.lastName || ''}`.trim();
        document.title = name || 'Card';
      } else {
        setView('404');
        setError('Card not found');
        document.title = 'Card Not Found';
      }
    } catch (e) {
      console.error('[FRONTEND] Error fetching card by short code:', e);
      setView('404');
      setError('Failed to load card');
      document.title = 'Card Not Found';
    } finally {
      setIsPublicLoading(false);
    }
  }, [settings]);

  const fetchCardByOrgAndSlug = useCallback(async (orgSlug, cardSlug) => {
    setIsPublicLoading(true);
    try {
      // Fetch settings for the organization from the URL
      await fetchPublicSettings(orgSlug);
      const res = await fetch(`${API_ENDPOINT}/cards/${orgSlug}/${cardSlug}`);
      if (res.ok) {
        const cardData = await res.json();
        const mergedData = {
          ...mergeCardDataWithDefaults(cardData, settings),
          _shortCode: cardData._shortCode // Preserve short code from backend
        };
        // Set isPublicLoading to false FIRST, then data, then view
        // This prevents renderAdminViews from showing loading when view='public-card'
        setIsPublicLoading(false);
        setData(mergedData);
        setView('public-card');
        const name = `${cardData.personal?.firstName || ''} ${cardData.personal?.lastName || ''}`.trim();
        document.title = name || 'Card';
      } else {
        setView('404');
        setError('Card not found');
        document.title = 'Card Not Found';
      }
    } catch (e) {
      console.error('[FRONTEND] Error fetching card by org and slug:', e);
      setView('404');
      setError('Failed to load card');
      document.title = 'Card Not Found';
    } finally {
      setIsPublicLoading(false);
    }
  }, [settings]);

  // Helper to initialize color data for settings (shared between fetchSettings and SettingsView)
  // Note: SettingsView has its own initializeColorData function, but we need this one for fetchSettings
  const initializeColorDataForSettings = (colors) => {
    if (!colors || !Array.isArray(colors)) return [];
    return colors.map(color => {
      let hexBase, baseColor, colorType;

      // Determine if this is a standard color (has Tailwind gradient or baseColor) or custom hex
      const hasValidTailwindGradient = color.gradient && typeof color.gradient === 'string' && color.gradient.startsWith('from-');
      const hasHexBase = color.hexBase && typeof color.hexBase === 'string' && color.hexBase.startsWith('#');

      if (hasValidTailwindGradient) {
        // Convert from Tailwind gradient to hex
        const extracted = extractBaseColorFromGradient(color.gradient);
        if (extracted) {
          baseColor = extracted.baseColor;
          hexBase = getTailwindColorHex(extracted.baseColor, 600); // Always use shade 600
          colorType = 'standard';
        } else {
          // Fallback if extraction fails
          baseColor = color.baseColor || 'indigo';
          hexBase = hasHexBase ? color.hexBase : getTailwindColorHex(baseColor, 600);
          colorType = color.colorType === 'custom' ? 'custom' : 'standard';
        }
      } else if (hasHexBase) {
        // Has hexBase - determine if standard or custom
        if (color.baseColor && color.colorType !== 'custom') {
          // Standard color with hexBase
          baseColor = color.baseColor;
          hexBase = color.hexBase;
          colorType = 'standard';
        } else {
          // Custom hex color
          baseColor = null;
          hexBase = color.hexBase;
          colorType = 'custom';
        }
      } else if (color.gradientStyle) {
        // Has gradientStyle but no hexBase - extract from gradientStyle or use default
        baseColor = null;
        hexBase = '#4f46e5'; // Default
        colorType = 'custom';
      } else {
        // Fallback - treat as standard with default
        baseColor = color.baseColor || 'indigo';
        hexBase = getTailwindColorHex(baseColor, 600);
        colorType = color.colorType === 'custom' ? 'custom' : 'standard';
      }

      // Always auto-generate complementary secondary color
      const complementaryColor = baseColor ? getComplementaryColor(baseColor) : null;
      const hexSecondary = color.hexSecondary || (complementaryColor ? getTailwindColorHex(complementaryColor, 600) : hexBase);

      // Generate all inline styles
      const gradientStyle = `linear-gradient(135deg, ${hexBase}, ${hexSecondary})`;
      const buttonStyle = hexBase;
      const linkStyle = hexBase;
      const textStyle = hexBase;

      // Build clean hex-only color object
      return {
        name: color.name,
        colorType: colorType,
        baseColor: baseColor, // null for custom, Tailwind name for standard
        hexBase: hexBase,
        hexSecondary: hexSecondary,
        gradientStyle: gradientStyle,
        buttonStyle: buttonStyle,
        linkStyle: linkStyle,
        textStyle: textStyle
      };
    });
  };

  const getSettingsCacheKey = (organisationId = '') => (
    organisationId ? `organisationSettingsCache:${organisationId}` : 'organisationSettingsCache:global'
  );

  const normalizeFetchedSettings = (settingsData) => ({
    ...settingsData,
    theme_variant: normalizeThemeVariantClient(settingsData.theme_variant || 'vcard'),
    theme_colors: initializeColorDataForSettings(settingsData.theme_colors || []),
    app_theme_overrides: settingsData.app_theme_overrides && typeof settingsData.app_theme_overrides === 'object'
      ? settingsData.app_theme_overrides
      : {}
  });

  const applyFetchedSettings = (settingsData, organisationId = '') => {
    const merged = normalizeFetchedSettings(settingsData);
    setSettings(merged);
    applyThemeCssVars(merged.theme_variant, merged.app_theme_overrides);
    document.body.classList.remove('theme-vcard', 'theme-minimal', 'theme-mint', 'theme-nagarro', 'theme-custom');
    document.body.classList.add(`theme-${merged.theme_variant}`);
    try {
      localStorage.setItem(getSettingsCacheKey(organisationId), JSON.stringify(merged));
    } catch (_) {
      // Ignore cache write failures
    }
    return merged;
  };

  const hydrateSettingsFromCache = (organisationId = '') => {
    try {
      const raw = localStorage.getItem(getSettingsCacheKey(organisationId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return applyFetchedSettings(parsed, organisationId);
    } catch (_) {
      return null;
    }
  };

  const fetchSettings = async (organisationIdOverride = null) => {
    try {
      const activeOrganisationId = organisationIdOverride ?? selectedOrganisationId ?? localStorage.getItem('selectedOrganisationId') ?? '';
      const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(activeOrganisationId);
      if (userRole === 'admin' && !looksLikeUuid) {
        hydrateSettingsFromCache(activeOrganisationId);
        return;
      }
      const targetUrl = userRole === 'admin' && activeOrganisationId
        ? `${API_ENDPOINT}/admin/settings?organisationId=${encodeURIComponent(activeOrganisationId)}`
        : `${API_ENDPOINT}/admin/settings`;
      const res = await apiCall(targetUrl);
      if (res.ok) {
        const settingsData = await res.json();
        applyFetchedSettings(settingsData, activeOrganisationId);
      } else {
        hydrateSettingsFromCache(activeOrganisationId);
      }
    } catch (e) {
      console.error('Failed to fetch settings:', e);
      const activeOrganisationId = organisationIdOverride ?? selectedOrganisationId ?? localStorage.getItem('selectedOrganisationId') ?? '';
      hydrateSettingsFromCache(activeOrganisationId);
    }
  };

  const fetchPublicSettings = async (orgSlug = 'default') => {
    try {
      const url = orgSlug ? `${API_ENDPOINT}/settings?orgSlug=${encodeURIComponent(orgSlug)}` : `${API_ENDPOINT}/settings`;
      const res = await fetch(url);
      if (res.ok) {
        const settingsData = await res.json();
        const mergedVariant = normalizeThemeVariantClient(settingsData.theme_variant || 'vcard');

        // Match admin pattern: create new object from API response
        // This ensures React sees it as a new object and triggers re-renders
        const merged = {
          default_organisation: settingsData.default_organisation || 'My Organisation',
          theme_variant: mergedVariant,
          theme_colors: (settingsData.theme_colors && Array.isArray(settingsData.theme_colors))
            ? initializeColorDataForSettings(settingsData.theme_colors)
            : THEME_PRESETS.vcard,
          app_theme_overrides: settingsData.app_theme_overrides && typeof settingsData.app_theme_overrides === 'object'
            ? settingsData.app_theme_overrides
            : {}
        };

        // Set state and apply theme immediately (same pattern as fetchSettings)
        setSettings(merged);
        applyThemeCssVars(mergedVariant, merged.app_theme_overrides);
        document.body.classList.remove('theme-vcard', 'theme-minimal', 'theme-mint', 'theme-nagarro', 'theme-custom');
        document.body.classList.add(`theme-${mergedVariant}`);
      }
    } catch (e) {
      console.error('Failed to fetch public settings:', e);
    }
  };

  // Group cards by user (userId or userEmail as fallback)
  const groupCardsByUser = (cards) => {
    const userMap = new Map();

    cards.forEach(card => {
      // Use userId as primary key, fallback to userEmail if userId not available
      const userKey = card.userId || card.userEmail || 'unknown';

      if (!userMap.has(userKey)) {
        userMap.set(userKey, {
          userId: card.userId || null,
          userEmail: card.userEmail || null,
          userRole: card.userRole || null,
          userCreatedAt: card.userCreatedAt || null,
          cards: []
        });
      }

      // Add card to user's cards array (even if slug is null, it represents a user without cards)
      userMap.get(userKey).cards.push(card);
    });

    // Convert map to array and sort by user creation date (newest first)
    return Array.from(userMap.values()).sort((a, b) => {
      if (!a.userCreatedAt && !b.userCreatedAt) return 0;
      if (!a.userCreatedAt) return 1;
      if (!b.userCreatedAt) return -1;
      return new Date(b.userCreatedAt) - new Date(a.userCreatedAt);
    });
  };

  const fetchCardList = async () => {
    try {
      const res = await apiCall(buildScopedApiUrl(`${API_ENDPOINT}/admin/cards`));
      if (res.ok) {
        const list = await res.json();
        setCardList(list);
        // Removed setView('admin-dashboard') - fetchCardList should only update data, not view
        // View should be determined by route, not by data fetching
        setIsAuthenticated(true);
        // Fetch settings when dashboard loads (only if we're actually on dashboard)
        if (location.pathname === '/people') {
          if (userRole === 'owner' || userRole === 'department_lead' || (userRole === 'admin' && selectedOrganisationId)) {
            fetchSettings();
          }
        }
      } else {
        // For admins, a stale/invalid org context can return 400/404.
        // This is not an auth failure: keep session alive and recover org selection.
        if (userRole === 'admin' && (res.status === 400 || res.status === 404)) {
          setCardList([]);
          await fetchOrganisations();
          return;
        }

        // Only 401 should force logout/login state.
        if (res.status === 401) {
          setIsAuthenticated(false);
          if (location.pathname !== '/login' && location.pathname !== '/setup') {
            setView('admin-login');
          }
          return;
        }

        // Keep session state for non-auth API errors to avoid breaking route access.
        console.error('Failed to fetch card list:', res.status);
      }
    } catch (e) {
      // Network/runtime errors should not immediately invalidate auth state.
      // This prevents accidental lockout from admin route pages.
      console.error('Card list request failed:', e);
    }
  };

  const handleCreateOrganisation = async (name) => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/organisations`, {
        method: 'POST',
        body: JSON.stringify({ name })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showAlert(data.error || t('organizations.alerts.createFailed', 'Failed to create organisation'), 'error');
        return false;
      }
      const data = await res.json();
      showAlert(t('organizations.alerts.created', 'Organisation created'), 'success');
      await fetchOrganisations();
      if (data?.organisation?.id) {
        setSelectedOrganisationId(data.organisation.id);
        localStorage.setItem('selectedOrganisationId', data.organisation.id);
        await saveActiveOrganisationPreference(data.organisation.id);
      }
      return true;
    } catch (e) {
      showAlert(t('organizations.alerts.createError', 'Error creating organisation'), 'error');
      return false;
    }
  };

  const handleDeleteOrganisation = async (organisationId, confirmation) => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/organisations/${organisationId}`, {
        method: 'DELETE',
        body: JSON.stringify({ confirmation })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showAlert(data.error || t('organizations.alerts.deleteFailed', 'Failed to delete organisation'), 'error');
        return false;
      }
      showAlert(t('organizations.alerts.deleted', 'Organisation deleted'), 'success');
      const orgs = await fetchOrganisations();
      if (selectedOrganisationId === organisationId) {
        const nextOrgId = orgs?.[0]?.id || '';
        setSelectedOrganisationId(nextOrgId);
        if (nextOrgId) {
          localStorage.setItem('selectedOrganisationId', nextOrgId);
        } else {
          localStorage.removeItem('selectedOrganisationId');
        }
        await saveActiveOrganisationPreference(nextOrgId || null);
      }
      return true;
    } catch (e) {
      showAlert(t('organizations.alerts.deleteError', 'Error deleting organisation'), 'error');
      return false;
    }
  };

  useEffect(() => {
    if (userRole === 'admin' && selectedOrganisationId && isAuthenticated) {
      fetchCardList();
    }
  }, [selectedOrganisationId, userRole, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || view !== 'admin-settings') return;
    if (userRole === 'owner') {
      fetchSettings();
      return;
    }
    if (userRole === 'admin' && selectedOrganisationId) {
      fetchSettings(selectedOrganisationId);
    }
  }, [view, isAuthenticated, selectedOrganisationId, userRole]);

  useEffect(() => {
    if (!isAuthenticated || view !== 'profile-management') return;
    if (userRole === 'owner') {
      fetchSettings();
      return;
    }
    if (userRole === 'admin' && selectedOrganisationId) {
      fetchSettings(selectedOrganisationId);
      return;
    }
    if (userRole === 'member' || userRole === 'department_lead') {
      checkAuth({ force: true });
    }
  }, [view, isAuthenticated, selectedOrganisationId, userRole]);

  useEffect(() => {
    if (userRole !== 'admin' || selectedOrganisationId) return;
    const storedSelectedOrganisationId = localStorage.getItem('selectedOrganisationId') || '';
    if (storedSelectedOrganisationId) {
      setSelectedOrganisationId(storedSelectedOrganisationId);
    }
  }, [selectedOrganisationId, userRole]);

  useEffect(() => {
    const rawPath = location.pathname || '/';
    const normalizedPath = rawPath.length > 1 ? rawPath.replace(/\/+$/, '') : rawPath;
    if (userRole === 'admin' && (view === 'organisation-management' || normalizedPath === '/organizations')) {
      fetchOrganisations(userRole);
    }
  }, [userRole, view, location.pathname, fetchOrganisations]);

  // Hard route/view sync: keep /organizations on organisation-management,
  // even if another async flow tries to switch back to dashboard.
  useEffect(() => {
    const rawPath = location.pathname || '/';
    const normalizedPath = rawPath.length > 1 ? rawPath.replace(/\/+$/, '') : rawPath;
    if (normalizedPath === '/organizations' && view !== 'organisation-management') {
      setView('organisation-management');
    }
  }, [location.pathname, view]);

  const refreshOrganisations = useCallback(() => {
    return fetchOrganisations('admin');
  }, [fetchOrganisations]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(`${API_ENDPOINT}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.toLowerCase().trim(), password })
      });
      if (res.ok) {
        // Token is now in httpOnly cookie, fetch CSRF token and check auth
        await fetchCsrfToken();
        const authResult = await checkAuth();
        if (authResult.isAuthenticated) {
          // Navigate to dashboard after successful login (explicit user action)
          navigate('/people');
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        setError(errorData.error || t('auth.login.errors.invalidCredentials', 'Invalid email or password'));
      }
    } catch (e) {
      setError(t('auth.login.errors.failed', 'Login failed'));
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_ENDPOINT}/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.error('Logout API call failed:', e);
    }
    // Force hard redirect immediately - don't set state first as it causes race conditions
    // The hard redirect will reload the page and useEffect will handle the login view
    window.location.href = '/login';
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setIsSettingUp(true);
    setError('');
    try {
      const res = await fetch(`${API_ENDPOINT}/setup/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({
          organisationName: setupData.organisationName.trim(),
          adminEmail: setupData.adminEmail.toLowerCase().trim(),
          adminPassword: setupData.adminPassword
        })
      });
      if (res.ok) {
        // Setup successful, automatically log in
        setIsSuccessSetup(true);
        setTimeout(() => setIsSuccessSetup(false), 2000);
        const authResult = await checkAuth();
        if (authResult.isAuthenticated) {
          // Navigate to dashboard after successful setup (explicit user action)
          navigate('/people');
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        setError(errorData.error || t('auth.setup.errors.failed', 'Setup failed'));
      }
    } catch (e) {
      setError(t('auth.setup.errors.retry', 'Setup failed. Please try again.'));
    } finally {
      setIsSettingUp(false);
    }
  };

  const handleCreateNew = () => {
    if (userRole === 'admin' && !selectedOrganisationId) {
      showAlert(t('common.errors.selectOrganisationFirst', 'Please select an organisation first'), 'info');
      navigate('/organizations');
      return;
    }
    if (canManagePeople) {
      // Show action selection modal for owners
      setActionSelectionModal({ isOpen: true });
    } else {
      // Members go directly to card creation (for themselves, no userId needed)
      setCreateCardModal({ isOpen: true, slug: '', userId: null });
    }
  };

  const handleOrganisationSettingsClick = () => {
    const activeOrganisationId = selectedOrganisationId || localStorage.getItem('selectedOrganisationId') || '';
    if (userRole === 'admin' && !activeOrganisationId) {
      showAlert(t('common.errors.selectOrganisationFirst', 'Please select an organisation first'), 'info');
      window.location.assign('/organizations');
      return;
    }
    window.location.assign('/settings');
  };

  const handleOpenProfile = () => {
    setView('profile-management');
    window.location.assign('/profile');
  };

  const headerPath = location.pathname || '';
  const normalizedHeaderPath = headerPath.length > 1 ? headerPath.replace(/\/+$/, '') : headerPath;
  const routeLockedView = (() => {
    if (normalizedHeaderPath === '/organizations') return 'organisation-management';
    if (normalizedHeaderPath === '/departments') return 'department-management';
    if (normalizedHeaderPath === '/settings') return 'admin-settings';
    if (normalizedHeaderPath === '/users') return 'user-management';
    if (normalizedHeaderPath === '/profile') return 'profile-management';
    return null;
  })();
  const activeView = routeLockedView || view;
  const isHeaderUsersActive = normalizedHeaderPath.startsWith('/users') || normalizedHeaderPath.startsWith('/people');
  const isHeaderDepartmentsActive = normalizedHeaderPath.startsWith('/departments');
  const isHeaderOrganisationsActive = normalizedHeaderPath.startsWith('/organizations');
  const isHeaderOrgSettingsActive = normalizedHeaderPath.startsWith('/settings');
  const canShowTeamNavButtons = isOrgAdmin && (!userRole || userRole !== 'admin' || selectedOrganisationId);
  const canShowOrgNavButtons = userRole === 'admin' || isOrgAdmin;
  const headerPillBase = "px-4 py-2.5 md:px-6 md:py-3 rounded-full border text-base font-semibold transition-all whitespace-nowrap flex items-center gap-2";
  const headerPillDefault = `${headerPillBase} bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark border-border dark:border-border-dark hover:bg-surface dark:hover:bg-surface-dark`;
  const headerPillActive = `${headerPillBase} bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark border-action dark:border-action-dark shadow-[0_0_0_2px_rgba(37,99,235,0.15)]`;
  const headerPillDanger = `${headerPillBase} bg-card dark:bg-card-dark text-error dark:text-error-text-dark border-error-border dark:border-error-border-dark hover:bg-error-bg dark:hover:bg-error-bg-dark`;
  const headerIconPill = "w-11 h-11 rounded-full border border-border dark:border-border-dark bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors flex items-center justify-center";

  const handleCreateCardConfirm = () => {
    const slug = createCardModal.slug.toLowerCase().trim().replace(/[^a-z0-9-]/g, '');
    if (!slug) {
      showAlert(t('editor.createCard.errors.invalidSlug', 'Please enter a valid user URL (e.g., "sarah")'), 'error', t('editor.createCard.errors.invalidSlugTitle', 'Invalid User URL'));
      return;
    }
    if (slug.length < 1) {
      showAlert(t('editor.createCard.errors.slugTooShort', 'User URL must be at least 1 character long'), 'error', t('editor.createCard.errors.invalidSlugTitle', 'Invalid User URL'));
      return;
    }
    // Prevent duplicate user URLs for the same user
    // Flatten all cards from grouped structure to check for duplicates
    const allCards = cardList.filter(c => c.slug === slug);
    if (allCards.length > 0) {
      showAlert(
        t('editor.createCard.errors.slugExists', 'A card with user URL "{{slug}}" already exists. Please choose another user URL.', { slug }),
        'error',
        t('editor.createCard.errors.slugExistsTitle', 'User URL already exists')
      );
      return;
    }
    // Store userId for this new card if provided
    if (createCardModal.userId) {
      setTargetUserIdForNewCard(createCardModal.userId);
    }
    setCreateCardModal({ isOpen: false, slug: '', userId: null });
    setCurrentSlug(slug);
    setData(getDefaultTemplate(settings));
    setView('admin-editor');
    // Navigate to editor route, just like handleEdit does
    navigate(`/people/edit/${slug}`);
  };

  const handleCreateCardCancel = () => {
    setCreateCardModal({ isOpen: false, slug: '' });
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password) {
      if (showAlert) showAlert(t('users.errors.emailPasswordRequired', 'Email and password are required'), 'error');
      return;
    }

    if (newUser.password.length < 8) {
      if (showAlert) showAlert(t('auth.setup.passwordHint', 'Password must be at least 8 characters long'), 'error');
      return;
    }

    setIsSavingUser(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/users`, {
        method: 'POST',
        body: JSON.stringify(newUser)
      });

      if (res.ok) {
        setIsSuccessCreateUser(true);
        setTimeout(() => setIsSuccessCreateUser(false), 2000);
        setShowCreateUserModal(false);
        setNewUser({ email: '', password: '', role: 'member', departmentId: '' });
        checkAuth(); // Refresh card list
      } else {
        // Try to parse error response
        let errorMessage = t('users.errors.createFailed', 'Failed to create user');
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          console.error('User creation failed:', {
            status: res.status,
            statusText: res.statusText,
            error: errorData
          });
        } catch (parseError) {
          // Response is not JSON, use status text
          console.error('User creation failed - non-JSON response:', {
            status: res.status,
            statusText: res.statusText
          });
          errorMessage = `Failed to create user: ${res.status} ${res.statusText}`;
        }
        if (showAlert) showAlert(errorMessage, 'error');
        // Don't close modal on error - let user fix and retry
      }
    } catch (e) {
      console.error('Error creating user:', e);
      const errorMessage = e.message || t('users.errors.createRetry', 'Error creating user. Please try again.');
      if (showAlert) showAlert(errorMessage, 'error');
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!newInvitation.email) {
      if (showAlert) showAlert(t('users.errors.emailRequired', 'Email is required'), 'error');
      return;
    }

    setIsSavingUser(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/invitations`, {
        method: 'POST',
        body: JSON.stringify(newInvitation)
      });
      if (res.ok) {
        setIsSuccessInvite(true);
        setTimeout(() => setIsSuccessInvite(false), 2000);
        setShowInviteModal(false);
        setNewInvitation({ email: '', role: 'member' });
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || t('users.invitations.sendFailed', 'Failed to send invitation'), 'error');
      }
    } catch (e) {
      if (showAlert) showAlert(t('users.invitations.sendError', 'Error sending invitation'), 'error');
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        if (showAlert) showAlert(t('users.roles.updated', 'User role updated successfully'), 'success');
        checkAuth(); // Refresh card list
        setEditingUserId(null);
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || t('users.roles.updateFailed', 'Failed to update role'), 'error');
      }
    } catch (e) {
      if (showAlert) showAlert(t('users.roles.updateError', 'Error updating role'), 'error');
    }
  };

  const handleRemoveUser = async (userId, userEmail) => {
    if (showConfirm) {
      showConfirm(
        t(
          'users.confirm.deleteUserMessage',
          'Are you sure you want to permanently delete {{email}}? This will delete the user and all their cards. This action cannot be undone.',
          { email: userEmail }
        ),
        async () => {
          try {
            const res = await apiCall(`${API_ENDPOINT}/admin/users/${userId}`, {
              method: 'DELETE'
            });
            if (res.ok) {
              if (showAlert) showAlert(t('users.alerts.deleted', 'User deleted successfully'), 'success');
              checkAuth(); // Refresh card list
            } else {
              const errorData = await res.json().catch(() => ({}));
              if (showAlert) showAlert(errorData.error || t('users.errors.deleteFailed', 'Failed to delete user'), 'error');
            }
          } catch (e) {
            if (showAlert) showAlert(t('users.errors.deleteError', 'Error deleting user'), 'error');
          }
        },
        t('users.confirm.deleteUserTitle', 'Delete User'),
        t('common.actions.delete', 'Delete'),
        t('common.actions.cancel', 'Cancel')
      );
    }
  };

  const handleEdit = async (slug, userId = null) => {
    const res = await fetch(`${API_ENDPOINT}/cards/${slug}`, {
      credentials: 'include'
    });
    if (res.ok) {
      const json = await res.json();
      const mergedData = mergeCardDataWithDefaults(json, settings);
      setData({
        ...mergedData,
        personal: {
          ...mergedData.personal,
          company: settings?.default_organisation || json.personal?.company || mergedData.personal.company
        }
      });
      setCurrentSlug(slug);
      setTargetUserIdForNewCard(userId);
      setView('admin-editor');
      // Navigate to editor route only if not already there
      if (location.pathname !== `/people/edit/${slug}`) {
        navigate(`/people/edit/${slug}`);
      }
    }
  };

  const handleDelete = async (slug, userId = null) => {
    showConfirm(
      t('editor.deleteCard.confirmMessage', 'Are you sure you want to delete {{slug}}?', { slug }),
      async () => {
        const requestOptions = { method: 'DELETE' };
        if (canManagePeople && userId) {
          requestOptions.body = JSON.stringify({ userId });
        }

        const res = await apiCall(`${API_ENDPOINT}/cards/${slug}`, requestOptions);
        if (res.ok) {
          showAlert(t('editor.deleteCard.success', 'Card deleted successfully'), 'success', '', () => {
            fetchCardList();
          });
        } else {
          showAlert(t('editor.deleteCard.failed', 'Failed to delete card'), 'error');
        }
      },
      t('editor.deleteCard.title', 'Delete Card'),
      t('common.actions.delete', 'Delete'),
      t('common.actions.cancel', 'Cancel')
    );
  };

  const buildAppleWalletUrlForCard = (card) => {
    if (!card || (!card.shortCode && !card.slug)) {
      return '';
    }

    const identifier = card.shortCode || card.slug;
    const orgSlugQuery = !card.shortCode && card.orgSlug
      ? `?orgSlug=${encodeURIComponent(card.orgSlug)}`
      : '';

    return `${API_ENDPOINT}/apple-wallet/${encodeURIComponent(identifier)}${orgSlugQuery}`;
  };

  const handleAddCardToAppleWallet = async (card) => {
    const walletUrl = buildAppleWalletUrlForCard(card);
    const loadingKey = card?.shortCode || card?.slug || '';

    if (!walletUrl || !loadingKey) {
      showAlert(t('wallet.errors.savedCardsOnly', 'Apple Wallet is only available for saved cards.'), 'error', t('wallet.title', 'Apple Wallet'));
      return;
    }

    setAppleWalletLoadingKey(loadingKey);

    try {
      const res = await fetch(`${API_ENDPOINT}/apple-wallet/status`, { credentials: 'include' });
      const status = await res.json().catch(() => ({}));

      if (!status?.configured) {
        const message = Array.isArray(status?.issues) && status.issues.length > 0
          ? status.issues.join('\n')
          : t('wallet.errors.notConfigured', 'Apple Wallet is not configured on this server yet.');
        showAlert(message, 'error', t('wallet.title', 'Apple Wallet'));
        return;
      }

      window.location.assign(walletUrl);
    } catch (error) {
      showAlert(t('wallet.errors.prepareFailed', 'Failed to prepare Apple Wallet pass.'), 'error', t('wallet.title', 'Apple Wallet'));
    } finally {
      setAppleWalletLoadingKey((current) => (current === loadingKey ? '' : current));
    }
  };

  const handleSave = async () => {
    const performSave = async () => {
      setIsSaving(true);
      const startTime = Date.now();
      try {
        // Include userId in request body if creating card for another user
        const body = { ...data };
        const inferredOwnerUserId = canManagePeople && currentSlug
          ? cardList.find(card => card.slug === currentSlug)?.userId
          : null;
        const targetUserId = targetUserIdForNewCard || inferredOwnerUserId;
        if (targetUserId) {
          body.userId = targetUserId;
        }

        const res = await apiCall(`${API_ENDPOINT}/cards/${currentSlug}`, {
          method: 'POST',
          body: JSON.stringify(body)
        });

        // Ensure at least 500ms has passed
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime < 500) {
          await new Promise(resolve => setTimeout(resolve, 500 - elapsedTime));
        }

        if (res.ok) {
          // Clear targetUserIdForNewCard after successful save
          setTargetUserIdForNewCard(null);
          setIsSuccess(true);
          fetchCardList();
          setTimeout(() => setIsSuccess(false), 2000);
        } else {
          showAlert(t('editor.save.failed', 'Save failed'), 'error');
        }
      } finally {
        setIsSaving(false);
      }
    };

    const email = (data.contact?.email || '').trim().toLowerCase();
    if (email && cardList.length > 0) {
      // Only check against actual cards (those with slugs) - this excludes user entries without cards
      // Also exclude the current card being edited
      const duplicates = cardList.filter(c =>
        c.slug && // Must be an actual card
        c.email && // Must have a contact email
        c.email === email && // Must match the email being saved
        c.slug !== currentSlug // Must not be the current card being edited
      );
      if (duplicates.length > 0) {
        showConfirm(
          `Another card already uses this email address (${email}). Would you like to save anyway?`,
          performSave,
          'Email already in use',
          'Save anyway',
          'Cancel'
        );
        return;
      }
    }

    await performSave();
  };

  const groupedPeople = canManagePeople ? groupCardsByUser(cardList) : [];
  const filteredGroupedPeople = groupedPeople.filter((user) => {
    const userCards = user.cards.filter(c => c.slug);
    const firstCardWithName = userCards.find((card) => (card.name || '').trim().length > 0);
    const fullName = (firstCardWithName?.name || '').trim();
    const [firstName = '', ...restName] = fullName.split(/\s+/);
    const lastName = restName.join(' ');

    const firstNameNeedle = ownerFilterFirstName.trim().toLowerCase();
    const lastNameNeedle = ownerFilterLastName.trim().toLowerCase();
    const emailNeedle = ownerFilterEmail.trim().toLowerCase();
    const userEmail = (user.userEmail || '').toLowerCase();

    const firstNameMatches = !firstNameNeedle || firstName.toLowerCase().includes(firstNameNeedle);
    const lastNameMatches = !lastNameNeedle || lastName.toLowerCase().includes(lastNameNeedle);
    const emailMatches = !emailNeedle || userEmail.includes(emailNeedle);

    const roleMatches = ownerFilterRole === 'all' || (user.userRole || 'member') === ownerFilterRole;

    const hasCards = userCards.length > 0;
    const cardsMatches = ownerFilterCards === 'all'
      || (ownerFilterCards === 'with' && hasCards)
      || (ownerFilterCards === 'without' && !hasCards);

    return firstNameMatches && lastNameMatches && emailMatches && roleMatches && cardsMatches;
  });

  const dashboardShellClassName = canManagePeople && ownerPeopleViewMode === 'list'
    ? 'w-full max-w-[calc(100vw-2rem)] md:max-w-[calc(100vw-5rem)]'
    : 'max-w-6xl w-full';

  // Always use Routes for proper URL handling
  // Admin routes must be defined before /:slug to prevent matching
  // All admin views are consolidated here to avoid duplication
  const renderAdminViews = () => {
    // Hard route lock: these views must always render for their URL,
    // independent from async state races in dashboard/auth fetch flows.
    if (normalizedHeaderPath === '/organizations') {
      return (
        <>
          <OrganisationManagementView
            organisations={organisations}
            selectedOrganisationId={selectedOrganisationId}
            onSelectOrganisation={async (organisationId) => {
              setSelectedOrganisationId(organisationId);
              localStorage.setItem('selectedOrganisationId', organisationId);
              await saveActiveOrganisationPreference(organisationId);
            }}
            onCreateOrganisation={handleCreateOrganisation}
            onDeleteOrganisation={handleDeleteOrganisation}
            onRefresh={refreshOrganisations}
            onOpenUsers={() => navigate('/users')}
            onOpenSettings={() => navigate('/settings')}
            onBack={() => navigate('/people')}
            showAlert={showAlert}
          />
        </>
      );
    }

    if (normalizedHeaderPath === '/departments') {
      return (
        <>
          <DepartmentManagementView
            apiCall={apiCall}
            userRole={userRole}
            selectedOrganisationId={selectedOrganisationId}
            onBack={() => navigate('/people')}
            showAlert={showAlert}
            showConfirm={showConfirm}
          />
        </>
      );
    }

    return (
      <>
      {activeView === 'loading' && (
        <div className="h-screen flex items-center justify-center text-text-muted-subtle dark:text-text-muted-dark bg-main dark:bg-main-dark bg-main-texture">
          {t('common.states.loading', 'Loading...')}
        </div>
      )}
      {activeView === '404' && (
        <div className="h-screen flex flex-col items-center justify-center bg-main dark:bg-main-dark bg-main-texture">
          <h1 className="text-4xl font-bold text-text-primary dark:text-text-primary-dark mb-2">{t('errors.notFound.code', '404')}</h1>
          <p className="text-text-muted dark:text-text-muted-dark">{t('errors.cardNotFound', 'Card not found.')}</p>
        </div>
      )}
      {activeView === 'setup-wizard' && (
        <>
          <div className="min-h-screen bg-surface dark:bg-main-dark flex items-center justify-center p-4">
            <div className="bg-card dark:bg-card-dark max-w-md w-full rounded-page shadow-xl p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4 text-indigo-600 dark:text-indigo-400"><Settings className="w-8 h-8" /></div>
                <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark">{t('auth.setup.title', 'Initial Setup')}</h1>
                <p className="text-sm text-text-muted dark:text-text-muted-dark mt-2">{t('auth.setup.subtitle', 'Configure your organisation and create the first admin user')}</p>
              </div>
              <form onSubmit={handleSetup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2">{t('auth.setup.fields.organisationName', 'Organisation Name')}</label>
                  <input
                    type="text"
                    value={setupData.organisationName}
                    onChange={e => setSetupData({ ...setupData, organisationName: e.target.value })}
                    placeholder={t('auth.setup.placeholders.organisationName', 'My Organization')}
                    className="w-full px-5 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2">{t('auth.setup.fields.adminEmail', 'Admin Email')}</label>
                  <input
                    type="email"
                    value={setupData.adminEmail}
                    onChange={e => setSetupData({ ...setupData, adminEmail: e.target.value })}
                    placeholder={t('auth.setup.placeholders.adminEmail', 'admin@example.com')}
                    className="w-full px-5 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2">{t('auth.setup.fields.adminPassword', 'Admin Password')}</label>
                  <input
                    type="password"
                    value={setupData.adminPassword}
                    onChange={e => setSetupData({ ...setupData, adminPassword: e.target.value })}
                    placeholder={t('auth.setup.placeholders.adminPassword', 'Minimum 8 characters')}
                    className="w-full px-5 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
                    required
                    minLength={8}
                  />
                  <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">{t('auth.setup.passwordHint', 'Password must be at least 8 characters long')}</p>
                </div>
                {error && <div className="flex items-center gap-2 text-error-text dark:text-error-text-dark text-sm">{error}</div>}
                <button
                  type="submit"
                  disabled={isSettingUp}
                  className="w-full py-3.5 rounded-full bg-action dark:bg-action-dark text-white font-bold hover:bg-action-hover dark:hover:bg-action-hover-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSettingUp ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : isSuccessSetup ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {isSettingUp ? t('auth.setup.states.settingUp', 'Setting up...') : t('auth.setup.actions.complete', 'Complete Setup')}
                </button>
              </form>
            </div>
          </div>
        </>
      )}
      {activeView === 'admin-login' && (
        <>
          <div className="min-h-screen bg-surface dark:bg-main-dark flex items-center justify-center p-4">
            <div className="bg-card dark:bg-card-dark max-w-sm w-full rounded-page shadow-xl p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4 text-indigo-600 dark:text-indigo-400"><Lock className="w-8 h-8" /></div>
                <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark">{t('auth.login.title', 'Login')}</h1>
              </div>
              <form onSubmit={handleLogin} className="space-y-4">
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('auth.login.placeholders.email', 'Email')} className="w-full px-5 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark" autoFocus />
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t('auth.login.placeholders.password', 'Password')} className="w-full px-5 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark" />
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowForgotPasswordModal(true)}
                    className="text-xs text-action dark:text-action-dark hover:underline"
                  >
                    {t('auth.login.forgotPassword', 'Forgot Password?')}
                  </button>
                </div>
                {error && <div className="flex items-center gap-2 text-error-text dark:text-error-text-dark text-sm">{error}</div>}
                <button type="submit" className="w-full py-3.5 rounded-full bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark font-bold hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark transition-colors">{t('common.actions.login', 'Login')}</button>
              </form>
            </div>
          </div>
        </>
      )}
      {
        activeView === 'admin-dashboard' && (
          <>
            <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture p-6 md:p-12 flex flex-col">
              <div className={`${dashboardShellClassName} mx-auto flex-1`}>
                <div className="mb-8 relative z-10 space-y-6">
                  <div className="sticky top-3 md:top-4 z-30">
                    <div className="rounded-container border border-border dark:border-border-dark bg-card/95 dark:bg-card-dark/95 px-3 py-3 md:px-4 md:py-3 backdrop-blur-md shadow-[0_8px_24px_rgba(15,23,42,0.18)]">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-2 md:gap-3">
                          {canShowTeamNavButtons && (
                            <button onClick={() => window.location.assign('/users')} className={isHeaderUsersActive ? headerPillActive : headerPillDefault}>
                              <Users className="w-5 h-5" /> {t('common.nav.users', 'Users')}
                            </button>
                          )}
                          {canShowTeamNavButtons && (
                            <button onClick={() => window.location.assign('/departments')} className={isHeaderDepartmentsActive ? headerPillActive : headerPillDefault}>
                              <FileText className="w-5 h-5" /> {t('common.nav.departments', 'Departments')}
                            </button>
                          )}
                          {canShowTeamNavButtons && canShowOrgNavButtons && (
                            <div className="hidden md:block h-8 w-px bg-border dark:bg-border-dark mx-1" />
                          )}
                          {userRole === 'admin' && (
                            <button onClick={() => window.location.assign('/organizations')} className={isHeaderOrganisationsActive ? headerPillActive : headerPillDefault}>
                              <Briefcase className="w-5 h-5" /> {t('common.nav.organisations', 'Organisations')}
                            </button>
                          )}
                          {isOrgAdmin && (
                            <button onClick={handleOrganisationSettingsClick} className={isHeaderOrgSettingsActive ? headerPillActive : headerPillDefault}>
                              <Settings className="w-5 h-5" /> <span className="hidden sm:inline">{t('common.actions.orgSettings', 'Org-Settings')}</span><span className="sm:hidden">{t('common.fields.settings', 'Settings')}</span>
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 md:gap-3">
                          <button onClick={handleOpenProfile} className={headerPillDefault}>
                            <User className="w-5 h-5" /> {t('common.nav.profile', 'Profile')}
                          </button>
                          <button onClick={toggleDarkMode} className={headerIconPill}>
                            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                          </button>
                          <button onClick={handleLogout} className={headerPillDanger}>{t('common.actions.logout', 'Logout')}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                      <h1 className="text-4xl font-bold text-text-primary dark:text-text-primary-dark">{canManagePeople ? t('dashboard.people.title', 'People') : t('dashboard.cards.title', 'My Cards')}</h1>
                      <p className="text-2xl text-text-muted dark:text-text-muted-dark">{canManagePeople ? t('dashboard.people.subtitle', 'Manage your team members and their cards') : t('dashboard.cards.subtitle', 'Manage your cards')}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {canManagePeople && (
                        <div className="hidden md:flex items-center rounded-full border border-border dark:border-border-dark overflow-hidden">
                          <button
                            onClick={() => setOwnerPeopleViewMode('cards')}
                            className={`px-3 py-2 text-xs md:text-sm ${ownerPeopleViewMode === 'cards' ? 'bg-action dark:bg-action-dark text-white' : 'bg-card dark:bg-card-dark text-text-secondary dark:text-text-secondary-dark'}`}
                          >
                            {t('dashboard.view.cards', 'Cards')}
                          </button>
                          <button
                            onClick={() => setOwnerPeopleViewMode('list')}
                            className={`px-3 py-2 text-xs md:text-sm ${ownerPeopleViewMode === 'list' ? 'bg-action dark:bg-action-dark text-white' : 'bg-card dark:bg-card-dark text-text-secondary dark:text-text-secondary-dark'}`}
                          >
                            {t('dashboard.view.list', 'List')}
                          </button>
                        </div>
                      )}
                      <button onClick={handleCreateNew} className="bg-action dark:bg-action-dark text-white px-6 py-3 rounded-full font-bold flex items-center gap-2 hover:bg-action-hover dark:hover:bg-action-hover-dark transition-all whitespace-nowrap text-base">
                        <Plus className="w-5 h-5" /> {canManagePeople ? t('dashboard.actions.newPerson', 'New Person') : t('dashboard.actions.newCard', 'New Card')}
                      </button>
                    </div>
                  </div>
                </div>
                {canManagePeople && (
                  <div className="mb-8 bg-card dark:bg-card-dark rounded-card border border-border dark:border-border-dark p-4 md:p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                      <input
                        type="text"
                        placeholder={t('dashboard.filters.firstName', 'First Name...')}
                        value={ownerFilterFirstName}
                        onChange={(e) => setOwnerFilterFirstName(e.target.value)}
                        className="px-3 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                      />
                      <input
                        type="text"
                        placeholder={t('dashboard.filters.lastName', 'Last Name...')}
                        value={ownerFilterLastName}
                        onChange={(e) => setOwnerFilterLastName(e.target.value)}
                        className="px-3 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                      />
                      <input
                        type="text"
                        placeholder={t('dashboard.filters.email', 'Email...')}
                        value={ownerFilterEmail}
                        onChange={(e) => setOwnerFilterEmail(e.target.value)}
                        className="px-3 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                      />
                      <select
                        value={ownerFilterRole}
                        onChange={(e) => setOwnerFilterRole(e.target.value)}
                        className="px-3 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                      >
                        <option value="all">{t('common.roles.all', 'All Roles')}</option>
                        <option value="admin">{t('common.roles.admin', 'Admin')}</option>
                        <option value="department_lead">{t('common.roles.departmentLead', 'Department Lead')}</option>
                        <option value="owner">{t('common.roles.owner', 'Owner')}</option>
                        <option value="member">{t('common.roles.member', 'Member')}</option>
                      </select>
                      <select
                        value={ownerFilterCards}
                        onChange={(e) => setOwnerFilterCards(e.target.value)}
                        className="px-3 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                      >
                        <option value="all">{t('common.fields.cardStatus', 'Card Status')}</option>
                        <option value="with">{t('common.cardStatus.with', 'With Cards')}</option>
                        <option value="without">{t('common.cardStatus.without', 'Without Cards')}</option>
                      </select>
                    </div>
                    <div className="mt-3 text-xs text-text-muted dark:text-text-muted-dark">
                      {t('dashboard.filters.hits', 'Hits: {{count}} of {{total}}', {
                        count: filteredGroupedPeople.length,
                        total: groupedPeople.length
                      })}
                    </div>
                  </div>
                )}
                {canManagePeople && ownerPeopleViewMode === 'list' ? (
                  <div className="bg-card dark:bg-card-dark rounded-input border border-border dark:border-border-dark overflow-hidden">
                    <div className="lg:hidden divide-y divide-border dark:divide-border-dark">
                      {filteredGroupedPeople.map((user) => {
                        const userCards = user.cards.filter(c => c.slug);
                        const firstCardWithName = userCards.find((card) => (card.name || '').trim().length > 0);
                        const fullName = (firstCardWithName?.name || '').trim();
                        const [firstName = '—', ...restName] = fullName ? fullName.split(/\s+/) : ['—'];
                        const lastName = restName.length > 0 ? restName.join(' ') : '—';
                        const isCurrentUser = user.userId === currentUserId;

                        return (
                          <div key={user.userId || user.userEmail} className="p-4 space-y-3">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="font-semibold text-text-primary dark:text-text-primary-dark">{firstName} {lastName}</div>
                                <div className="text-sm text-text-secondary dark:text-text-secondary-dark break-all">{user.userEmail || '—'}</div>
                              </div>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium ${user.userRole === 'owner'
                                ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                                : 'bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark'
                                }`}>
                                {user.userRole === 'owner'
                                  ? t('common.roles.owner', 'Owner')
                                  : user.userRole === 'department_lead'
                                    ? t('common.roles.departmentLead', 'Department Lead')
                                    : user.userRole === 'admin'
                                      ? t('common.roles.admin', 'Admin')
                                      : t('common.roles.member', 'Member')}
                              </span>
                            </div>

                            <div className="space-y-2">
                              <div className="text-xs text-text-muted dark:text-text-muted-dark">
                                {t('common.labels.cardsCount', 'Cards ({{count}})', { count: userCards.length })}
                              </div>
                              {userCards.length === 0 ? (
                                <div className="text-xs text-text-muted dark:text-text-muted-dark">
                                  {t('common.empty.noCards', 'No cards')}
                                </div>
                              ) : (
                                userCards.map((card) => (
                                  <div key={card.slug} className="p-2 rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark/30">
                                    <div className="font-mono text-xs text-text-secondary dark:text-text-secondary-dark break-all mb-2">
                                      {card.shortCode ? `/${card.shortCode}` : (card.orgSlug ? `/${card.orgSlug}/${card.slug}` : `/${card.slug}`)}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <a
                                        href={card.shortCode ? `/${card.shortCode}` : (card.orgSlug && card.slug ? `/${card.orgSlug}/${card.slug}` : `/${card.slug}`)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="px-2 py-1 text-xs bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button"
                                      >
                                        {t('common.actions.view', 'View')}
                                      </a>
                                      <button
                                        onClick={() => handleEdit(card.slug, user.userId || null)}
                                        className="px-2 py-1 text-xs bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button border border-border dark:border-border-dark"
                                      >
                                        {t('common.actions.edit', 'Edit')}
                                      </button>
                                      <button
                                        onClick={() => handleDelete(card.slug, user.userId || null)}
                                        className="px-2 py-1 text-xs bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark rounded-badge"
                                      >
                                        {t('common.actions.delete', 'Delete')}
                                      </button>
                                      <button
                                        onClick={() => handleAddCardToAppleWallet(card)}
                                        disabled={appleWalletLoadingKey === (card.shortCode || card.slug)}
                                        className="px-2 py-1 text-xs bg-card dark:bg-card-dark text-text-primary dark:text-text-secondary-dark rounded-button border border-border dark:border-border-dark disabled:opacity-60 flex items-center gap-1"
                                      >
                                        {appleWalletLoadingKey === (card.shortCode || card.slug)
                                          ? <RefreshCw className="w-3 h-3 animate-spin" />
                                          : <Download className="w-3 h-3" />}
                                        {t('wallet.actions.addToAppleWallet', 'Add to Wallet')}
                                      </button>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>

                            <div className="flex flex-wrap gap-2 pt-1">
                              {!isCurrentUser && editingUserId === user.userId ? (
                                <>
                                  <select
                                    value={user.userRole}
                                    onChange={(e) => handleUpdateRole(user.userId, e.target.value)}
                                    className="px-2 py-1 text-xs rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                                  >
                                    <option value="member">{t('common.roles.member', 'Member')}</option>
                                    <option value="department_lead">{t('common.roles.departmentLead', 'Department Lead')}</option>
                                    <option value="admin">{t('common.roles.admin', 'Admin')}</option>
                                    <option value="owner">{t('common.roles.owner', 'Owner')}</option>
                                  </select>
                                  <button
                                    onClick={() => setEditingUserId(null)}
                                    className="px-2 py-1 text-xs bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button border border-border dark:border-border-dark"
                                  >
                                    {t('common.actions.cancel', 'Cancel')}
                                  </button>
                                </>
                              ) : !isCurrentUser && (
                                <>
                                  <button
                                    onClick={() => setEditingUserId(user.userId)}
                                    className="px-2 py-1 text-xs bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button border border-border dark:border-border-dark"
                                  >
                                    {t('common.actions.role', 'Role')}
                                  </button>
                                  <button
                                    onClick={() => handleRemoveUser(user.userId, user.userEmail)}
                                    className="px-2 py-1 text-xs bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark rounded-badge"
                                  >
                                    {t('common.actions.remove', 'Remove')}
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => setCreateCardModal({ isOpen: true, slug: '', userId: user.userId || user.userEmail })}
                                className="px-2 py-1 text-xs bg-action dark:bg-action-dark text-white rounded-button"
                              >
                                {t('common.actions.createCard', 'Create Card')}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="w-full min-w-[1220px] text-sm">
                        <thead className="bg-surface dark:bg-surface-dark/60 border-b border-border dark:border-border-dark">
                          <tr>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark">{t('dashboard.table.firstName', 'First Name')}</th>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark">{t('dashboard.table.lastName', 'Last Name')}</th>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark">{t('dashboard.table.email', 'Email')}</th>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark">{t('dashboard.table.role', 'Role')}</th>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark">{t('dashboard.table.cards', 'Cards')}</th>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark">{t('dashboard.table.cardIds', 'Card IDs')}</th>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark">{t('dashboard.table.actions', 'Actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredGroupedPeople.map((user) => {
                            const userCards = user.cards.filter(c => c.slug);
                            const firstCardWithName = userCards.find((card) => (card.name || '').trim().length > 0);
                            const fullName = (firstCardWithName?.name || '').trim();
                            const [firstName = '—', ...restName] = fullName ? fullName.split(/\s+/) : ['—'];
                            const lastName = restName.length > 0 ? restName.join(' ') : '—';
                            return (
                              <tr key={user.userId || user.userEmail} className="border-b border-border dark:border-border-dark">
                                <td className="px-4 py-3 text-text-secondary dark:text-text-secondary-dark">{firstName}</td>
                                <td className="px-4 py-3 text-text-secondary dark:text-text-secondary-dark">{lastName}</td>
                                <td className="px-4 py-3 font-medium text-text-primary dark:text-text-primary-dark">{user.userEmail || '—'}</td>
                                <td className="px-4 py-3 text-text-secondary dark:text-text-secondary-dark">
                                  {user.userRole === 'owner'
                                    ? t('common.roles.owner', 'Owner')
                                    : user.userRole === 'department_lead'
                                      ? t('common.roles.departmentLead', 'Department Lead')
                                      : user.userRole === 'admin'
                                        ? t('common.roles.admin', 'Admin')
                                        : t('common.roles.member', 'Member')}
                                </td>
                                <td className="px-4 py-3 text-text-secondary dark:text-text-secondary-dark">{userCards.length}</td>
                                <td className="px-4 py-3 text-xs text-text-muted dark:text-text-muted-dark">
                                  {userCards.length === 0 ? (
                                    <span className="font-mono">—</span>
                                  ) : (
                                    <div className="space-y-2">
                                      {userCards.map((card) => (
                                        <div key={card.slug} className="flex items-center gap-2">
                                          <span className="font-mono">{card.shortCode || card.slug}</span>
                                          <a
                                            href={card.shortCode ? `/${card.shortCode}` : (card.orgSlug && card.slug ? `/${card.orgSlug}/${card.slug}` : `/${card.slug}`)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="px-2 py-1 rounded-button text-[11px] bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark"
                                          >
                                            {t('common.actions.view', 'View')}
                                          </a>
                                          <button
                                            onClick={() => handleEdit(card.slug, user.userId || null)}
                                            className="px-2 py-1 rounded-button text-[11px] bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark border border-border dark:border-border-dark"
                                          >
                                            {t('common.actions.edit', 'Edit')}
                                          </button>
                                          <button
                                            onClick={() => handleDelete(card.slug, user.userId || null)}
                                            className="px-2 py-1 rounded-badge text-[11px] bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark"
                                          >
                                            {t('common.actions.delete', 'Delete')}
                                          </button>
                                          <button
                                            onClick={() => handleAddCardToAppleWallet(card)}
                                            disabled={appleWalletLoadingKey === (card.shortCode || card.slug)}
                                            className="px-2 py-1 rounded-button text-[11px] bg-card dark:bg-card-dark text-text-primary dark:text-text-secondary-dark border border-border dark:border-border-dark disabled:opacity-60 flex items-center gap-1"
                                          >
                                            {appleWalletLoadingKey === (card.shortCode || card.slug)
                                              ? <RefreshCw className="w-3 h-3 animate-spin" />
                                              : <Download className="w-3 h-3" />}
                                            {t('wallet.actions.addToAppleWallet', 'Add to Wallet')}
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="flex flex-wrap items-center gap-2">
                                    {user.userId !== currentUserId ? (
                                      <>
                                        {editingUserId === user.userId ? (
                                          <>
                                            <select
                                              value={user.userRole}
                                              onChange={(e) => handleUpdateRole(user.userId, e.target.value)}
                                              className="px-2 py-1 text-xs rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                                            >
                                              <option value="member">{t('common.roles.member', 'Member')}</option>
                                    <option value="department_lead">{t('common.roles.departmentLead', 'Department Lead')}</option>
                                    <option value="admin">{t('common.roles.admin', 'Admin')}</option>
                                    <option value="owner">{t('common.roles.owner', 'Owner')}</option>
                                            </select>
                                            <button
                                              onClick={() => setEditingUserId(null)}
                                              className="px-2 py-1 text-xs rounded-button bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark border border-border dark:border-border-dark"
                                            >
                                              {t('common.actions.cancel', 'Cancel')}
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <button
                                              onClick={() => setEditingUserId(user.userId)}
                                              className="px-2 py-1 text-xs rounded-button bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark border border-border dark:border-border-dark"
                                            >
                                            {t('common.actions.role', 'Role')}
                                            </button>
                                            <button
                                              onClick={() => handleRemoveUser(user.userId, user.userEmail)}
                                              className="px-2 py-1 text-xs rounded-badge bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark"
                                            >
                                            {t('common.actions.remove', 'Remove')}
                                            </button>
                                          </>
                                        )}
                                      </>
                                    ) : (
                                      <span className="text-[11px] italic text-text-muted dark:text-text-muted-dark">{t('common.messages.cannotModifyYourself', 'Cannot modify yourself')}</span>
                                    )}
                                    <button
                                      onClick={() => setCreateCardModal({ isOpen: true, slug: '', userId: user.userId || user.userEmail })}
                                      className="px-2 py-1 text-xs rounded-button bg-action dark:bg-action-dark text-white"
                                    >
                                      Create Card
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                  {(canManagePeople ? filteredGroupedPeople : groupCardsByUser(cardList)).map(user => {
                    // Filter out entries without slugs (these represent users without cards)
                    const userCards = user.cards.filter(c => c.slug);
                    const userKey = user.userId || user.userEmail || 'unknown';
                    const firstCardWithName = userCards.find((card) => (card.name || '').trim().length > 0);
                    const displayName = (firstCardWithName?.name || user.userEmail || 'User').trim();
                    const nameParts = displayName.split(/\s+/).filter(Boolean);
                    const initials = (nameParts.slice(0, 2).map((part) => part[0]).join('') || (user.userEmail || 'U').slice(0, 2)).toUpperCase();
                    const isCurrentUser = user.userId === currentUserId;
                    const roleLabel = user.userRole === 'owner'
                      ? t('common.roles.owner', 'Owner')
                      : user.userRole === 'department_lead'
                        ? t('common.roles.departmentLead', 'Department Lead')
                        : user.userRole === 'admin'
                          ? t('common.roles.admin', 'Admin')
                          : t('common.roles.member', 'Member');

                    return (
                      <div key={userKey} className="bg-card dark:bg-card-dark rounded-card border border-border dark:border-border-dark p-4 md:p-5">
                        {canManagePeople && user.userEmail && (
                          <div className="mb-4">
                            <div className="flex items-center justify-between gap-3 mb-3">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-badge text-xs font-semibold bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark">{roleLabel}</span>
                              {user.userCreatedAt && (
                                <span className="text-xs text-text-muted dark:text-text-muted-dark">
                                  {new Date(user.userCreatedAt).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-12 h-12 rounded-xl bg-surface dark:bg-surface-dark border border-border dark:border-border-dark flex items-center justify-center font-bold text-action dark:text-action-dark">
                                {initials}
                              </div>
                              <div className="min-w-0">
                                <div className="font-bold text-xl text-text-primary dark:text-text-primary-dark truncate">{displayName}</div>
                                <div className="text-base text-text-muted dark:text-text-muted-dark truncate">{user.userEmail}</div>
                              </div>
                            </div>
                            {user.userId && user.userId !== currentUserId && (
                              <div className="grid grid-cols-2 gap-2">
                                {editingUserId === user.userId ? (
                                  <>
                                    <select
                                      value={user.userRole}
                                      onChange={(e) => handleUpdateRole(user.userId, e.target.value)}
                                      className="col-span-2 px-2 py-2 text-sm rounded-button border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                                    >
                                      <option value="member">{t('common.roles.member', 'Member')}</option>
                                      <option value="department_lead">{t('common.roles.departmentLead', 'Department Lead')}</option>
                                      <option value="admin">{t('common.roles.admin', 'Admin')}</option>
                                      <option value="owner">{t('common.roles.owner', 'Owner')}</option>
                                    </select>
                                    <button
                                      onClick={() => setEditingUserId(null)}
                                      className="col-span-2 px-3 py-2 text-sm rounded-button bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark border border-border dark:border-border-dark"
                                    >
                                      {t('common.actions.cancel', 'Cancel')}
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => setEditingUserId(user.userId)}
                                      className="px-3 py-2 text-sm rounded-button bg-card dark:bg-card-dark text-text-primary dark:text-text-secondary-dark border border-border dark:border-border-dark"
                                    >
                                      {t('common.actions.role', 'Role')}
                                    </button>
                                    <button
                                      onClick={() => handleRemoveUser(user.userId, user.userEmail)}
                                      className="px-3 py-2 text-sm rounded-button bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark"
                                    >
                                      {t('common.actions.remove', 'Remove')}
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                            {isCurrentUser && (
                              <div className="text-xs text-text-muted dark:text-text-muted-dark italic">{t('common.messages.cannotModifyYourself', 'Cannot modify yourself')}</div>
                            )}
                          </div>
                        )}

                        <div className="space-y-3">
                          {userCards.length > 0 ? (
                            <>
                              {userCards.map(card => (
                                <div key={card.slug} className="bg-surface dark:bg-surface-dark/50 rounded-input p-4 border border-border dark:border-border-dark">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <h3 className="font-bold text-xl text-text-primary dark:text-text-primary-dark truncate">{card.name || t('common.fields.card', 'Card')}</h3>
                                      <div className="text-sm text-text-muted dark:text-text-muted-dark font-mono truncate">
                                        {`${t('common.labels.short', 'Short')}: ${card.shortCode ? `/${card.shortCode}` : '-'}`}
                                      </div>
                                      <div className="text-sm text-text-muted dark:text-text-muted-dark font-mono truncate">
                                        {`${t('common.labels.url', 'URL')}: ${card.orgSlug && card.slug
                                          ? `/${card.orgSlug}/${card.slug}`
                                          : card.slug
                                            ? `/${card.slug}`
                                            : '-'}`}
                                      </div>
                                    </div>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleDelete(card.slug, user.userId || null); }}
                                      className="p-2 text-text-muted-subtle dark:text-text-muted-dark hover:text-error-text dark:hover:text-error-text-dark rounded-full transition-colors"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 mt-3">
                                    <a
                                      href={card.shortCode ? `/${card.shortCode}` : (card.orgSlug && card.slug ? `/${card.orgSlug}/${card.slug}` : `/${card.slug}`)}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="py-2 text-sm font-medium text-text-primary dark:text-text-secondary-dark bg-card dark:bg-card-dark rounded-button border border-border dark:border-border-dark flex items-center justify-center"
                                    >
                                      {t('common.actions.view', 'View')}
                                    </a>
                                    <button onClick={() => handleEdit(card.slug, user.userId || null)} className="py-2 text-sm font-medium text-text-primary dark:text-text-secondary-dark bg-card dark:bg-card-dark rounded-button border border-border dark:border-border-dark flex items-center justify-center">{t('common.actions.edit', 'Edit')}</button>
                                    <button
                                      onClick={() => handleAddCardToAppleWallet(card)}
                                      disabled={appleWalletLoadingKey === (card.shortCode || card.slug)}
                                      className="col-span-2 py-2 text-sm font-medium text-text-primary dark:text-text-secondary-dark bg-card dark:bg-card-dark rounded-button border border-border dark:border-border-dark flex items-center justify-center gap-2 disabled:opacity-60"
                                    >
                                      {appleWalletLoadingKey === (card.shortCode || card.slug)
                                        ? <RefreshCw className="w-4 h-4 animate-spin" />
                                        : <Download className="w-4 h-4" />}
                                      {t('wallet.actions.addToAppleWallet', 'Add to Wallet')}
                                    </button>
                                  </div>
                                </div>
                              ))}
                              <button onClick={() => setCreateCardModal({ isOpen: true, slug: '', userId: user.userId || user.userEmail })} className="w-full px-4 py-3 text-sm font-semibold text-text-muted dark:text-text-muted-dark border-2 border-dashed border-border dark:border-border-dark rounded-input hover:bg-surface dark:hover:bg-surface-dark transition-colors">{t('common.actions.add', '+')} {t('common.actions.createCard', 'Create Card')}</button>
                            </>
                          ) : (
                            <button onClick={() => setCreateCardModal({ isOpen: true, slug: '', userId: user.userId || user.userEmail })} className="w-full px-4 py-6 text-sm font-semibold text-text-muted dark:text-text-muted-dark border-2 border-dashed border-border dark:border-border-dark rounded-input hover:bg-surface dark:hover:bg-surface-dark transition-colors">{t('common.actions.add', '+')} {t('common.actions.createCard', 'Create Card')}</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {(canManagePeople ? filteredGroupedPeople.length === 0 : cardList.length === 0) && (
                    <div className="col-span-full py-20 text-center text-text-muted-subtle dark:text-text-muted-dark bg-card dark:bg-card-dark rounded-card border-thick border-dashed border-border dark:border-border-dark">
                      {canManagePeople
                        ? t('dashboard.empty.noMatchingPeople', 'No matching people found for your filters.')
                        : t('dashboard.empty.noCardsYet', 'No cards yet. Click "New Card" to create your first card.')}
                    </div>
                  )}
                </div>
                )}
              </div>
            </div>
            <div className="fixed bottom-4 right-4 z-10 text-center group">
              <div className="flex justify-center">
                <img src="/graphics/VCARD_Brand_Logo.svg" alt={t('common.alt.brandLogo', 'VCARD logo')} className="h-8 md:h-10 w-auto dark:hidden brand-logo" />
                <img src="/graphics/VCARD_Brand_Logo_DarkBg.svg" alt={t('common.alt.brandLogo', 'VCARD logo')} className="h-8 md:h-10 w-auto hidden dark:block brand-logo" />
              </div>
            </div>
            {/* Modals */}
            {actionSelectionModal.isOpen && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
                  <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">{t('dashboard.modals.actionSelection.title', 'What would you like to do?')}</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        setActionSelectionModal({ isOpen: false });
                        setShowInviteModal(true);
                      }}
                      className="w-full px-4 py-3 bg-action dark:bg-action-dark text-white rounded-button font-medium hover:bg-action-hover dark:hover:bg-action-hover-dark flex items-center justify-center gap-2"
                    >
                      <Users className="w-4 h-4" /> {t('dashboard.modals.actionSelection.invite', 'Invite User')}
                    </button>
                    <button
                      onClick={() => {
                        setActionSelectionModal({ isOpen: false });
                        setShowCreateUserModal(true);
                      }}
                      className="w-full px-4 py-3 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button font-medium hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark flex items-center justify-center gap-2"
                    >
                      <User className="w-4 h-4" /> {t('dashboard.modals.actionSelection.create', 'Create User')}
                    </button>
                  </div>
                  <button
                    onClick={() => setActionSelectionModal({ isOpen: false })}
                    className="w-full mt-4 px-4 py-2 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
                  >
                    {t('common.actions.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            )}
            {/* Invite User Modal */}
            {showInviteModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
                  <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">{t('dashboard.modals.invite.title', 'Invite User')}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.email', 'Email')}</label>
                      <input
                        type="email"
                        value={newInvitation.email}
                        onChange={(e) => setNewInvitation({ ...newInvitation, email: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                        placeholder={t('users.placeholders.email', 'user@example.com')}
                      />
                      <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">{t('dashboard.modals.invite.emailHint', 'An invitation email will be sent to this address')}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.role', 'Role')}</label>
                      <select
                        value={newInvitation.role}
                        onChange={(e) => setNewInvitation({ ...newInvitation, role: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                      >
                        <option value="member">{t('common.roles.member', 'Member')}</option>
                                    <option value="department_lead">{t('common.roles.departmentLead', 'Department Lead')}</option>
                                    <option value="admin">{t('common.roles.admin', 'Admin')}</option>
                                    <option value="owner">{t('common.roles.owner', 'Owner')}</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => {
                        setShowInviteModal(false);
                        setNewInvitation({ email: '', role: 'member' });
                      }}
                      className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendInvitation}
                      disabled={isSavingUser}
                      className="flex-1 px-4 py-2.5 bg-action dark:bg-action-dark text-white rounded-button font-bold hover:bg-action-hover dark:hover:bg-action-hover-dark disabled:opacity-50"
                    >
                      {isSavingUser ? t('dashboard.modals.invite.states.sending', 'Sending...') : t('common.actions.sendInvitation', 'Send Invitation')}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* Create User Modal */}
            {showCreateUserModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
                  <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">{t('dashboard.modals.createUser.title', 'Create New User')}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.email', 'Email')}</label>
                      <input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                        placeholder={t('users.placeholders.email', 'user@example.com')}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.password', 'Password')}</label>
                      <input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                        placeholder={t('users.placeholders.password', 'Minimum 8 characters')}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.role', 'Role')}</label>
                      <select
                        value={newUser.role}
                        onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                      >
                        <option value="member">{t('common.roles.member', 'Member')}</option>
                                    <option value="department_lead">{t('common.roles.departmentLead', 'Department Lead')}</option>
                                    <option value="admin">{t('common.roles.admin', 'Admin')}</option>
                                    <option value="owner">{t('common.roles.owner', 'Owner')}</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => {
                        setShowCreateUserModal(false);
                        setNewUser({ email: '', password: '', role: 'member', departmentId: '' });
                      }}
                      className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateUser}
                      disabled={isSavingUser}
                      className="flex-1 px-4 py-2.5 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button font-bold hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark disabled:opacity-50"
                    >
                      {isSavingUser ? t('dashboard.modals.createUser.states.creating', 'Creating...') : t('common.actions.createUser', 'Create User')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      {activeView === 'member-empty' && (
        <>
          <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex items-center justify-center p-6">
            <div className="bg-card dark:bg-card-dark rounded-card shadow-lg max-w-md w-full p-8 text-center">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h2 className="text-xl font-bold text-text-primary dark:text-text-primary-dark mb-2">{t('dashboard.memberEmpty.title', "You don't have a card yet")}</h2>
              <p className="text-text-secondary dark:text-text-muted-dark mb-6">{t('dashboard.memberEmpty.subtitle', 'Create your first card to get started.')}</p>
              <button
                onClick={() => setCreateCardModal({ isOpen: true, slug: '' })}
                className="w-full px-4 py-3 bg-action dark:bg-action-dark text-white rounded-button font-bold hover:bg-action-hover dark:hover:bg-action-hover-dark flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" /> {t('common.actions.createCard', 'Create Card')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full mt-3 px-4 py-2 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
              >
                Logout
              </button>
            </div>
          </div>
        </>
      )}
      {activeView === 'admin-editor' && (
        <>
          <EditorView
            data={data}
            setData={setData}
            onBack={() => navigate('/people')}
            onSave={handleSave}
            slug={currentSlug}
            settings={settings}
            csrfToken={csrfToken}
            showAlert={showAlert}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            isSaving={isSaving}
            isSuccess={isSuccess}
          />
        </>
      )}
      {activeView === 'admin-settings' && (
        <>
          <SettingsView
            settings={settings}
            setSettings={setSettings}
            apiCall={apiCall}
            userRole={userRole}
            selectedOrganisationId={selectedOrganisationId}
            onBack={() => navigate('/people')}
            onSave={async (organisationIdOverride = null) => {
              await fetchSettings(organisationIdOverride);
              fetchCardList();
            }}
            showAlert={showAlert}
            showConfirm={showConfirm}
          />
        </>
      )}
      {activeView === 'user-management' && (
        <>
          <UserManagementView
            apiCall={apiCall}
            userRole={userRole}
            userLanguage={userLanguage}
            organisations={organisations}
            selectedOrganisationId={selectedOrganisationId}
            onSelectOrganisation={async (organisationId) => {
              setSelectedOrganisationId(organisationId);
              localStorage.setItem('selectedOrganisationId', organisationId);
              await saveActiveOrganisationPreference(organisationId);
            }}
            onBack={() => navigate('/people')}
            showAlert={showAlert}
            showConfirm={showConfirm}
            setChangePasswordModal={setChangePasswordModal}
          />
        </>
      )}
      {activeView === 'profile-management' && (
        <>
          <ProfileManagementView
            currentUserEmail={currentUserEmail}
            userRole={userRole}
            settings={settings}
            userLanguage={userLanguage}
            onSaveLanguage={saveUserLanguage}
            onBack={() => navigate('/people')}
            showAlert={showAlert}
            onOpenChangePassword={() => setChangePasswordModal({ isOpen: true, userId: null, isOwnerChangingUser: false })}
            onOpenForgotPassword={() => setShowForgotPasswordModal(true)}
          />
        </>
      )}
      {activeView === 'department-management' && (
        <>
          <DepartmentManagementView
            apiCall={apiCall}
            userRole={userRole}
            selectedOrganisationId={selectedOrganisationId}
            onBack={() => navigate('/people')}
            showAlert={showAlert}
            showConfirm={showConfirm}
          />
        </>
      )}
      {activeView === 'organisation-management' && (
        <>
          <OrganisationManagementView
            organisations={organisations}
            selectedOrganisationId={selectedOrganisationId}
            onSelectOrganisation={async (organisationId) => {
              setSelectedOrganisationId(organisationId);
              localStorage.setItem('selectedOrganisationId', organisationId);
              await saveActiveOrganisationPreference(organisationId);
            }}
            onCreateOrganisation={handleCreateOrganisation}
            onDeleteOrganisation={handleDeleteOrganisation}
            onRefresh={refreshOrganisations}
            onOpenUsers={() => navigate('/users')}
            onOpenSettings={() => navigate('/settings')}
            onBack={() => navigate('/people')}
            showAlert={showAlert}
          />
        </>
      )}

      {/* Shared Components for all Admin Views */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onConfirm={modal.onConfirm}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        inputLabel={modal.inputLabel}
        inputPlaceholder={modal.inputPlaceholder}
        inputValue={modal.inputValue}
        onInputChange={modal.onInputChange}
      />
      <Modal
        isOpen={humanCheck.isOpen}
        onClose={closeHumanCheck}
        type="confirm"
        title={t('errors.tooManyRequests.title', 'Too Many Requests')}
        message={humanCheck.error
          ? t(
            'errors.tooManyRequests.messageWithError',
            'You clicked very quickly. {{error}} Please wait about {{seconds}} seconds before retrying.',
            { error: humanCheck.error, seconds: humanCheck.retryAfterSeconds }
          )
          : t(
            'errors.tooManyRequests.message',
            'You clicked very quickly. Please wait about {{seconds}} seconds and complete this quick human check before retrying.',
            { seconds: humanCheck.retryAfterSeconds }
          )}
        onConfirm={() => {
          if (String(humanCheck.input || '').trim() !== String(humanCheck.a + humanCheck.b)) {
            setHumanCheck((prev) => ({ ...prev, error: t('errors.tooManyRequests.answerIncorrect', 'The answer was not correct.') }));
            return false;
          }
          sessionStorage.setItem('humanVerifiedUntil', String(Date.now() + (10 * 60 * 1000)));
          setTimeout(() => {
            showAlert(
              t('errors.tooManyRequests.verifiedMessage', 'Verification complete. You can try again shortly.'),
              'success',
              t('errors.tooManyRequests.verifiedTitle', 'Verified')
            );
          }, 0);
          return true;
        }}
        confirmText={t('errors.tooManyRequests.verifyAction', 'Verify')}
        cancelText={t('common.actions.close', 'Close')}
        inputLabel={t('errors.tooManyRequests.inputLabel', 'Human check: {{a}} + {{b}} = ?', { a: humanCheck.a, b: humanCheck.b })}
        inputPlaceholder={t('errors.tooManyRequests.inputPlaceholder', 'Enter result')}
        inputValue={humanCheck.input}
        onInputChange={(value) => setHumanCheck((prev) => ({ ...prev, input: value, error: '' }))}
      />
      <Modal
        isOpen={createCardModal.isOpen}
        onClose={handleCreateCardCancel}
        type="info"
        title={t('editor.createCard.title', 'Create New Card')}
        message={t('editor.createCard.message', "Enter a user URL for the new card (e.g., 'sarah'):")}
        inputLabel={t('editor.createCard.inputLabel', 'User URL')}
        inputPlaceholder={t('editor.createCard.inputPlaceholder', 'sarah')}
        inputValue={createCardModal.slug}
        onInputChange={(value) => setCreateCardModal(prev => ({ ...prev, slug: value }))}
        onConfirm={handleCreateCardConfirm}
        confirmText={t('common.actions.create', 'Create')}
        cancelText={t('common.actions.cancel', 'Cancel')}
      />
      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />
      <ChangePasswordModal
        isOpen={changePasswordModal.isOpen}
        onClose={() => setChangePasswordModal({ isOpen: false, userId: null, isOwnerChangingUser: false })}
        apiCall={apiCall}
        userId={changePasswordModal.userId}
        isOwnerChangingUser={changePasswordModal.isOwnerChangingUser}
      />
      </>
    );
  };

  return (
    <>
      <DemoModeBanner />
      <Routes>
        {/* Admin routes - must come before public routes to prevent matching */}
        <Route path="/login" element={renderAdminViews()} />
        <Route path="/reset-password/:token" element={<ResetPasswordRoute />} />
        <Route path="/setup" element={renderAdminViews()} />
        <Route path="/people/edit/:slug" element={renderAdminViews()} />
        <Route path="/people" element={renderAdminViews()} />
        <Route path="/settings" element={renderAdminViews()} />
        <Route path="/users" element={renderAdminViews()} />
        <Route path="/profile" element={renderAdminViews()} />
        <Route path="/departments" element={renderAdminViews()} />
        <Route path="/organizations" element={renderAdminViews()} />
        <Route path="/cards" element={renderAdminViews()} />
        <Route path="/" element={renderAdminViews()} />
        {/* Invitation acceptance route - must come before public card routes */}
        <Route path="/invite/:token" element={
          <InvitationAcceptance
            apiCall={apiCall}
            showAlert={showAlert}
            API_ENDPOINT={API_ENDPOINT}
          />
        } />
        {/* Public card routes - org-scoped must come before single slug */}
        <Route path="/:orgSlug/:cardSlug" element={
          <PublicCardRoute
            view={view}
            isPublicLoading={isPublicLoading}
            error={error}
            data={data}
            settings={settings}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            showAlert={showAlert}
            fetchCardByOrgAndSlug={fetchCardByOrgAndSlug}
            fetchCardByShortCode={fetchCardByShortCode}
            fetchPublicCard={fetchPublicCard}
          />
        } />
        {/* Public card route - matches short code or legacy slug */}
        <Route path="/:slug" element={
          <PublicCardRoute
            view={view}
            isPublicLoading={isPublicLoading}
            error={error}
            data={data}
            settings={settings}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            showAlert={showAlert}
            fetchCardByOrgAndSlug={fetchCardByOrgAndSlug}
            fetchCardByShortCode={fetchCardByShortCode}
            fetchPublicCard={fetchPublicCard}
          />
        } />
        {/* Catch-all for other routes */}
        <Route path="*" element={renderAdminViews()} />
      </Routes>
    </>
  );
}

function PublicCardRoute({ view, isPublicLoading, error, data, settings, darkMode, toggleDarkMode, showAlert, fetchCardByOrgAndSlug, fetchCardByShortCode, fetchPublicCard }) {
  const { t } = useAppTranslation();
  const params = useParams();
  const location = useLocation();


  // Track the last fetched pathname to prevent duplicate fetches
  const lastFetchedPathRef = useRef(null);

  // Apply app theme variant class to body for CSS overrides (vcard|minimal|custom)
  // This ensures the theme is applied to public cards the same way as admin views
  useEffect(() => {
    const variant = normalizeThemeVariantClient(settings?.theme_variant || 'vcard');
    document.body.classList.remove('theme-vcard', 'theme-minimal', 'theme-mint', 'theme-nagarro', 'theme-custom');
    document.body.classList.add(`theme-${variant}`);
    applyThemeCssVars(variant, settings?.app_theme_overrides || {});
  }, [settings]);

  // Fetch the card when the route changes
  useEffect(() => {
    const path = location.pathname;

    // Single guard: skip if we've already fetched for this exact pathname
    if (lastFetchedPathRef.current === path) {
      return;
    }

    // Parse route to determine fetch strategy
    const pathParts = path.substring(1).split('/').filter(p => p);
    const isShortCode = pathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(pathParts[0]);
    const isOrgScoped = pathParts.length === 2 && pathParts[0] && pathParts[1];
    const isLegacy = pathParts.length === 1 && !isShortCode;

    // Mark this path as being fetched BEFORE calling fetch (prevents race conditions)
    lastFetchedPathRef.current = path;

    // Fetch based on route type
    if (isShortCode && fetchCardByShortCode) {
      fetchCardByShortCode(pathParts[0]);
    } else if (isOrgScoped && fetchCardByOrgAndSlug) {
      fetchCardByOrgAndSlug(pathParts[0], pathParts[1]);
    } else if (isLegacy && fetchPublicCard) {
      fetchPublicCard(pathParts[0]);
    } else {
      console.error('[PublicCardRoute] No valid fetch strategy for path:', path);
      // Reset ref if we can't fetch (allows retry on next render if functions become available)
      lastFetchedPathRef.current = null;
    }
  }, [location.pathname, fetchCardByOrgAndSlug, fetchCardByShortCode, fetchPublicCard]);

  // Show loading state while fetching card
  const pathParts = location.pathname.substring(1).split('/').filter(p => p);
  const hasPublicRouteParams = params.slug || params.cardSlug || params.orgSlug || pathParts.length > 0;

  if (isPublicLoading || ((view === 'loading' || view === 'public-loading') && hasPublicRouteParams)) {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify-center items-center">
        <div className="text-text-muted-subtle dark:text-text-muted-dark">{t('publicCard.status.loading', 'Loading...')}</div>
      </div>
    );
  }

  // Show 404 if card not found
  if (view === '404') {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify-center items-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark mb-2">{t('publicCard.status.notFoundTitle', 'Card Not Found')}</h1>
          <p className="text-text-secondary dark:text-text-muted-dark">{error || t('publicCard.status.notFoundMessage', 'The card you are looking for does not exist.')}</p>
        </div>
      </div>
    );
  }

  // Show card when loaded
  if (view === 'public-card' && data && data.personal) {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify-center items-start lg:items-center p-0 lg:p-8">
        <div className="w-full max-w-md bg-card dark:bg-card-dark min-h-screen lg:min-h-0 lg:h-auto lg:rounded-page shadow-2xl overflow-hidden relative animate-in fade-in duration-500 flex flex-col">
          <div className="flex-1">
            <CardDisplay
              data={data}
              settings={settings}
              darkMode={darkMode}
              toggleDarkMode={toggleDarkMode}
              showAlert={showAlert}
            />
          </div>
        </div>
      </div>
    );
  }

  // Default: show loading (shouldn't reach here, but safety net)
  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify_center items-center">
      <div className="text-text-muted-subtle dark:text-text-muted-dark">{t('publicCard.status.loading', 'Loading...')}</div>
    </div>
  );
}

function CardDisplay({ data, settings, darkMode, toggleDarkMode, showAlert }) {
  const { t } = useAppTranslation();
  const { personal = {}, contact = {}, social = {}, images = {}, theme = { color: 'indigo' }, links = [], privacy = {} } = data;
  const themeColor = settings?.theme_colors?.find(c => c.name === theme.color);
  const [showQR, setShowQR] = useState(false);
  const [qrMode, setQrMode] = useState(() => {
    if (typeof navigator !== 'undefined') {
      return navigator.onLine ? 'simple' : 'rich';
    }
    return 'simple';
  });
  const [qrSimpleDataUrl, setQrSimpleDataUrl] = useState('');
  const [qrRichDataUrl, setQrRichDataUrl] = useState('');
  const [offlineQrPayload, setOfflineQrPayload] = useState(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [qrError, setQrError] = useState(null);
  const [contactRevealed, setContactRevealed] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);
  // Lead capture deep links, using the card owner's contact details
  const phoneEntries = collectContactPhones(contact);
  const addressData = buildAddressData(contact, personal);
  const locationDisplay = sanitizeText(personal.location || addressData.summary || '').trim();
  const routeLink = addressData.routeQuery
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressData.routeQuery)}`
    : null;
  const ownerPhone = phoneEntries[0]?.value || '';
  const ownerEmail = contact.email || '';
  // Extract digits only from E.164 format (e.g., +447779331447 -> 447779331447)
  // This works for both E.164 (+44...) and legacy formats
  const ownerPhoneDigits = ownerPhone.replace(/\D/g, '');

  const whatsappLink = ownerPhoneDigits && ownerPhoneDigits.length >= 8
    ? `https://wa.me/${ownerPhoneDigits}?text=${encodeURIComponent(
      'Hi, we met via your VCARD card. My name is ... and my number/email is ...'
    )}`
    : null;

  const emailLink = ownerEmail
    ? `mailto:${ownerEmail}?subject=${encodeURIComponent(
      'My details from VCARD'
    )}&body=${encodeURIComponent(
      'Hi, we met via your VCARD card.\nMy name is ...\nMy phone number is ...\nMy email address is ...'
    )}`
    : null;

  const dropCallLink = ownerPhone ? `tel:${ownerPhone}` : null;

  // Helper functions for obfuscation
  const obfuscateContact = (value) => {
    if (!value) return '';
    return btoa(value);
  };

  const deobfuscateContact = (obfuscated) => {
    if (!obfuscated) return '';
    try {
      return atob(obfuscated);
    } catch (e) {
      return '';
    }
  };

  // Apply app theme variant class to body for CSS overrides (vcard|minimal|custom)
  // This ensures the theme is applied when CardDisplay renders (both in editor preview and public view)
  useEffect(() => {
    const variant = normalizeThemeVariantClient(settings?.theme_variant || 'vcard');
    document.body.classList.remove('theme-vcard', 'theme-minimal', 'theme-mint', 'theme-nagarro', 'theme-custom');
    document.body.classList.add(`theme-${variant}`);
    applyThemeCssVars(variant, settings?.app_theme_overrides || {});
  }, [settings]);

  // UPDATED: Set Title
  useEffect(() => {
    const firstName = sanitizeText(personal.firstName || '');
    const lastName = sanitizeText(personal.lastName || '');
    if (firstName || lastName) {
      document.title = `${firstName} ${lastName}`;
    }
  }, [personal]);

  // Dynamic per-card manifest link (per-card app name & start_url)
  // Note: The manifest link is updated synchronously in index.html before React loads
  // This useEffect is just a backup to ensure it's correct after navigation
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const path = window.location.pathname || '';
    const slug = path.replace(/^\//, '').split('/')[0];
    if (!slug || slug === 'admin') return;

    const head = document.head;
    let link = document.querySelector('link[rel="manifest"]');
    const dynamicHref = `/manifest/${slug}.json`;

    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'manifest');
      head.appendChild(link);
    }

    if (link.getAttribute('href') !== dynamicHref) {
      link.setAttribute('href', dynamicHref);
    }
  }, []);

  // Handle PWA install prompt (keep button visible until actually installed)
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsPwaInstalled(true);
      setDeferredPrompt(null);
    };

    const checkStandalone = () => {
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      if (isStandalone) {
        setIsPwaInstalled(true);
      }
    };

    checkStandalone();

    // Set up event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const shouldShowInstallButton = !isPwaInstalled;

  // Track online/offline status for UI
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle robots meta tag
  useEffect(() => {
    const shouldBlockRobots = privacy.blockRobots ?? true;
    if (shouldBlockRobots) {
      let metaRobots = document.querySelector('meta[name="robots"]');
      if (!metaRobots) {
        metaRobots = document.createElement('meta');
        metaRobots.setAttribute('name', 'robots');
        document.head.appendChild(metaRobots);
      }
      metaRobots.setAttribute('content', 'noindex, nofollow');
    } else {
      const metaRobots = document.querySelector('meta[name="robots"]');
      if (metaRobots) {
        metaRobots.remove();
      }
    }

    // Cleanup on unmount
    return () => {
      const metaRobots = document.querySelector('meta[name="robots"]');
      if (metaRobots) {
        metaRobots.remove();
      }
    };
  }, [privacy.blockRobots]);

  // Fetch QR code when modal opens; also cache rich payload for offline use
  useEffect(() => {
    if (!showQR) return;

    setQrError(null);

    // Get short code from data (if available) or extract from URL
    const pathParts = typeof window !== 'undefined' ? window.location.pathname.substring(1).split('/').filter(p => p) : [];
    const isShortCodeRoute = pathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(pathParts[0]);
    const shortCode = data._shortCode || (isShortCodeRoute ? pathParts[0] : null);

    // For QR generation, always use short code if available, otherwise fallback to slug
    const qrIdentifier = shortCode || (pathParts.length > 0 ? pathParts[pathParts.length - 1] : '');

    const payload = buildQrPayload(shortCode, { personal, contact, social, images, theme });
    // Always cache latest rich payload for offline use
    saveQrPayloadToStorage(payload);

    // Online check – if offline, try to use cached payload instead of hitting API
    const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

    if (!isOnline) {
      // In offline mode, only rich mode can leverage cached payload;
      // simple (link-only) QR is effectively online-only unless previously loaded.
      if (qrMode === 'rich') {
        const cached = loadQrPayloadFromStorage();
        if (cached) {
          setOfflineQrPayload(cached);
        }
      }
      return;
    }

    if (qrMode === 'simple' && !qrSimpleDataUrl) {
      fetch(`${API_ENDPOINT}/qr/${qrIdentifier}`, {
        method: 'GET',
        credentials: 'include'
      })
        .then(res => {
          if (!res.ok) {
            throw new Error(`QR request failed: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          if (data?.qrCode) {
            setQrSimpleDataUrl(data.qrCode);
          }
        })
        .catch(err => {
          console.error('Failed to fetch simple QR code:', err);
          setQrError('Unable to load link-only QR right now. Please try again in a moment.');
        });
    } else if (qrMode === 'rich' && !qrRichDataUrl) {
      fetch(`${API_ENDPOINT}/qr/${qrIdentifier}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ payload })
      })
        .then(res => {
          if (!res.ok) {
            throw new Error(`QR request failed: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          if (data?.qrCode) {
            setQrRichDataUrl(data.qrCode);
          }
          saveQrPayloadToStorage(payload);
          setOfflineQrPayload(payload);
        })
        .catch(err => {
          console.error('Failed to fetch rich QR code:', err);
          const cached = loadQrPayloadFromStorage();
          if (cached) {
            setOfflineQrPayload(cached);
          }
          setQrError('Unable to load full-details QR right now. Your last saved details are still available offline.');
        });
    }
  }, [showQR, qrMode, qrSimpleDataUrl, qrRichDataUrl, personal, contact, social, images, theme, data]);

  const generateVCard = () => {
    const vcard = buildVCardString({
      personal,
      contact,
      cardUrl: shortUrl
    });
    const firstName = sanitizeText(personal.firstName || 'contact');
    const lastName = sanitizeText(personal.lastName || 'card');
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${firstName}_${lastName}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const currentQrDataUrl = qrMode === 'simple' ? qrSimpleDataUrl : qrRichDataUrl;

  // Extract short code for display
  const pathParts = typeof window !== 'undefined' ? window.location.pathname.substring(1).split('/').filter(p => p) : [];
  const isShortCodeRoute = pathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(pathParts[0]);
  const shortCode = data._shortCode || (isShortCodeRoute ? pathParts[0] : null);
  const shortUrl = shortCode ? `${window.location.origin}/${shortCode}` : '';
  const cardName = `${personal.firstName || ''} ${personal.lastName || ''}`.trim();
  const company = personal.company || '';

  // If QR is shown, render only the QR view (full screen, independent of card)
  if (showQR) {
    return (
      <div className="fixed inset-0 bg-card dark:bg-card-dark flex flex-col text-center overflow-hidden lg:rounded-[22px] z-50 min-h-screen lg:min-h-0 lg:h-auto">
        {/* QR Code section at top */}
        <div className="flex flex-col items-center justify-start pt-8 px-4 pb-8 lg:pt-8 lg:flex-shrink-0">
          <div className="w-[90%]">
            <div className="w-full bg-input-bg dark:bg-input-bg-dark p-3 rounded-input border-thick border-border-subtle dark:border-border-dark flex items-center justify-center overflow-hidden">
              {currentQrDataUrl ? (
                <img src={currentQrDataUrl} className="w-full aspect-square mix-blend-multiply dark:mix-blend-normal" alt={t('publicCard.qr.alt', 'QR code')} />
              ) : qrMode === 'rich' && offlineQrPayload ? (
                <div className="w-full aspect-square flex flex-col items-center justify-center text-text-muted-subtle dark:text-text-secondary-dark text-xs space-y-1">
                  <span>{isOnline ? t('publicCard.qr.savedDetails', 'Saved details') : t('publicCard.qr.offlineMode', 'Offline mode')}</span>
                  <span className="text-[10px] opacity-80 px-1">
                    {t('publicCard.qr.savedDetailsDescription', 'This code includes your saved VCARD details and works even when you\'re offline.')}
                  </span>
                </div>
              ) : (!isOnline && qrMode === 'simple' && !qrSimpleDataUrl) ? (
                <div className="w-full aspect-square flex flex-col items-center justify-center text-text-muted-subtle dark:text-text-secondary-dark text-xs text-center space-y-1">
                  <span>{t('publicCard.qr.linkOnlyOnline', 'Link-only QR is available when you\'re online.')}</span>
                  <span className="text-[10px] opacity-80 px-1">
                    {t('publicCard.qr.switchToFullDetails', 'Switch to "Full details" to store contact details directly in the QR code.')}
                  </span>
                </div>
              ) : (
                <div className="w-full aspect-square flex items-center justify-center text-text-muted-subtle dark:text-text-muted-dark text-xs text-center">
                  {isOnline
                    ? (qrError || t('publicCard.qr.loading', 'Loading your QR code...'))
                    : t('publicCard.qr.connectOnce', 'Connect once to generate and save your QR code for offline use.')}
                </div>
              )}
            </div>
          </div>

          {/* Card information display */}
          <div className="mt-6 space-y-2 px-4">
            {cardName && (
              <h2 className="text-xl font-bold text-text-primary dark:text-text-primary-dark">{cardName}</h2>
            )}
            {company && (
              <div className="text-text-muted dark:text-text-muted-dark text-sm">{company}</div>
            )}
            {shortUrl && (
              <div className="text-text-muted-subtle dark:text-text-secondary-dark text-xs font-mono break-all">{shortUrl}</div>
            )}
          </div>

          {/* Offline note */}
          {!isOnline && offlineQrPayload && (
            <p className="text-xs text-text-muted dark:text-text-muted-dark mt-4 px-4">
              {t('publicCard.qr.usingOffline', 'Using last saved QR details (offline)...')}
            </p>
          )}
        </div>

        {/* Controls and logo section at bottom */}
        <div className="mt-auto pb-4 px-4 space-y-3 lg:pb-4 lg:pt-4 lg:flex-shrink-0">
          {/* Toggle buttons */}
          <div className="flex w-full items-center justify-center gap-1 bg-surface dark:bg-surface-dark rounded-full p-1 text-[11px] max-w-md mx-auto">
            <button
              type="button"
              onClick={() => setQrMode('simple')}
              className={`flex-1 px-2.5 py-1 rounded-full font-medium transition-colors ${qrMode === 'simple'
                ? 'bg-card dark:bg-main-dark text-text-primary dark:text-text-primary-dark shadow-sm'
                : 'text-text-muted dark:text-text-secondary-dark'
              }`}
            >
              {t('publicCard.qr.linkOnly', 'Link only')}
            </button>
            <button
              type="button"
              onClick={() => setQrMode('rich')}
              className={`flex-1 px-2.5 py-1 rounded-full font-medium transition-colors ${qrMode === 'rich'
                ? 'bg-card dark:bg-main-dark text-text-primary dark:text-text-primary-dark shadow-sm'
                : 'text-text-muted dark:text-text-secondary-dark'
              }`}
            >
              {t('publicCard.qr.fullDetails', 'Full details')}
            </button>
          </div>

          <button onClick={() => setShowQR(false)} className="w-full max-w-md mx-auto py-3 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark font-bold rounded-input hover:bg-surface dark:hover:bg-surface-dark text-sm transition-colors">{t('common.actions.close', 'Close')}</button>

          {/* VCARD logo */}
          <div className="bg-card dark:bg-card-dark text-center space-y-2 mt-[24px] mb-[12px]">
            <div className="flex justify-center py-4">
              <img src="/graphics/VCARD_Brand_Logo.svg" alt={t('common.alt.brandLogo', 'VCARD logo')} className="h-8 md:h-10 w-auto dark:hidden brand-logo" />
              <img src="/graphics/VCARD_Brand_Logo_DarkBg.svg" alt={t('common.alt.brandLogo', 'VCARD logo')} className="h-8 md:h-10 w-auto hidden dark:block brand-logo" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Otherwise, render the normal card view
  return (
    <div className={`flex flex-col h-full bg-card dark:bg-card-dark`}>
      <div className="h-44 w-full relative bg-surface dark:bg-surface-dark">
        {images.banner ? (
          <img src={images.banner} className="w-full h-full object-cover" alt={t('publicCard.images.bannerAlt', 'Card banner')} />
        ) : (
          (() => {
            const gradientStyle = getThemeGradient(theme.color, settings);
            return <div className="w-full h-full opacity-90" style={{ background: gradientStyle }} />;
          })()
        )}
        <div className="absolute top-4 right-4 flex gap-2">
          <button onClick={toggleDarkMode} className="bg-white/30 dark:bg-black/30 backdrop-blur-md p-2.5 rounded-full text-white hover:bg-white/40 dark:hover:bg-black/40 transition-all border border-white/20 dark:border-white/10 shadow-sm">
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <button onClick={() => setShowQR(true)} className="bg-white/30 dark:bg-black/30 backdrop-blur-md p-2.5 rounded-full text-white hover:bg-white/40 dark:hover:bg-black/40 transition-all border border-white/20 dark:border-white/10 shadow-sm" aria-label={t('publicCard.actions.showQr', 'Show QR code')} title={t('publicCard.actions.showQr', 'Show QR code')}>
            <Share2 className="w-5 h-5" />
          </button>
          {shouldShowInstallButton && (
            <button
              onClick={async () => {
                if (deferredPrompt) {
                  try {
                    await deferredPrompt.prompt();
                    const { outcome } = await deferredPrompt.userChoice;
                    if (outcome === 'accepted') {
                      setIsPwaInstalled(true);
                    }
                  } catch (e) {
                    console.error('Install prompt failed:', e);
                    if (typeof showAlert === 'function') {
                      showAlert(
                        t(
                          'pwa.install.failedMessage',
                          'Install prompt failed. Please use your browser menu to install: Chrome/Edge (three dots menu > Install app), Firefox (menu > Install), or Safari (Share > Add to Home Screen).'
                        ),
                        'error',
                        t('pwa.install.failedTitle', 'Install Failed')
                      );
                    }
                  } finally {
                    setDeferredPrompt(null);
                  }
                } else {
                  // Check if app is already installed
                  const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                    window.navigator.standalone === true;

                  if (isStandalone) {
                    if (typeof showAlert === 'function') {
                      showAlert(
                        t('pwa.install.alreadyInstalledMessage', 'This app is already installed on your device.'),
                        'info',
                        t('pwa.install.alreadyInstalledTitle', 'Already Installed')
                      );
                    }
                    setIsPwaInstalled(true);
                  } else {
                    // Provide manual installation instructions
                    const userAgent = navigator.userAgent.toLowerCase();
                    let instructions = `${t('pwa.install.instructionsIntro', 'To install this app:')}\n\n`;

                    if (userAgent.includes('chrome') || userAgent.includes('edge')) {
                      instructions += t('pwa.install.instructions.chrome', 'Chrome/Edge: Click the three dots menu (⋮) in the address bar, then select "Install app" or "Add to Home Screen".');
                    } else if (userAgent.includes('firefox')) {
                      instructions += t('pwa.install.instructions.firefox', 'Firefox: Click the menu button, then select "Install" or "Add to Home Screen".');
                    } else if (userAgent.includes('safari')) {
                      instructions += t('pwa.install.instructions.safari', 'Safari (iOS): Tap the Share button, then "Add to Home Screen".');
                    } else {
                      instructions += t('pwa.install.instructions.generic', 'Open your browser menu and look for "Install app" or "Add to Home Screen" option.');
                    }

                    instructions += `\n\n${t('pwa.install.instructions.note', "Note: The install button may not be available if the app doesn't meet PWA requirements (service worker, valid manifest, etc.).")}`;

                    if (typeof showAlert === 'function') {
                      showAlert(
                        instructions,
                        'info',
                        t('pwa.install.title', 'Install VCARD')
                      );
                    } else {
                      // Fallback if showAlert is not available
                      alert(instructions);
                    }
                  }
                }
              }}
              className="bg-white/30 dark:bg-black/30 backdrop-blur-md p-2.5 rounded-full text-white hover:bg-white/40 dark:hover:bg-black/40 transition-all border border-white/20 dark:border-white/10 shadow-sm"
              aria-label={t('pwa.install.buttonLabel', 'Install app for offline access')}
              title={t('pwa.install.buttonLabel', 'Install app for offline access')}
            >
              <Download className="w-5 h-5" />
            </button>
          )}
          {!isOnline && offlineQrPayload && (
            <span className="hidden xs:inline-flex items-center px-3 py-1 rounded-full text-[10px] font-semibold bg-amber-500/80 text-white shadow-sm">
              {t('publicCard.status.offlineQrReady', 'Offline QR ready')}
            </span>
          )}
        </div>
      </div>

      <div className="px-6 pb-6 -mt-16 relative flex-1 flex flex-col min-h-0">
        <div className="w-32 h-32 min-h-[8rem] flex-shrink-0 rounded-full border-avatar border-white dark:border-card-dark shadow-xl overflow-hidden bg-card dark:bg-card-dark relative mb-4">
          {images.avatar ? <img src={images.avatar} className="w-full h-full object-cover" alt={t('publicCard.images.avatarAlt', 'Profile avatar')} /> : <div className="w-full h-full bg-surface dark:bg-surface-dark flex items-center justify-center text-text-muted-subtle dark:text-text-muted-dark"><User className="w-12 h-12" /></div>}
        </div>

        <div className="space-y-1 mb-8">
          <h1 className="text-3xl font-bold text-text-primary dark:text-text-primary-dark tracking-tight">{sanitizeText(`${personal.firstName || ''} ${personal.lastName || ''}`).trim() || t('publicCard.fallbacks.untitled', 'Untitled')}</h1>
          {(() => {
            const color = settings?.theme_colors?.find(c => c.name === theme.color);
            const title = sanitizeText(personal.title || '');
            if (color?.textStyle) {
              return <div className="text-lg font-medium" style={{ color: color.textStyle }}>{title}</div>;
            }
            return <div className="text-lg font-medium" style={{ color: getTextColor(theme.color, settings) }}>{title}</div>;
          })()}
          <div className="flex items-center text-text-muted dark:text-text-muted-dark text-sm gap-2"><Briefcase className="w-4 h-4" /><span>{sanitizeText(personal.company || '')}</span></div>
          {locationDisplay && <div className="flex items-center text-text-muted-subtle dark:text-text-muted-dark text-sm gap-2 mt-1"><MapPin className="w-4 h-4" /><span>{locationDisplay}</span></div>}
        </div>

        {personal.bio && <div className="mb-8"><p className="text-text-secondary dark:text-text-secondary-dark leading-relaxed text-sm" dangerouslySetInnerHTML={{ __html: sanitizeHTML(personal.bio) }}></p></div>}

        <div className="grid grid-cols-2 gap-3 mb-8">
          {(() => {
            const requireInteraction = privacy.requireInteraction ?? false;
            const shouldShowVCF = !requireInteraction || contactRevealed;

            // Only show VCF button if interaction is not required OR contact has been revealed
            if (shouldShowVCF) {
              const color = settings?.theme_colors?.find(c => c.name === theme.color);
              if (color?.buttonStyle) {
                const hoverColor = darkenHex(color.buttonStyle, 10);
                return (
                  <>
                    <button
                      onClick={generateVCard}
                      className="col-span-2 flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-white shadow-lg transition-all active:scale-[0.98]"
                      style={{ backgroundColor: color.buttonStyle }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = hoverColor;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = color.buttonStyle;
                      }}
                    >
                      <Save className="w-5 h-5" /> {t('publicCard.actions.saveContact', 'Save Contact')}
                    </button>
                  </>
                );
              }
              return (
                <>
                  <button
                    onClick={generateVCard}
                    className="col-span-2 flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-white shadow-lg transition-transform active:scale-[0.98]"
                    style={{ backgroundColor: getButtonColor(theme.color, settings) }}
                  >
                    <Save className="w-5 h-5" /> {t('publicCard.actions.saveContact', 'Save Contact')}
                  </button>
                </>
              );
            }
            return null;
          })()}
          {(() => {
            const requireInteraction = privacy.requireInteraction ?? false;
            const useObfuscation = privacy.clientSideObfuscation ?? true;
            const hasEmail = contact.email;
            const hasPhone = phoneEntries.length > 0;

            // If interaction required and not yet revealed, show reveal button
            if (requireInteraction && !contactRevealed && (hasEmail || hasPhone)) {
              return (
                <button
                  onClick={() => setContactRevealed(true)}
                  className="col-span-2 flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors border border-border dark:border-border-dark"
                >
                  <Eye className="w-5 h-5" /> {t('publicCard.actions.showDetails', 'See my details')}
                </button>
              );
            }

            // Get actual contact values
            const emailValue = hasEmail ? contact.email : '';
            const phones = hasPhone ? phoneEntries : [];

            // If obfuscation is enabled, store obfuscated values in data attributes
            const emailData = useObfuscation && emailValue ? obfuscateContact(emailValue) : '';

            return (
              <>
                {hasEmail && (
                  <a
                    href={useObfuscation ? '#' : `mailto:${emailValue}`}
                    data-email={useObfuscation ? emailData : undefined}
                    onClick={(e) => {
                      if (useObfuscation) {
                        e.preventDefault();
                        const actualEmail = deobfuscateContact(e.currentTarget.dataset.email);
                        window.location.href = `mailto:${actualEmail}`;
                      }
                    }}
                    className="flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors border border-border dark:border-border-dark"
                  >
                    <Mail className="w-5 h-5" /> {t('publicCard.actions.email', 'Email')}
                  </a>
                )}
                {phones.map((phoneEntry, index) => {
                  const phoneValue = phoneEntry.value;
                  const phoneLabel = phoneEntry.label || (index === 0 ? 'Mobil' : 'Telefon');
                  const phoneData = useObfuscation && phoneValue ? obfuscateContact(phoneValue) : '';
                  return (
                    <a
                      key={`${phoneValue}-${index}`}
                      href={useObfuscation ? '#' : `tel:${phoneValue}`}
                      data-phone={useObfuscation ? phoneData : undefined}
                      onClick={(e) => {
                        if (useObfuscation) {
                          e.preventDefault();
                          const actualPhone = deobfuscateContact(e.currentTarget.dataset.phone);
                          window.location.href = `tel:${actualPhone}`;
                        }
                      }}
                      className="flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors border border-border dark:border-border-dark"
                    >
                      <Phone className="w-5 h-5" /> {phoneLabel}
                    </a>
                  );
                })}
                {routeLink && data.contact?.showRouteButton !== false && (
                  <a
                    href={routeLink}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors border border-border dark:border-border-dark"
                  >
                    <MapPin className="w-5 h-5" /> {t('publicCard.actions.route', 'Route')}
                  </a>
                )}
              </>
            );
          })()}
        </div>

        {/* Send your details CTA */}
        <div className="mb-8">
          <button
            type="button"
            onClick={() => setShowSendOptions(open => !open)}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold bg-confirm text-confirm-text dark:bg-confirm-dark dark:text-confirm-text-dark hover:opacity-90 transition-colors shadow-lg active:scale-[0.98]"
          >
            <MessageCircle className="w-5 h-5" />
            {showSendOptions ? t('publicCard.actions.hideSendOptions', 'Hide send options') : t('publicCard.actions.sendDetails', 'Send your details')}
          </button>

          {showSendOptions && (
            <div className="mt-3 space-y-2 rounded-card border border-border dark:border-border-dark bg-surface/60 dark:bg-card-dark/60 p-3 text-left">
              {whatsappLink && (
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-semibold bg-success dark:bg-success-dark text-white hover:bg-success-hover dark:hover:bg-success-hover-dark transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  {t('publicCard.actions.whatsApp', 'WhatsApp me your number')}
                </a>
              )}

              {emailLink && (
                <a
                  href={emailLink}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-semibold bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors border border-border dark:border-border-dark"
                >
                  <Mail className="w-5 h-5" />
                  {t('publicCard.actions.emailMe', 'Email me your details')}
                </a>
              )}

              {dropCallLink && (
                <a
                  href={dropCallLink}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-semibold bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors border border-border dark:border-border-dark"
                >
                  <Phone className="w-5 h-5" />
                  {t('publicCard.actions.dropcall', 'Drop call me your number')}
                </a>
              )}

              <p className="mt-1 text-[11px] text-text-muted dark:text-text-muted-dark text-center">
                {t('publicCard.helper.sharePrivacy', 'Only shared with me, never sold.')}
              </p>
            </div>
          )}
        </div>

        {links.length > 0 && (
          <div className="flex flex-col gap-3 mb-8">
            {links.map(link => {
              const color = settings?.theme_colors?.find(c => c.name === theme.color);
              if (color?.linkStyle) {
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center p-4 rounded-input border transition-all active:scale-[0.99]"
                    style={{
                      color: color.linkStyle,
                      backgroundColor: color.linkStyle + '15',
                      borderColor: color.linkStyle + '30'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = color.linkStyle + '25';
                      const iconContainer = e.target.querySelector('.link-icon-container');
                      if (iconContainer) {
                        iconContainer.style.transform = 'scale(1.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = color.linkStyle + '15';
                      const iconContainer = e.target.querySelector('.link-icon-container');
                      if (iconContainer) {
                        iconContainer.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    <div className="mr-4 p-2 bg-input-bg dark:bg-input-bg-dark rounded-container shadow-sm transition-transform link-icon-container">
                      {React.createElement(ICON_MAP[link.icon] || LinkIcon, { className: "w-5 h-5 text-text-secondary dark:text-text-secondary-dark" })}
                    </div>
                    <span className="font-semibold text-sm flex-1">{sanitizeText(link.title || '')}</span>
                    <ExternalLink className="w-4 h-4 opacity-50" />
                  </a>
                );
              }
              return (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center p-4 rounded-input border transition-all active:scale-[0.99] dark:border-border-dark"
                  style={{ color: getLinkColor(theme.color, settings) }}
                  onMouseEnter={(e) => {
                    const iconContainer = e.target.querySelector('.link-icon-container');
                    if (iconContainer) {
                      iconContainer.style.transform = 'scale(1.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    const iconContainer = e.target.querySelector('.link-icon-container');
                    if (iconContainer) {
                      iconContainer.style.transform = 'scale(1)';
                    }
                  }}
                >
                  <div className="mr-4 p-2 bg-input-bg dark:bg-input-bg-dark rounded-container shadow-sm transition-transform link-icon-container">
                    {React.createElement(ICON_MAP[link.icon] || LinkIcon, { className: "w-5 h-5 text-text-secondary dark:text-text-secondary-dark" })}
                  </div>
                  <span className="font-semibold text-sm flex-1">{link.title}</span>
                  <ExternalLink className="w-4 h-4 opacity-50" />
                </a>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-4 gap-3 mb-8">
          <SocialIcon url={contact.website} icon={Globe} label={t('publicCard.social.website', 'Web')} themeColor={themeColor} />
          <SocialIcon url={social.linkedin} icon={Linkedin} label={t('publicCard.social.linkedin', 'LinkedIn')} themeColor={themeColor} />
          <SocialIcon url={social.twitter} icon={Twitter} label={t('publicCard.social.twitter', 'X')} themeColor={themeColor} />
          <SocialIcon url={social.instagram} icon={Instagram} label={t('publicCard.social.instagram', 'Insta')} themeColor={themeColor} />
          <SocialIcon url={social.github} icon={Github} label={t('publicCard.social.github', 'Git')} themeColor={themeColor} />
        </div>

        {/* VCARD logo */}
        <div className="bg-card dark:bg-card-dark pb-4 text-center space-y-2 mt-auto lg:pb-4">
          <div className="flex justify-center">
            <img src="/graphics/VCARD_Brand_Logo.svg" alt={t('common.alt.brandLogo', 'VCARD logo')} className="h-8 md:h-10 w-auto dark:hidden brand-logo" />
            <img src="/graphics/VCARD_Brand_Logo_DarkBg.svg" alt={t('common.alt.brandLogo', 'VCARD logo')} className="h-8 md:h-10 w-auto hidden dark:block brand-logo" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SortableLinkItem({ link, children }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: link.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  return children({ setNodeRef, style, attributes, listeners });
}

function EditorSectionCard({ title, description, actions = null, children }) {
  return (
    <section className="rounded-card border border-border dark:border-border-dark bg-card dark:bg-card-dark shadow-sm">
      <div className="px-5 py-4 md:px-6 md:py-5 border-b border-border-subtle dark:border-border-dark">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-text-primary dark:text-text-primary-dark">
              {title}
            </h2>
            {description && (
              <p className="mt-1 text-sm text-text-secondary dark:text-text-muted-dark">{description}</p>
            )}
          </div>
          {actions}
        </div>
      </div>
      <div className="px-5 py-5 md:px-6 md:py-6">
        {children}
      </div>
    </section>
  );
}

function EditorView({ data, setData, onBack, onSave, slug, settings, csrfToken, showAlert, darkMode, toggleDarkMode, isSaving, isSuccess }) {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const safeBack = withSafeHandler(onBack, () => navigate('/people'), 'editor-back');
  const [activeTab, setActiveTab] = useState('details');
  const [isUploading, setIsUploading] = useState(false);
  const editorTabs = [
    { key: 'details', label: t('editor.tabs.details', 'Details') },
    { key: 'links', label: t('editor.tabs.links', 'Links') },
    { key: 'images', label: t('editor.tabs.images', 'Images') },
    { key: 'style', label: t('editor.tabs.style', 'Style') },
    { key: 'privacy', label: t('editor.tabs.privacy', 'Privacy') }
  ];
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  );

  const handleInputChange = (section, field, value) => {
    setData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };

  const handleImageUpload = async (type, e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_ENDPOINT}/upload`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRF-Token': csrfToken
        },
        body: formData
      });

      if (res.ok) {
        const { url } = await res.json();
        setData(prev => ({ ...prev, images: { ...prev.images, [type]: url } }));
      } else {
        if (showAlert) showAlert(t('editor.images.uploadFailed', 'Upload failed'), 'error');
      }
    } catch (error) {
      if (showAlert) showAlert(t('editor.images.uploadError', 'Upload error'), 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const addLink = () => {
    const newLink = { id: Date.now(), title: '', url: '', icon: 'link' };
    setData(prev => ({ ...prev, links: [...prev.links, newLink] }));
  };
  const removeLink = (id) => {
    setData(prev => ({ ...prev, links: prev.links.filter(l => l.id !== id) }));
  };
  const updateLink = (id, field, value) => {
    setData(prev => ({ ...prev, links: prev.links.map(l => l.id === id ? { ...l, [field]: value } : l) }));
  };

  const reorderLinks = (oldIndex, newIndex) => {
    if (oldIndex === newIndex) return;
    setData(prev => ({ ...prev, links: arrayMove(prev.links, oldIndex, newIndex) }));
  };

  const moveLinkUp = (index) => {
    if (index <= 0) return;
    reorderLinks(index, index - 1);
  };

  const moveLinkDown = (index) => {
    if (index >= data.links.length - 1) return;
    reorderLinks(index, index + 1);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = data.links.findIndex(link => link.id === active.id);
    const newIndex = data.links.findIndex(link => link.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    reorderLinks(oldIndex, newIndex);
  };

  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture">
      <div className="flex flex-col lg:flex-row lg:items-start">
        <div className="w-full lg:w-[58%] bg-main dark:bg-main-dark relative z-0">
          <div className="sticky top-0 z-[120] isolate lg:fixed lg:top-0 lg:left-0 lg:w-[58%] border-b border-border-subtle dark:border-border-dark bg-card dark:bg-card-dark shadow-lg">
            <div className="px-4 py-4 md:px-6 md:py-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    onClick={safeBack}
                    className="p-2 rounded-full border border-border dark:border-border-dark bg-card dark:bg-card-dark text-text-muted dark:text-text-muted-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors shrink-0"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-[0.18em] text-text-muted dark:text-text-muted-dark font-semibold">{t('editor.header.editingLabel', 'Editing')}</div>
                    <h1 className="text-xl md:text-2xl font-bold text-text-primary dark:text-text-primary-dark truncate">{slug}</h1>
                  </div>
                </div>
                <button
                  onClick={onSave}
                  disabled={isSaving}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark text-sm font-bold hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark transition-colors disabled:opacity-50"
                >
                  {isSaving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : isSuccess ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {t('common.actions.save', 'Save')}
                </button>
              </div>
            </div>
            <div className="px-4 pb-4 md:px-6 md:pb-5">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 rounded-container border border-border dark:border-border-dark bg-surface dark:bg-surface-dark p-1">
                {editorTabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-3 py-2.5 rounded-button text-sm font-semibold transition-all ${activeTab === tab.key
                      ? 'bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark shadow-sm'
                      : 'text-text-muted dark:text-text-muted-dark hover:text-text-primary dark:hover:text-text-primary-dark'
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="relative z-0 px-4 py-5 md:px-6 md:py-6 lg:pt-[212px] xl:pt-[224px] space-y-6">
          {activeTab === 'details' && (
            <div className="space-y-6">
              <EditorSectionCard title={t('editor.sections.generalInfo', 'General Info')}>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label={t('editor.fields.firstName', 'First Name')} value={data.personal.firstName} onChange={v => handleInputChange('personal', 'firstName', v)} />
                    <Input label={t('editor.fields.lastName', 'Last Name')} value={data.personal.lastName} onChange={v => handleInputChange('personal', 'lastName', v)} />
                    <Input label={t('editor.fields.jobTitle', 'Job Title')} value={data.personal.title} onChange={v => handleInputChange('personal', 'title', v)} />
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">
                        {t('editor.fields.organisation', 'Organisation')} <span className="text-xs text-text-muted dark:text-text-muted-dark">({t('editor.fields.readOnly', 'Read-only')})</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={settings?.default_organisation || data.personal.company || ''}
                          disabled
                          className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark text-text-secondary dark:text-text-muted-dark cursor-not-allowed"
                          placeholder={t('editor.fields.organisation', 'Organisation')}
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <Lock className="w-4 h-4 text-text-muted-subtle dark:text-text-muted-dark" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <Input label={t('editor.fields.locationDisplay', 'Location (Display)')} value={data.personal.location} onChange={v => handleInputChange('personal', 'location', v)} />
                  <TextArea label={t('editor.fields.bio', 'Bio')} value={data.personal.bio} onChange={v => handleInputChange('personal', 'bio', v)} rows={1} minHeightClass="min-h-[46px]" />
                </div>
              </EditorSectionCard>

              <EditorSectionCard title={t('editor.sections.contact', 'Contact')}>
                <div className="space-y-4">
                  <Input label={t('editor.fields.email', 'Email')} icon={Mail} placeholder={t('editor.fields.email', 'Email')} value={data.contact.email} onChange={v => handleInputChange('contact', 'email', v)} type="email" />
                  <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(220px,1fr)] gap-4">
                    <PhoneInputField
                      label={t('editor.fields.phone1', 'Phone 1')}
                      value={data.contact.phone || ''}
                      onChange={(value) => handleInputChange('contact', 'phone', value || '')}
                      placeholder={t('editor.fields.phone1', 'Phone 1')}
                    />
                    <Input label={t('editor.fields.label', 'Label')} value={data.contact.phoneLabel} onChange={v => handleInputChange('contact', 'phoneLabel', v)} placeholder={t('common.phoneLabels.mobile', 'Mobil')} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(220px,1fr)] gap-4">
                    <PhoneInputField
                      label={t('editor.fields.phone2', 'Phone 2')}
                      value={data.contact.phoneSecondary || ''}
                      onChange={(value) => handleInputChange('contact', 'phoneSecondary', value || '')}
                      placeholder={t('editor.fields.phone2', 'Phone 2')}
                    />
                    <Input label={t('editor.fields.label', 'Label')} value={data.contact.phoneSecondaryLabel} onChange={v => handleInputChange('contact', 'phoneSecondaryLabel', v)} placeholder={t('common.phoneLabels.phone', 'Telefon')} />
                  </div>
                </div>
              </EditorSectionCard>

              <EditorSectionCard title={t('editor.sections.address', 'Address')}>
                <div className="space-y-4">
                  <Input label={t('editor.fields.street', 'Street')} value={data.contact.addressStreet} onChange={v => handleInputChange('contact', 'addressStreet', v)} />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input label={t('editor.fields.postalCode', 'Postal Code')} value={data.contact.addressPostalCode} onChange={v => handleInputChange('contact', 'addressPostalCode', v)} />
                    <Input label={t('editor.fields.city', 'City')} value={data.contact.addressCity} onChange={v => handleInputChange('contact', 'addressCity', v)} />
                    <SelectField label={t('editor.fields.country', 'Country')} value={data.contact.addressCountry || 'DE'} onChange={v => handleInputChange('contact', 'addressCountry', v)} options={COUNTRY_OPTIONS} />
                  </div>
                  <Toggle
                    label={t('editor.fields.showRouteButton', 'Show route button')}
                    description={t('editor.fields.showRouteButtonDescription', 'Display a route button on the public card when an address is available.')}
                    checked={data.contact.showRouteButton !== false}
                    onChange={(checked) => handleInputChange('contact', 'showRouteButton', checked)}
                  />
                </div>
              </EditorSectionCard>

              <EditorSectionCard title={t('editor.sections.onlinePresence', 'Online Presence')}>
                <div className="space-y-4">
                  <Input label={t('editor.fields.website', 'Website')} icon={Globe} placeholder={t('editor.fields.website', 'Website')} value={data.contact.website} onChange={v => handleInputChange('contact', 'website', v)} type="url" />
                  <Input label={t('editor.fields.linkedin', 'LinkedIn URL')} icon={Linkedin} placeholder={t('editor.fields.linkedin', 'LinkedIn URL')} value={data.social.linkedin} onChange={v => handleInputChange('social', 'linkedin', v)} type="url" />
                  <Input label={t('editor.fields.twitter', 'Twitter / X')} icon={Twitter} placeholder={t('editor.fields.twitter', 'Twitter / X')} value={data.social.twitter} onChange={v => handleInputChange('social', 'twitter', v)} type="url" />
                  <Input label={t('editor.fields.instagram', 'Instagram')} icon={Instagram} placeholder={t('editor.fields.instagram', 'Instagram')} value={data.social.instagram} onChange={v => handleInputChange('social', 'instagram', v)} type="url" />
                  <Input label={t('editor.fields.github', 'Github')} icon={Github} placeholder={t('editor.fields.github', 'Github')} value={data.social.github} onChange={v => handleInputChange('social', 'github', v)} type="url" />
                </div>
              </EditorSectionCard>
            </div>
          )}

          {activeTab === 'links' && (
            <EditorSectionCard
              title={t('editor.tabs.links', 'Links')}
              actions={settings?.allow_links_customisation !== false ? (
                <button onClick={addLink} className="text-sm font-bold text-action dark:text-action-dark hover:opacity-80 flex items-center gap-1">
                  <Plus className="w-4 h-4" /> {t('editor.links.add', 'Add Link')}
                </button>
              ) : (
                <div className="flex items-center gap-2 text-text-muted dark:text-text-muted-dark">
                  <Lock className="w-4 h-4" />
                  <span className="text-sm">{t('editor.links.locked', 'Locked')}</span>
                </div>
              )}
            >
              {settings?.allow_links_customisation === false ? (
                <LockedOption message={t('editor.locks.links', 'Your organisation has disabled custom links. Contact your administrator to enable this feature.')}>
                  <div className="space-y-4">
                    {data.links.map((link) => (
                      <div key={link.id} className="bg-surface dark:bg-surface-dark p-4 rounded-input border border-border dark:border-border-dark">
                        <div className="grid gap-3">
                          <div className="flex gap-3 items-center">
                            <div className="w-10 h-10 rounded-container bg-card dark:bg-surface-dark border border-border dark:border-border-dark flex items-center justify-center shrink-0">
                              {React.createElement(ICON_MAP[link.icon] || LinkIcon, { className: "w-5 h-5 text-text-secondary dark:text-text-secondary-dark" })}
                            </div>
                            <input type="text" value={link.title} disabled className="flex-1 bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-muted dark:text-text-muted-dark rounded-input px-3 py-2 text-sm cursor-not-allowed" />
                          </div>
                          <input type="text" value={link.url} disabled className="w-full bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-muted dark:text-text-muted-dark rounded-input px-3 py-2 text-sm cursor-not-allowed" />
                        </div>
                      </div>
                    ))}
                    {data.links.length === 0 && (
                      <div className="text-center py-8 text-text-muted-subtle dark:text-text-muted-dark text-sm border-thick border-dashed border-border dark:border-border-dark rounded-input">
                        {t('editor.links.empty', 'No custom links yet.')}
                      </div>
                    )}
                  </div>
                </LockedOption>
              ) : (
                <div className="space-y-4">
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={data.links.map(link => link.id)} strategy={verticalListSortingStrategy}>
                      {data.links.map((link, index) => (
                        <SortableLinkItem key={link.id} link={link}>
                          {({ setNodeRef, style, attributes, listeners }) => (
                            <div
                              ref={setNodeRef}
                              style={style}
                              className="bg-surface dark:bg-surface-dark p-4 rounded-input border border-border dark:border-border-dark relative"
                            >
                              <div className="absolute top-2 left-2 flex items-center gap-2">
                                <button
                                  type="button"
                                  className="p-1 rounded-button border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-muted dark:text-text-muted-dark hover:border-action-dark hover:text-action-dark dark:hover:border-action dark:hover:text-action"
                                  {...attributes}
                                  {...listeners}
                                  aria-label={t('editor.links.dragToReorder', 'Drag to reorder')}
                                >
                                  <GripVertical className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="absolute top-2 right-10 flex gap-1">
                                <button
                                  type="button"
                                  onClick={() => moveLinkUp(index)}
                                  disabled={index === 0}
                                  className={`p-1 rounded-button border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-muted dark:text-text-muted-dark hover:border-action-dark hover:text-action-dark dark:hover:border-action dark:hover:text-action disabled:opacity-40 disabled:cursor-not-allowed`}
                                  aria-label={t('editor.links.moveUp', 'Move link up')}
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveLinkDown(index)}
                                  disabled={index === data.links.length - 1}
                                  className={`p-1 rounded-button border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-muted dark:text-text-muted-dark hover:border-action-dark hover:text-action-dark dark:hover:border-action dark:hover:text-action disabled:opacity-40 disabled:cursor-not-allowed`}
                                  aria-label={t('editor.links.moveDown', 'Move link down')}
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </button>
                              </div>
                              <button onClick={() => removeLink(link.id)} className="absolute top-2 right-2 text-text-muted-subtle dark:text-text-muted-dark hover:text-error-text dark:hover:text-error-text-dark hover:bg-error-bg dark:hover:bg-error-bg-dark rounded-full p-1 transition-colors" aria-label={t('editor.links.remove', 'Remove link')}>
                                <X className="w-4 h-4" />
                              </button>
                              <div className="grid gap-3 pt-6">
                                <div className="flex gap-3 items-center">
                                  <div className="w-10 h-10 rounded-container bg-card dark:bg-surface-dark border border-border dark:border-border-dark flex items-center justify-center shrink-0">
                                    {React.createElement(ICON_MAP[link.icon], { className: "w-5 h-5 text-text-secondary dark:text-text-secondary-dark" })}
                                  </div>
                                  <input
                                    type="text"
                                    placeholder={t('editor.links.titlePlaceholder', 'Link Title (e.g. Download CV)')}
                                    value={link.title}
                                    onChange={(e) => updateLink(link.id, 'title', e.target.value)}
                                    className="flex-1 bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark rounded-input px-3 py-2 text-sm focus:outline-none focus:border-action dark:focus:border-action-dark"
                                  />
                                </div>
                                <input
                                  type="text"
                                  placeholder={t('editor.links.urlPlaceholder', 'https://...')}
                                  value={link.url}
                                  onChange={(e) => updateLink(link.id, 'url', e.target.value)}
                                  className="w-full bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark rounded-input px-3 py-2 text-sm focus:outline-none focus:border-action dark:focus:border-action-dark"
                                />
                                {/* Simple Icon Picker */}
                                <div className="flex gap-2 overflow-x-auto pb-2 pt-1 no-scrollbar">
                                  {Object.keys(ICON_MAP).map(iconKey => (
                                    <button
                                      key={iconKey}
                                      onClick={() => updateLink(link.id, 'icon', iconKey)}
                                      className={`p-2 rounded-button border flex-shrink-0 transition-all ${link.icon === iconKey ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-400 text-indigo-600 dark:text-indigo-300' : 'bg-card dark:bg-surface-dark border-border dark:border-border-dark text-text-muted-subtle dark:text-text-muted-dark hover:border-border dark:hover:border-border-dark'}`}
                                      title={iconKey}
                                    >
                                      {React.createElement(ICON_MAP[iconKey], { className: "w-4 h-4" })}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </SortableLinkItem>
                      ))}
                    </SortableContext>
                  </DndContext>
                  {data.links.length === 0 && (
                      <div className="text-center py-8 text-text-muted-subtle dark:text-text-muted-dark text-sm border-thick border-dashed border-border dark:border-border-dark rounded-input">
                      {t('editor.links.empty', 'No custom links yet.')}
                      </div>
                  )}
                </div>
              )}
            </EditorSectionCard>
          )}

          {activeTab === 'images' && (
            <EditorSectionCard title={t('editor.tabs.images', 'Images')}>
              {settings?.allow_image_customisation === false ? (
                <LockedOption message={t('editor.locks.images', 'Your organisation has disabled custom image uploads. Contact your administrator to enable this feature.')}>
                  <div className="space-y-8">
                    <ImageUpload label={t('editor.images.profile', 'Profile Picture')} image={data.images.avatar} onUpload={() => { }} onRemove={() => { }} disabled={true} />
                    <ImageUpload label={t('editor.images.banner', 'Header Banner')} image={data.images.banner} onUpload={() => { }} onRemove={() => { }} isBanner disabled={true} />
                  </div>
                </LockedOption>
              ) : (
                <>
                  {isUploading && <div className="text-center text-sm text-indigo-600 dark:text-indigo-400 animate-pulse">{t('editor.images.uploading', 'Uploading image...')}</div>}
                  <ImageUpload label={t('editor.images.profile', 'Profile Picture')} image={data.images.avatar} onUpload={e => handleImageUpload('avatar', e)} onRemove={() => handleInputChange('images', 'avatar', null)} />
                  <ImageUpload label={t('editor.images.banner', 'Header Banner')} image={data.images.banner} onUpload={e => handleImageUpload('banner', e)} onRemove={() => handleInputChange('images', 'banner', null)} isBanner />
                </>
              )}
            </EditorSectionCard>
          )}

          {activeTab === 'style' && (
            <EditorSectionCard title={t('editor.tabs.style', 'Style')}>
              {settings?.allow_theme_customisation === false ? (
                <LockedOption message={t('editor.locks.theme', 'Your organisation has disabled theme colour customisation. Your card will use the default theme colour.')}>
                  <div className="flex flex-wrap gap-4">
                    {(settings?.theme_colors || []).map(color => (
                      <div
                        key={color.name}
                        className={`w-12 h-12 rounded-full relative ${data.theme.color === color.name ? 'ring-4 ring-slate-200 scale-110' : ''}`}
                        style={{ background: color.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                      >
                        {data.theme.color === color.name && <Check className="w-5 h-5 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10" />}
                      </div>
                    ))}
                  </div>
                </LockedOption>
              ) : (
                <div className="flex flex-wrap gap-4">
                  {(settings?.theme_colors || []).map(color => (
                    <button
                      key={color.name}
                      onClick={() => handleInputChange('theme', 'color', color.name)}
                      className={`w-12 h-12 rounded-full relative hover:scale-110 ${data.theme.color === color.name ? 'ring-4 ring-slate-200 scale-110' : ''}`}
                      style={{ background: color.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                    >
                      {data.theme.color === color.name && <Check className="w-5 h-5 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10" />}
                    </button>
                  ))}
                </div>
              )}
            </EditorSectionCard>
          )}

          {activeTab === 'privacy' && (
            <EditorSectionCard title={t('editor.tabs.privacy', 'Privacy')}>
              {settings?.allow_privacy_customisation === false ? (
                <LockedOption message={t('editor.locks.privacy', 'Your organisation has disabled privacy settings customisation. Privacy settings are controlled by your organisation.')}>
                  <div className="space-y-6">
                    <Toggle
                      label={t('editor.privacy.requireInteraction', 'Require Interaction')}
                      description={t('editor.privacy.requireInteractionDescription', 'Requires users to click a button to reveal email and phone. Prevents basic bots from seeing contact info in the initial page load.')}
                      checked={data.privacy?.requireInteraction ?? false}
                      onChange={() => { }}
                    />
                    <div className="h-px bg-border-subtle" />
                    <Toggle
                      label={t('editor.privacy.clientSideObfuscation', 'Client-Side Obfuscation')}
                      description={t('editor.privacy.clientSideObfuscationDescription', 'Encodes email and phone in the HTML to make scraping harder. Note: Determined scrapers can still decode this.')}
                      checked={data.privacy?.clientSideObfuscation ?? true}
                      onChange={() => { }}
                    />
                    <div className="h-px bg-border-subtle" />
                    <Toggle
                      label={t('editor.privacy.blockSearchEngines', 'Block Search Engines')}
                      description={t('editor.privacy.blockSearchEnginesDescription', 'Adds meta robots tag to prevent search engines from indexing this card.')}
                      checked={data.privacy?.blockRobots ?? true}
                      onChange={() => { }}
                    />
                  </div>
                </LockedOption>
              ) : (
                <div className="space-y-6">
                  <Toggle
                    label={t('editor.privacy.requireInteraction', 'Require Interaction')}
                    description={t('editor.privacy.requireInteractionDescription', 'Requires users to click a button to reveal email and phone. Prevents basic bots from seeing contact info in the initial page load.')}
                    checked={data.privacy?.requireInteraction ?? false}
                    onChange={(checked) => handleInputChange('privacy', 'requireInteraction', checked)}
                  />
                  <div className="h-px bg-border-subtle" />
                  <Toggle
                    label={t('editor.privacy.clientSideObfuscation', 'Client-Side Obfuscation')}
                    description={t('editor.privacy.clientSideObfuscationDescription', 'Encodes email and phone in the HTML to make scraping harder. Note: Determined scrapers can still decode this.')}
                    checked={data.privacy?.clientSideObfuscation ?? true}
                    onChange={(checked) => handleInputChange('privacy', 'clientSideObfuscation', checked)}
                  />
                  <div className="h-px bg-border-subtle" />
                  <Toggle
                    label={t('editor.privacy.blockSearchEngines', 'Block Search Engines')}
                    description={t('editor.privacy.blockSearchEnginesDescription', 'Adds meta robots tag to prevent search engines from indexing this card.')}
                    checked={data.privacy?.blockRobots ?? true}
                    onChange={(checked) => handleInputChange('privacy', 'blockRobots', checked)}
                  />
                </div>
              )}
            </EditorSectionCard>
          )}
        </div>
      </div>

        <div className="hidden lg:block lg:fixed lg:top-0 lg:right-0 lg:w-[42%] lg:h-screen self-start min-h-screen bg-border-subtle dark:bg-card-dark border-l border-border dark:border-border-dark">
          <div className="h-full px-8 py-6 xl:px-10 xl:py-8 flex items-start justify-center">
          <div className="w-full max-w-[420px]">
              <div className="mb-4">
              <div className="text-xs uppercase tracking-[0.18em] text-text-muted dark:text-text-muted-dark font-semibold">{t('editor.sections.livePreview', 'Live Preview')}</div>
              <h2 className="mt-1 text-lg font-bold text-text-primary dark:text-text-primary-dark">{t('editor.sections.publicCard', 'Public card')}</h2>
            </div>
            <div className="w-[375px] max-w-full h-[750px] bg-card dark:bg-main-dark rounded-[3rem] shadow-2xl border-device border-text-primary dark:border-border-dark overflow-hidden relative mx-auto">
              <CardDisplay data={data} settings={settings} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
            </div>
          </div>
        </div>
        </div>
      </div>
      <div className="fixed bottom-4 right-4 z-10 text-center group">
        <div className="flex justify-center">
          <img src="/graphics/VCARD_Brand_Logo.svg" alt={t('common.alt.brandLogo', 'VCARD logo')} className="h-8 md:h-10 w-auto dark:hidden brand-logo" />
          <img src="/graphics/VCARD_Brand_Logo_DarkBg.svg" alt={t('common.alt.brandLogo', 'VCARD logo')} className="h-8 md:h-10 w-auto hidden dark:block brand-logo" />
        </div>
      </div>
    </div>
  );
}

function Input({ label, icon: Icon, value, onChange, type = "text", placeholder }) {
  return (
    <div className="space-y-1">
      {label && <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">{label}</label>}
      <div className="relative">
        {Icon && <Icon className="absolute left-4 top-3 w-5 h-5 text-text-muted-subtle dark:text-text-muted-dark" />}
        <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`w-full ${Icon ? 'pl-11' : 'px-4'} py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark placeholder:text-text-muted-subtle dark:placeholder:text-text-muted-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark`} />
      </div>
    </div>
  );
}

function PhoneInputField({ label, value, onChange, placeholder }) {
  return (
    <div className="space-y-1">
      {label && <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">{label}</label>}
      <PhoneInput
        international
        defaultCountry="DE"
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        flags={flags}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options = [] }) {
  const { t } = useAppTranslation();
  return (
    <div className="space-y-1">
      {label && <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">{label}</label>}
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.labelKey ? t(option.labelKey, option.defaultLabel || option.label || option.value) : option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function LockedOption({ message, children }) {
  const { t } = useAppTranslation();
  return (
    <div className="relative">
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-surface/80 dark:bg-card-dark/80 rounded-container border-thick border-dashed border-border dark:border-border-dark">
        <div className="bg-input-bg dark:bg-input-bg-dark rounded-container p-4 border border-border dark:border-border-dark shadow-lg max-w-sm mx-4">
          <div className="flex items-center gap-3 mb-2">
            <Lock className="w-5 h-5 text-text-muted dark:text-text-muted-dark" />
            <span className="text-sm font-semibold text-text-primary dark:text-text-secondary-dark">{t('common.locked.byOrganisation', 'Locked by Organisation')}</span>
          </div>
          <p className="text-xs text-text-secondary dark:text-text-muted-dark">{message}</p>
        </div>
      </div>
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 3, minHeightClass = 'min-h-[120px]' }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">{label}</label>
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={rows} className={`w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark resize-y ${minHeightClass}`} />
    </div>
  );
}

function Toggle({ label, description, checked, onChange }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">{label}</label>
          {description && <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">{description}</p>}
        </div>
        <button
          type="button"
          onClick={() => onChange(!checked)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:ring-offset-2 ${checked ? 'bg-action dark:bg-action-dark' : 'bg-border dark:bg-surface-dark'
            }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'
              }`}
          />
        </button>
      </div>
    </div>
  );
}

function ImageUpload({ label, image, onUpload, onRemove, isBanner, disabled = false }) {
  const { t } = useAppTranslation();
  return (
    <section>
      <h3 className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-3">{label}</h3>
      <div className={`relative ${isBanner ? 'w-full h-32' : 'w-24 h-24'} rounded-input bg-surface dark:bg-surface-dark border-thick border-dashed border-border dark:border-border-dark flex items-center justify-center overflow-hidden group ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-border-dark dark:hover:border-border-dark'} transition-colors`}>
        {image ? <img src={image} className="w-full h-full object-cover" alt={t('common.actions.upload', 'Upload')} /> : <div className="text-center text-text-muted-subtle dark:text-text-muted-dark pointer-events-none"><Upload className="w-6 h-6 mx-auto mb-1" /><span className="text-xs">{t('common.actions.upload', 'Upload')}</span></div>}
        <input type="file" accept="image/*" onChange={onUpload} disabled={disabled} className="absolute inset-0 opacity-0 cursor-pointer appearance-none bg-transparent focus:outline-none disabled:cursor-not-allowed" />
      </div>
      {image && !disabled && <button onClick={onRemove} className="mt-2 text-sm text-red-500 dark:text-red-400 font-medium hover:text-red-600 dark:hover:text-red-300">{t('common.actions.remove', 'Remove')}</button>}
    </section>
  );
}

function SocialIcon({ url, icon: Icon, label, themeColor }) {
  if (!url) return null;
  // Use theme color if available, otherwise default to indigo
  const hoverColor = themeColor?.buttonStyle || themeColor?.textStyle || '#4f46e5';
  return (
    <a href={url} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 group">
      <div className="w-full aspect-square rounded-card bg-surface dark:bg-surface-dark border border-border-subtle dark:border-border-dark flex items-center justify-center text-text-secondary dark:text-text-secondary-dark group-hover:scale-105 transition-transform group-hover:shadow-md group-hover:text-white" style={{ '--hover-bg': hoverColor }}>
        <style>{`.group:hover div { background-color: ${hoverColor}; border-color: ${hoverColor}; }`}</style>
        <Icon className="w-6 h-6" />
      </div>
      <span className="text-[10px] uppercase font-bold tracking-wider text-text-muted-subtle dark:text-text-muted-dark">{label}</span>
    </a>
  );
}

// Color Selector Component
function ColorSelector({ selectedColor, onSelect, label, showAuto = false, autoLabel = "Auto (complementary)" }) {
  const { t } = useAppTranslation();
  const colorGradients = {
    indigo: 'from-indigo-500 to-indigo-700',
    blue: 'from-blue-500 to-blue-700',
    rose: 'from-rose-500 to-rose-700',
    emerald: 'from-emerald-500 to-emerald-700',
    slate: 'from-slate-500 to-slate-700',
    purple: 'from-purple-500 to-purple-700',
    cyan: 'from-cyan-500 to-cyan-700',
    teal: 'from-teal-500 to-teal-700',
    orange: 'from-orange-500 to-orange-700',
    pink: 'from-pink-500 to-pink-700',
    violet: 'from-violet-500 to-violet-700',
    fuchsia: 'from-fuchsia-500 to-fuchsia-700',
    amber: 'from-amber-500 to-amber-700',
    lime: 'from-lime-500 to-lime-700',
    green: 'from-green-500 to-green-700',
    yellow: 'from-yellow-500 to-yellow-700',
    red: 'from-red-500 to-red-700'
  };

  return (
    <div>
      {label && <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{label}</label>}
      <div className="grid grid-cols-8 gap-2">
        {showAuto && (
          <button
            onClick={() => onSelect(null)}
            className={`w-10 h-10 rounded-full border-thick flex flex-col items-center justify-center text-[10px] font-medium transition-all ${selectedColor === null
              ? 'border-indigo-500 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 ring-2 ring-indigo-200 dark:ring-indigo-800'
              : 'border-border dark:border-border-dark bg-surface dark:bg-surface-dark text-text-secondary dark:text-text-secondary-dark hover:border-border-dark dark:hover:border-border-dark'
              }`}
          >
            <span>{t('common.auto', 'Auto')}</span>
          </button>
        )}
        {TAILWIND_COLORS.map(color => (
          <button
            key={color}
            onClick={() => onSelect(color)}
            className={`w-10 h-10 rounded-full border-thick transition-all overflow-hidden ${selectedColor === color
              ? 'border-indigo-500 dark:border-indigo-400 ring-2 ring-indigo-200 dark:ring-indigo-800 scale-105'
              : 'border-border dark:border-border-dark hover:border-border-dark dark:hover:border-border-dark'
              }`}
            title={color.charAt(0).toUpperCase() + color.slice(1)}
          >
            <div className={`w-full h-full bg-gradient-to-br ${colorGradients[color] || 'from-gray-500 to-gray-700'}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

function OrganisationManagementView({
  organisations,
  selectedOrganisationId,
  onSelectOrganisation,
  onCreateOrganisation,
  onDeleteOrganisation,
  onRefresh,
  onOpenUsers,
  onOpenSettings,
  onBack,
  showAlert
}) {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const safeBack = withSafeHandler(onBack, () => navigate('/people'), 'organisation-management-back');
  const hasInitialRefreshRunRef = useRef(false);
  const [newOrganisationName, setNewOrganisationName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    organisationId: '',
    organisationName: '',
    confirmationCode: '',
    input: '',
    isDeleting: false
  });

  useEffect(() => {
    if (hasInitialRefreshRunRef.current) return;
    hasInitialRefreshRunRef.current = true;
    if (typeof onRefresh === 'function') {
      onRefresh();
    }
  }, [onRefresh]);

  const handleCreate = async () => {
    const name = newOrganisationName.trim();
    if (!name) {
      showAlert?.(t('organizations.validation.nameRequired', 'Please enter an organisation name'), 'error');
      return;
    }
    setIsCreating(true);
    try {
      const created = await onCreateOrganisation(name);
      if (created) {
        setNewOrganisationName('');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const openDeleteDialog = (org) => {
    setDeleteDialog({
      isOpen: true,
      organisationId: org.id,
      organisationName: org.name,
      confirmationCode: `DELETE ${org.slug}`,
      input: '',
      isDeleting: false
    });
  };

  const closeDeleteDialog = (force = false) => {
    if (deleteDialog.isDeleting && !force) return;
    setDeleteDialog({
      isOpen: false,
      organisationId: '',
      organisationName: '',
      confirmationCode: '',
      input: '',
      isDeleting: false
    });
  };

  const confirmDeleteOrganisation = async () => {
    if ((deleteDialog.input || '').trim() !== deleteDialog.confirmationCode) {
      showAlert?.(t('organizations.validation.deleteCodeMismatch', 'Confirmation code does not match'), 'error');
      return;
    }
    setDeleteDialog((prev) => ({ ...prev, isDeleting: true }));
    const deleted = await onDeleteOrganisation?.(deleteDialog.organisationId, deleteDialog.input.trim());
    if (deleted) {
      closeDeleteDialog(true);
      return;
    }
    setDeleteDialog((prev) => ({ ...prev, isDeleting: false }));
  };

  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex flex-col">
      <div className="w-full bg-card dark:bg-card-dark border-b border-border dark:border-border-dark">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={safeBack} className="p-2 hover:bg-surface dark:hover:bg-surface-dark rounded-full text-text-muted dark:text-text-muted-dark">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-text-primary dark:text-text-primary-dark">{t('organizations.title', 'Organisation Management')}</h1>
              <p className="text-sm text-text-secondary dark:text-text-muted-dark">{t('organizations.subtitle', 'Manage multiple organizations and switch to user management.')}</p>
            </div>
          </div>
          <button onClick={onRefresh} className="px-4 py-2 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-full text-sm font-bold flex items-center gap-2 border border-border dark:border-border-dark">
            <RefreshCw className="w-4 h-4" /> {t('common.actions.update', 'Update')}
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="bg-card dark:bg-card-dark rounded-input border border-border dark:border-border-dark p-4">
          <h2 className="font-semibold text-text-primary dark:text-text-primary-dark mb-3">{t('organizations.newOrganisation', 'New Organisation')}</h2>
          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="text"
              value={newOrganisationName}
              onChange={(e) => setNewOrganisationName(e.target.value)}
              placeholder={t('organizations.newOrganisationPlaceholder', 'e.g. Sales North')}
              className="flex-1 px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
            />
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="px-4 py-2.5 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button font-bold disabled:opacity-50"
            >
              {isCreating ? t('common.states.creating', 'Creating...') : t('organizations.actions.create', 'Create Organization')}
            </button>
          </div>
        </div>

        <div className="bg-card dark:bg-card-dark rounded-input border border-border dark:border-border-dark overflow-hidden">
          <div className="p-4 border-b border-border dark:border-border-dark">
            <h2 className="font-semibold text-text-primary dark:text-text-primary-dark">{t('organizations.listTitle', 'Organisations')}</h2>
          </div>
          {organisations.length === 0 ? (
            <div className="p-6 text-text-muted dark:text-text-muted-dark">{t('organizations.empty', 'No organizations found.')}</div>
          ) : (
            <div className="divide-y divide-border dark:divide-border-dark">
              {organisations.map((org) => {
                const isSelected = selectedOrganisationId === org.id;
                return (
                  <div key={org.id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <div className="font-medium text-text-primary dark:text-text-primary-dark">{org.name}</div>
                      <div className="text-xs text-text-muted dark:text-text-muted-dark">
                        {t('organizations.meta.summary', '/{{slug}} • {{userCount}} users • {{ownerCount}} owners', {
                          slug: org.slug,
                          userCount: org.user_count,
                          ownerCount: org.owner_count
                        })}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          try {
                            await onSelectOrganisation(org.id);
                          } catch (e) {
                            showAlert?.(e.message || t('organizations.errors.activateFailed', 'Failed to activate organisation'), 'error');
                          }
                        }}
                        className={`px-3 py-1.5 text-sm rounded-button border ${isSelected ? 'bg-action dark:bg-action-dark text-white border-action dark:border-action-dark' : 'bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark border-border dark:border-border-dark'}`}
                      >
                        {isSelected ? t('organizations.actions.active', 'Active') : t('organizations.actions.activate', 'Select')}
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await onSelectOrganisation(org.id);
                            onOpenSettings?.();
                          } catch (e) {
                            showAlert?.(e.message || t('organizations.errors.activateFailed', 'Failed to activate organisation'), 'error');
                          }
                        }}
                        className="px-3 py-1.5 text-sm rounded-button bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark border border-border dark:border-border-dark"
                      >
                        {t('organizations.actions.settings', 'Settings')}
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await onSelectOrganisation(org.id);
                            onOpenUsers();
                          } catch (e) {
                            showAlert?.(e.message || t('organizations.errors.activateFailed', 'Failed to activate organisation'), 'error');
                          }
                        }}
                        className="px-3 py-1.5 text-sm rounded-button bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark"
                      >
                        {t('organizations.actions.openUsers', 'Open Users')}
                      </button>
                      <button
                        onClick={() => openDeleteDialog(org)}
                        className="px-3 py-1.5 text-sm rounded-button bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark border border-error-border dark:border-error-border-dark"
                      >
                        {t('common.actions.delete', 'Delete')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {deleteDialog.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-2">{t('organizations.delete.title', 'Delete organisation')}</h3>
            <p className="text-sm text-text-secondary dark:text-text-muted-dark mb-4">
              {t('organizations.delete.description', 'To prevent accidental deletion, please enter this confirmation code:')}
            </p>
            <div className="mb-4 px-3 py-2 rounded-input bg-surface dark:bg-surface-dark border border-border dark:border-border-dark font-mono text-sm text-text-primary dark:text-text-primary-dark">
              {deleteDialog.confirmationCode}
            </div>
            <p className="text-xs text-text-muted dark:text-text-muted-dark mb-3">
              {t('organizations.delete.organisationLabel', 'Organisation: {{name}}', { name: deleteDialog.organisationName })}
            </p>
            <input
              type="text"
              value={deleteDialog.input}
              onChange={(e) => setDeleteDialog((prev) => ({ ...prev, input: e.target.value }))}
              placeholder={t('organizations.delete.placeholder', 'Enter confirmation code')}
              className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
              autoFocus
            />
            <div className="flex gap-3 mt-6">
              <button
                onClick={closeDeleteDialog}
                disabled={deleteDialog.isDeleting}
                className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium disabled:opacity-50"
              >
                {t('common.actions.cancel', 'Cancel')}
              </button>
              <button
                onClick={confirmDeleteOrganisation}
                disabled={deleteDialog.isDeleting}
                className="flex-1 px-4 py-2.5 bg-error dark:bg-error-dark text-white rounded-button font-bold disabled:opacity-50"
              >
                {deleteDialog.isDeleting ? t('common.states.deleting', 'Deleting...') : t('common.actions.delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileManagementView({
  currentUserEmail,
  userRole,
  settings,
  userLanguage,
  onSaveLanguage,
  onBack,
  showAlert,
  onOpenChangePassword,
  onOpenForgotPassword
}) {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const safeBack = withSafeHandler(onBack, () => navigate('/people'), 'profile-management-back');
  const [languageDraft, setLanguageDraft] = useState(userLanguage || 'en');
  const [isSaving, setIsSaving] = useState(false);
  const roleLabel = userRole === 'owner'
    ? t('common.roles.owner', 'Owner')
    : userRole === 'admin'
      ? t('common.roles.admin', 'Admin')
      : userRole === 'department_lead'
        ? t('common.roles.departmentLead', 'Department Lead')
        : userRole === 'member'
          ? t('common.roles.member', 'Member')
          : '—';
  const organisationName = settings?.default_organisation || t('common.states.notSet', 'Not set');

  useEffect(() => {
    setLanguageDraft(userLanguage || 'en');
  }, [userLanguage]);

  const handleSaveLanguage = async () => {
    setIsSaving(true);
    try {
      await onSaveLanguage(languageDraft);
      showAlert?.(t('profile.alerts.languageSaved', 'Language saved'), 'success');
    } catch (e) {
      showAlert?.(e.message || t('profile.alerts.languageSaveFailed', 'Failed to save language'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex flex-col">
      <div className="w-full bg-card dark:bg-card-dark border-b border-border dark:border-border-dark">
        <div className="p-6 flex items-center gap-4">
          <button onClick={safeBack} className="p-2 hover:bg-surface dark:hover:bg-surface-dark rounded-full text-text-muted dark:text-text-muted-dark">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-text-primary dark:text-text-primary-dark">
              {t('profile.title', 'Profile & Account')}
            </h1>
            <p className="text-sm text-text-secondary dark:text-text-muted-dark">
              {t('profile.description', 'Manage your account settings')}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 md:p-6 w-full max-w-3xl mx-auto space-y-4">
        <div className="bg-card dark:bg-card-dark rounded-input shadow-sm border border-border dark:border-border-dark p-6">
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark mb-4">
            {t('profile.sections.user', 'User')}
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-card border border-border dark:border-border-dark bg-main dark:bg-main-dark px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-text-muted dark:text-text-muted-dark mb-2">
                {t('common.fields.email', 'Email')}
              </div>
              <div className="text-sm md:text-base font-medium text-text-primary dark:text-text-primary-dark break-all">
                {currentUserEmail || '—'}
              </div>
            </div>
            <div className="rounded-card border border-border dark:border-border-dark bg-main dark:bg-main-dark px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-text-muted dark:text-text-muted-dark mb-2">
                {t('common.fields.role', 'Role')}
              </div>
              <div className="text-sm md:text-base font-medium text-text-primary dark:text-text-primary-dark">
                {roleLabel}
              </div>
            </div>
            <div className="rounded-card border border-border dark:border-border-dark bg-main dark:bg-main-dark px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-text-muted dark:text-text-muted-dark mb-2">
                {t('common.fields.organisation', 'Organisation')}
              </div>
              <div className="text-sm md:text-base font-medium text-text-primary dark:text-text-primary-dark">
                {organisationName}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card dark:bg-card-dark rounded-input shadow-sm border border-border dark:border-border-dark p-6">
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark mb-4">
            {t('profile.sections.security', 'Security')}
          </h2>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={onOpenChangePassword}
              className="px-4 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button font-bold"
            >
              {t('profile.actions.changePassword', 'Change Password')}
            </button>
            <button
              onClick={onOpenForgotPassword}
              className="px-4 py-2 bg-surface dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium"
            >
              {t('profile.actions.forgotPassword', 'Forgot Password')}
            </button>
          </div>
        </div>

        <div className="bg-card dark:bg-card-dark rounded-input shadow-sm border border-border dark:border-border-dark p-6">
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark mb-4">
            {t('profile.sections.language', 'Language')}
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={languageDraft}
              onChange={(e) => setLanguageDraft(e.target.value === 'de' ? 'de' : 'en')}
              className="px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
            >
              <option value="en">{t('common.languages.en', 'English')}</option>
              <option value="de">{t('common.languages.de', 'Deutsch')}</option>
            </select>
            <button
              onClick={handleSaveLanguage}
              disabled={isSaving || languageDraft === userLanguage}
              className="px-4 py-2 bg-action dark:bg-action-dark text-white rounded-button font-bold disabled:opacity-50"
            >
              {isSaving
                ? t('common.status.saving', 'Saving...')
                : t('common.actions.save', 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserManagementView({ apiCall, userRole, userLanguage, selectedOrganisationId, organisations = [], onBack, onSelectOrganisation, showAlert, showConfirm, setChangePasswordModal }) {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const safeBack = withSafeHandler(onBack, () => navigate('/people'), 'user-management-back');
  const effectiveSelectedOrganisationId = selectedOrganisationId || localStorage.getItem('selectedOrganisationId') || '';
  const isAdmin = userRole === 'admin';
  const roleLabel = (role) => {
    if (role === 'owner') return t('common.roles.owner', 'Owner');
    if (role === 'department_lead') return t('common.roles.departmentLead', 'Department Lead');
    if (role === 'admin') return t('common.roles.admin', 'Admin');
    return t('common.roles.member', 'Member');
  };
  const [users, setUsers] = useState([]);
  const [globalUsers, setGlobalUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGlobalLoading, setIsGlobalLoading] = useState(true);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserDraft, setEditingUserDraft] = useState({ role: 'member', departmentId: '', organisationId: '', departments: [], loadingDepartments: false });
  const [currentUserId, setCurrentUserId] = useState(null);
  const [globalOrganisationFilter, setGlobalOrganisationFilter] = useState('all');
  const [moveUserModal, setMoveUserModal] = useState({
    isOpen: false,
    userId: null,
    userEmail: '',
    currentOrganisationName: '',
    currentOrganisationId: '',
    organisationId: '',
    role: 'member',
    departmentId: '',
    departments: [],
    loadingDepartments: false
  });
  const [copyUserModal, setCopyUserModal] = useState({
    isOpen: false,
    userId: null,
    userEmail: '',
    currentOrganisationName: '',
    currentOrganisationId: '',
    organisationId: '',
    role: 'member',
    departmentId: '',
    departments: [],
    loadingDepartments: false
  });
  const [renameCardIdModal, setRenameCardIdModal] = useState({
    isOpen: false,
    userId: null,
    userEmail: '',
    cards: [],
    selectedSlug: '',
    newShortCode: ''
  });

  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'member', departmentId: '' });
  const [newInvitation, setNewInvitation] = useState({ email: '', role: 'member' });
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!userRole) return;
    fetchCurrentUser();
    if (isAdmin) {
      fetchGlobalUsers();
    } else {
      setIsGlobalLoading(false);
    }

    if (userRole === 'admin' && !effectiveSelectedOrganisationId) {
      setUsers([]);
      setInvitations([]);
      setDepartments([]);
      setIsLoading(false);
      return;
    }

    fetchUsers();
    fetchInvitations();
    fetchDepartments();
  }, [userRole, effectiveSelectedOrganisationId]);

  const fetchCurrentUser = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/auth/me`);
      if (res.ok) {
        const userData = await res.json();
        setCurrentUserId(userData.id);
      }
    } catch (e) {
      console.error('Failed to fetch current user:', e);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/users`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      } else {
        if (showAlert) showAlert(t('users.alerts.failedToLoad', 'Failed to load users'), 'error');
      }
    } catch (e) {
      console.error('Failed to fetch users:', e);
      if (showAlert) showAlert(t('users.alerts.errorLoading', 'Error loading users'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGlobalUsers = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/users/all`);
      if (res.ok) {
        const data = await res.json();
        setGlobalUsers(Array.isArray(data) ? data : []);
      } else if (showAlert) {
        showAlert(t('users.alerts.failedToLoadGlobal', 'Failed to load users across organisations'), 'error');
      }
    } catch (e) {
      console.error('Failed to fetch all users:', e);
      if (showAlert) showAlert(t('users.alerts.errorLoadingGlobal', 'Error loading users across organisations'), 'error');
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/departments`);
      if (res.ok) {
        const data = await res.json();
        setDepartments(data.departments || []);
      }
    } catch (e) {
      console.error('Failed to fetch departments:', e);
    }
  };

  const fetchDepartmentsForOrganisation = async (organisationId) => {
    if (!organisationId) return [];
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/departments?organisationId=${encodeURIComponent(organisationId)}`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.departments || [];
    } catch (e) {
      console.error('Failed to fetch target organisation departments:', e);
      return [];
    }
  };

  const fetchInvitations = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/invitations`);
      if (res.ok) {
        const data = await res.json();
        setInvitations(data.invitations);
      }
    } catch (e) {
      console.error('Failed to fetch invitations:', e);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password) {
      if (showAlert) showAlert(t('users.alerts.emailPasswordRequired', 'Email and password are required'), 'error');
      return;
    }

    if (newUser.password.length < 8) {
      if (showAlert) showAlert(t('users.alerts.passwordTooShort', 'Password must be at least 8 characters long'), 'error');
      return;
    }

    setIsSaving(true);
    try {
      let payload = { ...newUser };
      if (!payload.departmentId || !String(payload.departmentId).trim()) {
        delete payload.departmentId;
      }
      if (payload.role === 'department_lead' && !payload.departmentId) {
        const assignedDepartmentId = await promptDepartmentAssignmentForLead();
        if (!assignedDepartmentId) {
          setIsSaving(false);
          return;
        }
        payload = { ...payload, departmentId: assignedDepartmentId };
      }
      const res = await apiCall(`${API_ENDPOINT}/admin/users`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 2000);
        setShowCreateUserModal(false);
        setNewUser({ email: '', password: '', role: 'member', departmentId: '' });
        fetchUsers();
        fetchDepartments();
      } else {
        // Try to parse error response
        let errorMessage = t('users.alerts.createFailed', 'Failed to create user');
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          console.error('User creation failed:', {
            status: res.status,
            statusText: res.statusText,
            error: errorData
          });
        } catch (parseError) {
          // Response is not JSON, use status text
          console.error('User creation failed - non-JSON response:', {
            status: res.status,
            statusText: res.statusText
          });
          errorMessage = t('users.alerts.createFailedWithStatus', 'Failed to create user: {{status}} {{statusText}}', {
            status: res.status,
            statusText: res.statusText
          });
        }
        if (showAlert) showAlert(errorMessage, 'error');
      }
    } catch (e) {
      console.error('Error creating user:', e);
      const errorMessage = e.message || t('users.alerts.createError', 'Error creating user. Please try again.');
      if (showAlert) showAlert(errorMessage, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const createDepartment = async (name, leadUserIds = []) => {
    const payload = {
      name,
      leadUserIds: Array.from(new Set((leadUserIds || []).filter(Boolean)))
    };
    const res = await apiCall(`${API_ENDPOINT}/admin/departments`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || t('users.alerts.createDepartmentFailed', 'Failed to create department'));
    }
    return data.departmentId;
  };

  const promptDepartmentAssignmentForLead = async () => {
    const options = departments
      .map((dept, index) => `${index + 1}: ${dept.name}`)
      .join('\n');
    const promptText = departments.length > 0
      ? t(
        'users.departmentLead.promptSelect',
        'Choose the department for the department lead:\n{{options}}\n\nInput:\n- Number for an existing department\n- "new:Name" to create a new department',
        { options }
      )
      : t(
        'users.departmentLead.promptCreateFirst',
        'There is no department yet.\nCreate a new one with "new:Name"'
      );
    const input = window.prompt(
      promptText,
      departments.length > 0 ? '1' : t('users.departmentLead.promptDefaultNew', 'new:Sales')
    );
    if (!input) {
      return null;
    }

    const trimmed = input.trim();
    if (!trimmed) {
      return null;
    }

    const lowered = trimmed.toLowerCase();
    if (lowered.startsWith('new:')) {
      const newName = trimmed.slice(4).trim();
      if (!newName) {
        showAlert?.(t('users.departmentLead.invalidName', 'Please enter a valid department name'), 'error');
        return null;
      }
      const departmentId = await createDepartment(newName);
      await fetchDepartments();
      return departmentId;
    }

    const selectedIndex = Number.parseInt(trimmed, 10) - 1;
    if (!Number.isNaN(selectedIndex) && departments[selectedIndex]) {
      return departments[selectedIndex].id;
    }

    showAlert?.(t('users.departmentLead.invalidSelection', 'Invalid department selection'), 'error');
    return null;
  };

  const handleSendInvitation = async () => {
    if (!newInvitation.email) {
      if (showAlert) showAlert(t('users.invitations.emailRequired', 'Email is required'), 'error');
      return;
    }

    setIsSaving(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/invitations`, {
        method: 'POST',
        body: JSON.stringify(newInvitation)
      });
      if (res.ok) {
        const data = await res.json();
        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 2000);
        setShowInviteModal(false);
        setNewInvitation({ email: '', role: 'member' });

        // Show warning if email failed
        if (data.status === 'failed' && showAlert) {
          showAlert(
            t(
              'users.invitations.emailFailedWarning',
              'Invitation created but email failed to send. You can retry from the invitations list below.'
            ),
            'warning'
          );
        }

        // Reload invitations list
        fetchInvitations();
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || t('users.invitations.sendFailed', 'Failed to send invitation'), 'error');
      }
    } catch (e) {
      if (showAlert) showAlert(t('users.invitations.sendError', 'Error sending invitation'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const startEditUser = async (user) => {
    let draftDepartments = departments;
    let loadingDepartments = false;
    if (isAdmin && user.organisationId) {
      loadingDepartments = true;
      setEditingUserId(user.id);
      setEditingUserDraft({
        role: user.role || 'member',
        departmentId: user.departmentId || '',
        organisationId: user.organisationId || '',
        departments: [],
        loadingDepartments: true
      });
      draftDepartments = await fetchDepartmentsForOrganisation(user.organisationId);
      loadingDepartments = false;
    }

    setEditingUserId(user.id);
    setEditingUserDraft({
      role: user.role || 'member',
      departmentId: user.departmentId || '',
      organisationId: user.organisationId || effectiveSelectedOrganisationId || '',
      departments: draftDepartments,
      loadingDepartments
    });
  };

  const cancelEditUser = () => {
    setEditingUserId(null);
    setEditingUserDraft({ role: 'member', departmentId: '', organisationId: '', departments: [], loadingDepartments: false });
  };

  const handleUpdateRole = async (userId, newRole, departmentId = undefined) => {
    try {
      let resolvedDepartmentId = departmentId;
      if (newRole === 'department_lead' && !resolvedDepartmentId) {
        resolvedDepartmentId = await promptDepartmentAssignmentForLead();
        if (!resolvedDepartmentId) {
          return;
        }
      }

      const payload = { role: newRole };
      if (resolvedDepartmentId !== undefined) {
        payload.departmentId = resolvedDepartmentId || null;
      }
      const res = await apiCall(`${API_ENDPOINT}/admin/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        if (showAlert) showAlert(t('users.alerts.updated', 'User updated successfully'), 'success');
        fetchUsers();
        if (isAdmin) {
          fetchGlobalUsers();
        }
        cancelEditUser();
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || t('users.alerts.updateFailed', 'Failed to update user'), 'error');
      }
    } catch (e) {
      if (showAlert) showAlert(t('users.alerts.updateError', 'Error updating user'), 'error');
    }
  };

  const handleRemoveUser = async (userId, userEmail) => {
    if (showConfirm) {
      showConfirm(
        t(
          'users.confirm.deleteUserMessage',
          'Are you sure you want to permanently delete {{email}}? This will delete the user and all their cards. This action cannot be undone.',
          { email: userEmail }
        ),
        async () => {
          try {
            const res = await apiCall(`${API_ENDPOINT}/admin/users/${userId}`, {
              method: 'DELETE'
            });
            if (res.ok) {
              if (showAlert) showAlert(t('users.alerts.deleted', 'User deleted successfully'), 'success');
              fetchUsers();
              if (isAdmin) {
                fetchGlobalUsers();
              }
            } else {
              const errorData = await res.json().catch(() => ({}));
              if (showAlert) showAlert(errorData.error || t('users.alerts.deleteFailed', 'Failed to delete user'), 'error');
            }
          } catch (e) {
            if (showAlert) showAlert(t('users.alerts.deleteError', 'Error deleting user'), 'error');
          }
        },
        t('users.confirm.deleteUserTitle', 'Delete User'),
        t('common.actions.delete', 'Delete'),
        t('common.actions.cancel', 'Cancel')
      );
    }
  };

  const handleRetryInvitation = async (invitationId) => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/invitations/${invitationId}/retry`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        if (showAlert) {
          showAlert(data.success
            ? t('users.invitations.retrySuccess', 'Invitation email sent successfully')
            : t('users.invitations.retryFailed', 'Failed to send invitation email'),
            data.success ? 'success' : 'error');
        }
        fetchInvitations();
      }
    } catch (e) {
      if (showAlert) showAlert(t('users.invitations.retryError', 'Error retrying invitation'), 'error');
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (showConfirm) {
      showConfirm(
        t('users.confirm.deleteInvitationMessage', 'Are you sure you want to delete this invitation?'),
        async () => {
          try {
            const res = await apiCall(`${API_ENDPOINT}/admin/invitations/${invitationId}`, {
              method: 'DELETE'
            });
            if (res.ok) {
              if (showAlert) showAlert(t('users.invitations.deleted', 'Invitation deleted'), 'success');
              fetchInvitations();
            }
          } catch (e) {
            if (showAlert) showAlert(t('users.invitations.deleteError', 'Error deleting invitation'), 'error');
          }
        },
        t('users.confirm.deleteInvitationTitle', 'Delete Invitation'),
        t('common.actions.delete', 'Delete'),
        t('common.actions.cancel', 'Cancel')
      );
    }
  };

  const handleActivateOrganisationContext = async (organisationId) => {
    if (!organisationId) return;
    try {
      if (onSelectOrganisation) {
        await onSelectOrganisation(organisationId);
      } else {
        localStorage.setItem('selectedOrganisationId', organisationId);
      }
      if (showAlert) showAlert(t('users.alerts.organisationContextActivated', 'Organisation context activated'), 'success');
    } catch (e) {
      console.error('Failed to activate organisation context:', e);
      if (showAlert) showAlert(t('users.alerts.organisationContextFailed', 'Failed to activate organisation context'), 'error');
    }
  };

  const openMoveUserModal = async (user) => {
    const availableOrganisations = organisations.filter((org) => org.id !== user.organisationId);
    const initialOrganisationId = availableOrganisations[0]?.id || '';
    setMoveUserModal({
      isOpen: true,
      userId: user.id,
      userEmail: user.email,
      currentOrganisationName: user.organisationName || '',
      currentOrganisationId: user.organisationId || '',
      organisationId: initialOrganisationId,
      role: user.role || 'member',
      departmentId: '',
      departments: [],
      loadingDepartments: Boolean(initialOrganisationId)
    });

    if (initialOrganisationId) {
      const targetDepartments = await fetchDepartmentsForOrganisation(initialOrganisationId);
      setMoveUserModal((prev) => ({
        ...prev,
        departments: targetDepartments,
        loadingDepartments: false
      }));
    }
  };

  const openCopyUserModal = async (user) => {
    const availableOrganisations = organisations.filter((org) => org.id !== user.organisationId);
    const initialOrganisationId = availableOrganisations[0]?.id || '';
    setCopyUserModal({
      isOpen: true,
      userId: user.id,
      userEmail: user.email,
      currentOrganisationName: user.organisationName || '',
      currentOrganisationId: user.organisationId || '',
      organisationId: initialOrganisationId,
      role: user.role || 'member',
      departmentId: '',
      departments: [],
      loadingDepartments: Boolean(initialOrganisationId)
    });

    if (initialOrganisationId) {
      const targetDepartments = await fetchDepartmentsForOrganisation(initialOrganisationId);
      setCopyUserModal((prev) => ({
        ...prev,
        departments: targetDepartments,
        loadingDepartments: false
      }));
    }
  };

  const handleMoveOrganisationChange = async (organisationId) => {
    setMoveUserModal((prev) => ({
      ...prev,
      organisationId,
      departmentId: '',
      departments: [],
      loadingDepartments: Boolean(organisationId)
    }));

    if (!organisationId) {
      setMoveUserModal((prev) => ({ ...prev, loadingDepartments: false }));
      return;
    }

    const targetDepartments = await fetchDepartmentsForOrganisation(organisationId);
    setMoveUserModal((prev) => ({
      ...prev,
      departments: targetDepartments,
      loadingDepartments: false
    }));
  };

  const handleCopyOrganisationChange = async (organisationId) => {
    setCopyUserModal((prev) => ({
      ...prev,
      organisationId,
      departmentId: '',
      departments: [],
      loadingDepartments: Boolean(organisationId)
    }));

    if (!organisationId) {
      setCopyUserModal((prev) => ({ ...prev, loadingDepartments: false }));
      return;
    }

    const targetDepartments = await fetchDepartmentsForOrganisation(organisationId);
    setCopyUserModal((prev) => ({
      ...prev,
      departments: targetDepartments,
      loadingDepartments: false
    }));
  };

  const handleMoveUser = async () => {
    if (!moveUserModal.userId || !moveUserModal.organisationId) {
      if (showAlert) showAlert(t('users.alerts.moveTargetRequired', 'Please select a target organisation'), 'error');
      return;
    }

    if (moveUserModal.role === 'department_lead' && !moveUserModal.departmentId) {
      if (showAlert) showAlert(t('users.alerts.moveDepartmentRequired', 'Please select a department for the department lead'), 'error');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        organisationId: moveUserModal.organisationId,
        role: moveUserModal.role
      };
      if (moveUserModal.role === 'department_lead') {
        payload.departmentId = moveUserModal.departmentId;
      }

      const res = await apiCall(`${API_ENDPOINT}/admin/users/${moveUserModal.userId}/move`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        if (showAlert) showAlert(t('users.alerts.userMoved', 'User moved successfully'), 'success');
        setMoveUserModal({
          isOpen: false,
          userId: null,
          userEmail: '',
          currentOrganisationName: '',
          currentOrganisationId: '',
          organisationId: '',
          role: 'member',
          departmentId: '',
          departments: [],
          loadingDepartments: false
        });
        fetchGlobalUsers();
        if (effectiveSelectedOrganisationId) {
          fetchUsers();
          fetchDepartments();
          fetchInvitations();
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || t('users.alerts.moveFailed', 'Failed to move user'), 'error');
      }
    } catch (e) {
      console.error('Failed to move user:', e);
      if (showAlert) showAlert(t('users.alerts.moveError', 'Error moving user'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyUser = async () => {
    if (!copyUserModal.userId || !copyUserModal.organisationId) {
      if (showAlert) showAlert(t('users.alerts.copyTargetRequired', 'Please select a target organisation'), 'error');
      return;
    }

    if (copyUserModal.role === 'department_lead' && !copyUserModal.departmentId) {
      if (showAlert) showAlert(t('users.alerts.copyDepartmentRequired', 'Please select a department for the department lead'), 'error');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        organisationId: copyUserModal.organisationId,
        role: copyUserModal.role
      };
      if (copyUserModal.role === 'department_lead') {
        payload.departmentId = copyUserModal.departmentId;
      }

      const res = await apiCall(`${API_ENDPOINT}/admin/users/${copyUserModal.userId}/copy`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (showAlert) {
          showAlert(
            t(
              'users.alerts.userCopied',
              'Profile copied successfully. New organisation copy uses login email {{email}} and new card IDs.',
              { email: data.email || t('common.fields.unknown', 'Unknown') }
            ),
            'success'
          );
        }
        setCopyUserModal({
          isOpen: false,
          userId: null,
          userEmail: '',
          currentOrganisationName: '',
          currentOrganisationId: '',
          organisationId: '',
          role: 'member',
          departmentId: '',
          departments: [],
          loadingDepartments: false
        });
        fetchGlobalUsers();
        if (effectiveSelectedOrganisationId) {
          fetchUsers();
          fetchDepartments();
          fetchInvitations();
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || t('users.alerts.copyFailed', 'Failed to copy profile to another organisation'), 'error');
      }
    } catch (e) {
      console.error('Failed to copy user:', e);
      if (showAlert) showAlert(t('users.alerts.copyError', 'Error copying profile to another organisation'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const openRenameCardIdModal = (user) => {
    const cards = Array.isArray(user.cards) ? user.cards.filter((card) => card.slug) : [];
    if (cards.length === 0) {
      if (showAlert) showAlert(t('users.alerts.noCard', 'This user has no card yet'), 'info');
      return;
    }

    setRenameCardIdModal({
      isOpen: true,
      userId: user.id,
      userEmail: user.email,
      cards,
      selectedSlug: cards[0].slug,
      newShortCode: cards[0].shortCode || ''
    });
  };

  const handleSelectedCardChange = (slug) => {
    const selected = renameCardIdModal.cards.find((card) => card.slug === slug);
    setRenameCardIdModal((prev) => ({
      ...prev,
      selectedSlug: slug,
      newShortCode: selected?.shortCode || ''
    }));
  };

  const handleRenameCardId = async () => {
    const shortCode = (renameCardIdModal.newShortCode || '').trim();
    if (!/^[a-zA-Z0-9]{7}$/.test(shortCode)) {
      if (showAlert) showAlert(t('users.alerts.cardIdLength', 'Card ID must be exactly 7 characters (letters/numbers)'), 'error');
      return;
    }

    const selectedCard = renameCardIdModal.cards.find((card) => card.slug === renameCardIdModal.selectedSlug);
    if (!selectedCard) {
      if (showAlert) showAlert(t('users.alerts.cardNotFound', 'Card could not be found'), 'error');
      return;
    }

    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/cards/${selectedCard.slug}/short-code`, {
        method: 'PATCH',
        body: JSON.stringify({
          userId: renameCardIdModal.userId,
          shortCode
        })
      });

      if (res.ok) {
        if (showAlert) showAlert(t('users.alerts.cardIdUpdated', 'Card ID updated successfully'), 'success');
        setRenameCardIdModal({
          isOpen: false,
          userId: null,
          userEmail: '',
          cards: [],
          selectedSlug: '',
          newShortCode: ''
        });
        fetchUsers();
        if (isAdmin) {
          fetchGlobalUsers();
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || t('users.alerts.cardIdUpdateFailed', 'Card ID could not be updated'), 'error');
      }
    } catch (e) {
      if (showAlert) showAlert(t('users.alerts.cardIdUpdateError', 'Error while updating card ID'), 'error');
    }
  };

  const getExportRows = () => {
    return users.map((user) => ({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      cards: Array.isArray(user.cardsShortIds) && user.cardsShortIds.length > 0
        ? user.cardsShortIds.join(', ')
        : ''
    }));
  };

  const escapeCsv = (value) => {
    const str = String(value ?? '');
    if (/[",\n]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const downloadFile = (content, fileName, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = () => {
    const rows = getExportRows();
    if (rows.length === 0) {
      if (showAlert) showAlert(t('users.alerts.noUsersToExport', 'No users to export'), 'info');
      return;
    }

    const headers = [
      t('users.export.headers.firstName', 'First Name'),
      t('users.export.headers.lastName', 'Last Name'),
      t('users.export.headers.email', 'Email'),
      t('users.export.headers.phone', 'Phone'),
      t('users.export.headers.cardsId', 'Cards ID')
    ];
    const lines = [
      headers.join(','),
      ...rows.map((row) => [
        escapeCsv(row.firstName),
        escapeCsv(row.lastName),
        escapeCsv(row.email),
        escapeCsv(row.phone),
        escapeCsv(row.cards)
      ].join(','))
    ];

    downloadFile(lines.join('\n'), 'users-overview.csv', 'text/csv;charset=utf-8;');
  };

  const handleExportExcel = () => {
    const rows = getExportRows();
    if (rows.length === 0) {
      if (showAlert) showAlert(t('users.alerts.noUsersToExport', 'No users to export'), 'info');
      return;
    }

    const exportData = rows.map((row) => ({
      [t('users.export.headers.firstName', 'First Name')]: row.firstName,
      [t('users.export.headers.lastName', 'Last Name')]: row.lastName,
      [t('users.export.headers.email', 'Email')]: row.email,
      [t('users.export.headers.phone', 'Phone')]: row.phone,
      [t('users.export.headers.cardsId', 'Cards ID')]: row.cards
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, t('users.export.sheetName', 'Users'));
    XLSX.writeFile(workbook, 'users-overview.xlsx');
  };

  const filteredGlobalUsers = globalUsers.filter((user) => {
    if (globalOrganisationFilter !== 'all' && user.organisationId !== globalOrganisationFilter) {
      return false;
    }
    return true;
  });
  const canManageOrganisationContext = !isAdmin || Boolean(effectiveSelectedOrganisationId);
  const currentOrganisationName = organisations.find((org) => org.id === effectiveSelectedOrganisationId)?.name || '';

  if (userRole !== 'owner' && userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark flex items-center justify-center p-6">
        <div className="bg-card dark:bg-card-dark rounded-card p-8 shadow-lg max-w-md w-full">
          <h2 className="text-xl font-bold text-text-primary dark:text-text-primary-dark mb-4">
            {t('users.accessDenied.title', 'Access Denied')}
          </h2>
          <p className="text-text-secondary dark:text-text-muted-dark mb-6">
            {t('users.accessDenied.description', "You don't have permission to manage users.")}
          </p>
          <button onClick={safeBack} className="px-4 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-full text-sm font-bold">
            {t('users.accessDenied.back', 'Back to Dashboard')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex flex-col">
      <div className="w-full bg-card dark:bg-card-dark border-b border-border dark:border-border-dark">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={safeBack} className="p-2 hover:bg-surface dark:hover:bg-surface-dark rounded-full text-text-muted dark:text-text-muted-dark">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-text-primary dark:text-text-primary-dark">{t('users.title', 'User Management')}</h1>
              <p className="text-sm text-text-secondary dark:text-text-muted-dark">{t('users.subtitle', 'Manage users and invitations for your organisation')}</p>
            </div>
          </div>
          <div className="flex gap-3">
            {canManageOrganisationContext && (
              <>
                <button
                  onClick={handleExportCsv}
                  className="px-4 py-2 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-full text-sm font-bold flex items-center gap-2 hover:bg-card dark:hover:bg-card-dark border border-border dark:border-border-dark"
                >
                  <Download className="w-4 h-4" /> {t('users.actions.exportCsv', 'Export CSV')}
                </button>
                <button
                  onClick={handleExportExcel}
                  className="px-4 py-2 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-full text-sm font-bold flex items-center gap-2 hover:bg-card dark:hover:bg-card-dark border border-border dark:border-border-dark"
                >
                  <FileText className="w-4 h-4" /> {t('users.actions.exportExcel', 'Export Excel')}
                </button>
                <button
                  onClick={() => setShowBulkImportModal(true)}
                  className="px-4 py-2 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-full text-sm font-bold flex items-center gap-2 hover:bg-card dark:hover:bg-card-dark border border-border dark:border-border-dark"
                >
                  <Upload className="w-4 h-4" /> {t('bulkImport.actions.open', 'Bulk Import')}
                </button>
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="px-4 py-2 bg-action dark:bg-action-dark text-white rounded-full text-sm font-bold flex items-center gap-2 hover:bg-action-hover dark:hover:bg-action-hover-dark"
                >
                  <Plus className="w-4 h-4" /> {t('users.actions.inviteUser', 'Invite User')}
                </button>
                <button
                  onClick={() => setShowCreateUserModal(true)}
                  className="px-4 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-full text-sm font-bold flex items-center gap-2 hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark"
                >
                  <Plus className="w-4 h-4" /> {t('users.actions.createUser', 'Create User')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 md:p-6 w-full">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="text-text-secondary dark:text-text-muted-dark">
              {t('users.status.loading', 'Loading users...')}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {isAdmin && (
              <div className="bg-card dark:bg-card-dark rounded-input shadow-sm border border-border dark:border-border-dark overflow-hidden">
                <div className="p-6 border-b border-border dark:border-border-dark flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark">
                      {t('users.sections.globalUsers.title', 'All users across organisations')}
                    </h2>
                    <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">
                      {t('users.sections.globalUsers.description', 'Admin-only overview across all organisations. Move users into another organisation without changing the existing org-based user management.')}
                    </p>
                  </div>
                  <div className="w-full lg:w-72">
                    <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">
                      {t('common.fields.organisation', 'Organisation')}
                    </label>
                    <select
                      value={globalOrganisationFilter}
                      onChange={(e) => setGlobalOrganisationFilter(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                    >
                      <option value="all">{t('users.filters.allOrganisations', 'All organisations')}</option>
                      {organisations.map((org) => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="overflow-x-auto overscroll-x-contain">
                  {isGlobalLoading ? (
                    <div className="p-6 text-center text-text-muted dark:text-text-muted-dark">
                      {t('users.status.loadingGlobal', 'Loading users across organisations...')}
                    </div>
                  ) : filteredGlobalUsers.length === 0 ? (
                    <div className="p-6 text-center text-text-muted dark:text-text-muted-dark">
                      {t('users.status.noGlobalUsers', 'No users found across organisations')}
                    </div>
                  ) : (
                    <>
                      <div className="lg:hidden divide-y divide-border dark:divide-border-dark">
                        {filteredGlobalUsers.map((user) => {
                          const cardsShortDisplay = Array.isArray(user.cardsShortIds) && user.cardsShortIds.length > 0
                            ? user.cardsShortIds.join(', ')
                            : '—';
                          const isCurrentUser = user.id === currentUserId;
                          return (
                            <div key={`global-${user.id}`} className="p-4 space-y-3">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="font-semibold text-text-primary dark:text-text-primary-dark break-words">
                                    {user.firstName || '—'} {user.lastName || '—'}
                                  </div>
                                  <div className="text-sm text-text-secondary dark:text-text-secondary-dark break-all">{user.email}</div>
                                  <div className="text-xs text-text-muted dark:text-text-muted-dark mt-1">
                                    {user.organisationName || t('common.fields.noOrganisation', 'No Organisation')}
                                  </div>
                                  {isCurrentUser && <div className="text-xs text-text-muted dark:text-text-muted-dark">{t('users.labels.currentUser', '(You)')}</div>}
                                </div>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark">
                                  {roleLabel(user.role)}
                                </span>
                              </div>
                              <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 text-xs">
                                <div>
                                  <div className="text-text-muted dark:text-text-muted-dark">{t('common.fields.phone', 'Phone')}</div>
                                  <div className="text-text-secondary dark:text-text-secondary-dark break-all">{user.phone || '—'}</div>
                                </div>
                                <div>
                                  <div className="text-text-muted dark:text-text-muted-dark">{t('users.labels.cardsId', 'Cards ID')}</div>
                                  <div className="font-mono text-text-secondary dark:text-text-secondary-dark break-all">{cardsShortDisplay}</div>
                                </div>
                              </div>
                              {!isCurrentUser && (
                                <div className="flex flex-wrap items-center gap-2">
                                  {editingUserId === user.id ? (
                                    <>
                                      <select
                                        value={editingUserDraft.role}
                                        onChange={(e) => setEditingUserDraft((prev) => ({ ...prev, role: e.target.value }))}
                                        className="px-3 py-1.5 text-sm rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                                      >
                                        <option value="member">{t('common.roles.member', 'Member')}</option>
                                        <option value="department_lead">{t('common.roles.departmentLead', 'Department Lead')}</option>
                                        <option value="admin">{t('common.roles.admin', 'Admin')}</option>
                                        <option value="owner">{t('common.roles.owner', 'Owner')}</option>
                                      </select>
                                      <select
                                        value={editingUserDraft.departmentId}
                                        onChange={(e) => setEditingUserDraft((prev) => ({ ...prev, departmentId: e.target.value }))}
                                        disabled={editingUserDraft.loadingDepartments}
                                        className="px-3 py-1.5 text-sm rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark disabled:opacity-60"
                                      >
                                        <option value="">{t('common.fields.noDepartment', 'No Department')}</option>
                                        {(editingUserDraft.departments || []).map((dept) => (
                                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                                        ))}
                                      </select>
                                      <button
                                        onClick={() => handleUpdateRole(user.id, editingUserDraft.role, editingUserDraft.departmentId)}
                                        className="px-3 py-1.5 text-sm bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button"
                                      >
                                        {t('common.actions.save', 'Save')}
                                      </button>
                                      <button
                                        onClick={cancelEditUser}
                                        className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark"
                                      >
                                        {t('common.actions.cancel', 'Cancel')}
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => startEditUser(user)}
                                        className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark"
                                      >
                                        {t('common.fields.role', 'Role')}
                                      </button>
                                      <button
                                        onClick={() => setChangePasswordModal({ isOpen: true, userId: user.id, isOwnerChangingUser: true })}
                                        className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark"
                                      >
                                        {t('common.fields.password', 'Password')}
                                      </button>
                                      <button
                                        onClick={() => openRenameCardIdModal(user)}
                                        disabled={!Array.isArray(user.cards) || user.cards.length === 0}
                                        className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark disabled:opacity-50"
                                      >
                                        {t('common.fields.cardId', 'Card ID')}
                                      </button>
                                      <button
                                        onClick={() => openCopyUserModal(user)}
                                        className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark"
                                      >
                                        {t('users.actions.copyUser', 'Copy profile')}
                                      </button>
                                      <button
                                        onClick={() => handleActivateOrganisationContext(user.organisationId)}
                                        className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark"
                                      >
                                        {t('users.actions.activateOrganisationContext', 'Use organisation context')}
                                      </button>
                                      <button
                                        onClick={() => openMoveUserModal(user)}
                                        className="px-3 py-1.5 text-sm bg-action dark:bg-action-dark text-white rounded-button hover:bg-action-hover dark:hover:bg-action-hover-dark"
                                      >
                                        {t('users.actions.moveUser', 'Move user')}
                                      </button>
                                      <button
                                        onClick={() => handleRemoveUser(user.id, user.email)}
                                        className="px-3 py-1.5 text-sm bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark rounded-badge hover:bg-error-bg dark:hover:bg-error-bg-dark"
                                      >
                                        {t('common.actions.remove', 'Remove')}
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <table className="hidden lg:table w-full min-w-[1240px] text-sm">
                        <thead className="bg-surface dark:bg-surface-dark/60 border-b border-border dark:border-border-dark">
                          <tr>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[140px]">{t('common.fields.firstName', 'First Name')}</th>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[160px]">{t('common.fields.lastName', 'Last Name')}</th>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[260px]">{t('common.fields.email', 'Email')}</th>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[220px]">{t('common.fields.organisation', 'Organisation')}</th>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[160px]">{t('common.fields.department', 'Department')}</th>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[130px]">{t('users.labels.cardsId', 'Cards ID')}</th>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[120px]">{t('common.fields.role', 'Role')}</th>
                            <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[280px]">{t('common.fields.actions', 'Actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredGlobalUsers.map((user) => {
                            const isCurrentUser = user.id === currentUserId;
                            const cardsShortDisplay = Array.isArray(user.cardsShortIds) && user.cardsShortIds.length > 0
                              ? user.cardsShortIds.join(', ')
                              : '—';
                            return (
                              <tr key={`global-row-${user.id}`} className="border-b border-border dark:border-border-dark hover:bg-surface dark:hover:bg-surface-dark/40">
                                <td className="px-4 py-3 text-text-secondary dark:text-text-secondary-dark">{user.firstName || '—'}</td>
                                <td className="px-4 py-3 text-text-secondary dark:text-text-secondary-dark">{user.lastName || '—'}</td>
                                <td className="px-4 py-3 text-text-primary dark:text-text-primary-dark">
                                  <div className="font-medium">{user.email}</div>
                                  {isCurrentUser && <div className="text-xs text-text-muted dark:text-text-muted-dark">{t('users.labels.currentUser', '(You)')}</div>}
                                </td>
                                <td className="px-4 py-3 text-text-secondary dark:text-text-secondary-dark">{user.organisationName || '—'}</td>
                                <td className="px-4 py-3 text-text-secondary dark:text-text-secondary-dark">{user.departmentName || '—'}</td>
                                <td className="px-4 py-3 font-mono text-xs text-text-muted dark:text-text-muted-dark">{cardsShortDisplay}</td>
                                <td className="px-4 py-3 text-text-secondary dark:text-text-secondary-dark">{roleLabel(user.role)}</td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {isCurrentUser ? (
                                    <span className="text-xs text-text-muted dark:text-text-muted-dark italic">{t('users.labels.cannotModifySelf', 'Cannot modify yourself')}</span>
                                  ) : editingUserId === user.id ? (
                                    <div className="flex items-center gap-2">
                                      <select
                                        value={editingUserDraft.role}
                                        onChange={(e) => setEditingUserDraft((prev) => ({ ...prev, role: e.target.value }))}
                                        className="px-3 py-1.5 text-sm rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                                      >
                                        <option value="member">{t('common.roles.member', 'Member')}</option>
                                        <option value="department_lead">{t('common.roles.departmentLead', 'Department Lead')}</option>
                                        <option value="admin">{t('common.roles.admin', 'Admin')}</option>
                                        <option value="owner">{t('common.roles.owner', 'Owner')}</option>
                                      </select>
                                      <select
                                        value={editingUserDraft.departmentId}
                                        onChange={(e) => setEditingUserDraft((prev) => ({ ...prev, departmentId: e.target.value }))}
                                        disabled={editingUserDraft.loadingDepartments}
                                        className="px-3 py-1.5 text-sm rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark disabled:opacity-60"
                                      >
                                        <option value="">{t('common.fields.noDepartment', 'No Department')}</option>
                                        {(editingUserDraft.departments || []).map((dept) => (
                                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                                        ))}
                                      </select>
                                      <button
                                        onClick={() => handleUpdateRole(user.id, editingUserDraft.role, editingUserDraft.departmentId)}
                                        className="px-3 py-1.5 text-sm bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button"
                                      >
                                        {t('common.actions.save', 'Save')}
                                      </button>
                                      <button
                                        onClick={cancelEditUser}
                                        className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark"
                                      >
                                        {t('common.actions.cancel', 'Cancel')}
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => startEditUser(user)}
                                        className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark"
                                      >
                                        {t('common.fields.role', 'Role')}
                                      </button>
                                      <button
                                        onClick={() => setChangePasswordModal({ isOpen: true, userId: user.id, isOwnerChangingUser: true })}
                                        className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark"
                                      >
                                        {t('common.fields.password', 'Password')}
                                      </button>
                                      <button
                                        onClick={() => openRenameCardIdModal(user)}
                                        disabled={!Array.isArray(user.cards) || user.cards.length === 0}
                                        className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark disabled:opacity-50"
                                      >
                                        {t('common.fields.cardId', 'Card ID')}
                                      </button>
                                      <button
                                        onClick={() => openCopyUserModal(user)}
                                        className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark"
                                      >
                                        {t('users.actions.copyUser', 'Copy profile')}
                                      </button>
                                      <button
                                        onClick={() => handleActivateOrganisationContext(user.organisationId)}
                                        className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark"
                                      >
                                        {t('users.actions.activateOrganisationContext', 'Use organisation context')}
                                      </button>
                                      <button
                                        onClick={() => openMoveUserModal(user)}
                                        className="px-3 py-1.5 text-sm bg-action dark:bg-action-dark text-white rounded-button hover:bg-action-hover dark:hover:bg-action-hover-dark"
                                      >
                                        {t('users.actions.moveUser', 'Move user')}
                                      </button>
                                      <button
                                        onClick={() => handleRemoveUser(user.id, user.email)}
                                        className="px-3 py-1.5 text-sm bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark rounded-badge hover:bg-error-bg dark:hover:bg-error-bg-dark"
                                      >
                                        {t('common.actions.remove', 'Remove')}
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              </div>
            )}

            {isAdmin && !effectiveSelectedOrganisationId && (
              <div className="bg-card dark:bg-card-dark rounded-input shadow-sm border border-border dark:border-border-dark overflow-hidden">
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark">
                    {t('users.organisationRequired.title', 'Select an organisation')}
                  </h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">
                    {t('users.organisationRequired.description', 'First, select an organisation in the organisation management.')}
                  </p>
                  <div className="flex gap-3 mt-4">
                    <button onClick={() => navigate('/organizations')} className="px-4 py-2 bg-action dark:bg-action-dark text-white rounded-full text-sm font-bold">
                      {t('users.organisationRequired.primaryAction', 'Organisations')}
                    </button>
                    <button onClick={safeBack} className="px-4 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-full text-sm font-bold">
                      {t('common.actions.back', 'Back')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {(!isAdmin || effectiveSelectedOrganisationId) && (
              <>
            <div className="bg-card dark:bg-card-dark rounded-input shadow-sm border border-border dark:border-border-dark overflow-hidden">
              <div className="p-6 border-b border-border dark:border-border-dark">
                <h2 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark">
                  {t('users.sections.organisationUsers.title', 'Organisation Users')}
                </h2>
                <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">
                  {t('users.sections.organisationUsers.description', 'Users currently in your organisation')}
                </p>
              </div>
              <div className="overflow-x-auto overscroll-x-contain">
                {users.length === 0 ? (
                  <div className="p-6 text-center text-text-muted dark:text-text-muted-dark">
                    {t('users.status.noUsers', 'No users found')}
                  </div>
                ) : (
                  <>
                    <div className="lg:hidden divide-y divide-border dark:divide-border-dark">
                      {users.map((user) => {
                        const isCurrentUser = user.id === currentUserId;
                        const cardsShortDisplay = Array.isArray(user.cardsShortIds) && user.cardsShortIds.length > 0
                          ? user.cardsShortIds.join(', ')
                          : '—';

                        return (
                          <div key={user.id} className="p-4 space-y-3">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="font-semibold text-text-primary dark:text-text-primary-dark break-words">{user.firstName || '—'} {user.lastName || '—'}</div>
                                <div className="text-sm text-text-secondary dark:text-text-secondary-dark break-all">{user.email}</div>
                                {isCurrentUser && <div className="text-xs text-text-muted dark:text-text-muted-dark">{t('users.labels.currentUser', '(You)')}</div>}
                              </div>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium ${user.role === 'owner'
                                ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                                : 'bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark'
                                }`}>
                                {roleLabel(user.role)}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 text-xs">
                              <div>
                                <div className="text-text-muted dark:text-text-muted-dark">{t('common.fields.phone', 'Phone')}</div>
                                <div className="text-text-secondary dark:text-text-secondary-dark break-all">{user.phone || '—'}</div>
                              </div>
                              <div>
                                <div className="text-text-muted dark:text-text-muted-dark">{t('users.labels.cardsId', 'Cards ID')}</div>
                                <div className="font-mono text-text-secondary dark:text-text-secondary-dark break-all">{cardsShortDisplay}</div>
                                {Array.isArray(user.cards) && user.cards.length > 0 && (
                                  <button
                                    onClick={() => openRenameCardIdModal(user)}
                                    className="mt-2 px-2 py-1 text-[11px] bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark border border-border dark:border-border-dark"
                                  >
                                    {t('users.actions.renameCardId', 'Rename Card ID')}
                                  </button>
                                )}
                              </div>
                            </div>

                            <div>
                              {isCurrentUser ? (
                                <span className="text-xs text-text-muted dark:text-text-muted-dark italic">
                                  {t('users.labels.cannotModifySelf', 'Cannot modify yourself')}
                                </span>
                              ) : editingUserId === user.id ? (
                                <div className="flex flex-wrap items-center gap-2">
                                  <select
                                    value={editingUserDraft.role}
                                    onChange={(e) => setEditingUserDraft((prev) => ({ ...prev, role: e.target.value }))}
                                    className="px-3 py-1.5 text-sm rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                                  >
                                    <option value="member">{t('common.roles.member', 'Member')}</option>
                                    <option value="department_lead">{t('common.roles.departmentLead', 'Department Lead')}</option>
                                    <option value="admin">{t('common.roles.admin', 'Admin')}</option>
                                    <option value="owner">{t('common.roles.owner', 'Owner')}</option>
                                  </select>
                                  <select
                                    value={editingUserDraft.departmentId}
                                    onChange={(e) => setEditingUserDraft((prev) => ({ ...prev, departmentId: e.target.value }))}
                                    className="px-3 py-1.5 text-sm rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                                  >
                                    <option value="">{t('common.fields.noDepartment', 'No Department')}</option>
                                    {departments.map((dept) => (
                                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={() => handleUpdateRole(user.id, editingUserDraft.role, editingUserDraft.departmentId)}
                                    className="px-3 py-1.5 text-sm bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button"
                                  >
                                    {t('common.actions.save', 'Save')}
                                  </button>
                                  <button
                                    onClick={cancelEditUser}
                                    className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark"
                                  >
                                    {t('common.actions.cancel', 'Cancel')}
                                  </button>
                                </div>
                              ) : (
                                <div className="flex flex-wrap items-center gap-2">
                                  <button
                                    onClick={() => startEditUser(user)}
                                    className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark flex items-center gap-1"
                                  >
                                    <Edit3 className="w-3 h-3" /> {t('common.fields.role', 'Role')}
                                  </button>
                                  <button
                                    onClick={() => setChangePasswordModal({ isOpen: true, userId: user.id, isOwnerChangingUser: true })}
                                    className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark flex items-center gap-1"
                                  >
                                    <Lock className="w-3 h-3" /> {t('common.fields.password', 'Password')}
                                  </button>
                                  <button
                                    onClick={() => openRenameCardIdModal(user)}
                                    className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark flex items-center gap-1"
                                  >
                                    <Edit3 className="w-3 h-3" /> {t('common.fields.cardId', 'Card ID')}
                                  </button>
                                  <button
                                    onClick={() => handleRemoveUser(user.id, user.email)}
                                    className="px-3 py-1.5 text-sm bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark rounded-badge hover:bg-error-bg dark:hover:bg-error-bg-dark flex items-center gap-1"
                                  >
                                    <Trash2 className="w-3 h-3" /> {t('common.actions.remove', 'Remove')}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <table className="hidden lg:table w-full min-w-[1140px] text-sm">
                    <thead className="bg-surface dark:bg-surface-dark/60 border-b border-border dark:border-border-dark">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[140px]">{t('common.fields.firstName', 'First Name')}</th>
                        <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[160px]">{t('common.fields.lastName', 'Last Name')}</th>
                        <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[300px]">{t('common.fields.email', 'Email')}</th>
                        <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[190px]">{t('common.fields.phone', 'Phone')}</th>
                        <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[150px]">{t('users.labels.cardsId', 'Cards ID')}</th>
                        <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[120px]">{t('common.fields.role', 'Role')}</th>
                        <th className="text-left px-4 py-3 font-semibold text-text-primary dark:text-text-primary-dark min-w-[320px]">{t('common.fields.actions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => {
                        const isCurrentUser = user.id === currentUserId;
                        const cardsShortDisplay = Array.isArray(user.cardsShortIds) && user.cardsShortIds.length > 0
                          ? user.cardsShortIds.join(', ')
                          : '—';

                        return (
                          <tr key={user.id} className="border-b border-border dark:border-border-dark hover:bg-surface dark:hover:bg-surface-dark/40">
                            <td className="px-4 py-3 text-text-secondary dark:text-text-secondary-dark">{user.firstName || '—'}</td>
                            <td className="px-4 py-3 text-text-secondary dark:text-text-secondary-dark">{user.lastName || '—'}</td>
                            <td className="px-4 py-3 text-text-primary dark:text-text-primary-dark">
                              <div className="font-medium">{user.email}</div>
                              {isCurrentUser && <div className="text-xs text-text-muted dark:text-text-muted-dark">{t('users.labels.currentUser', '(You)')}</div>}
                            </td>
                            <td className="px-4 py-3 text-text-secondary dark:text-text-secondary-dark">{user.phone || '—'}</td>
                            <td className="px-4 py-3 font-mono text-xs text-text-muted dark:text-text-muted-dark">{cardsShortDisplay}</td>
                            <td className="px-4 py-3 text-text-secondary dark:text-text-secondary-dark">
                              {roleLabel(user.role)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {isCurrentUser ? (
                                <span className="text-xs text-text-muted dark:text-text-muted-dark italic">{t('users.labels.cannotModifySelf', 'Cannot modify yourself')}</span>
                              ) : editingUserId === user.id ? (
                                <div className="flex items-center gap-2 whitespace-nowrap">
                                  <select
                                    value={editingUserDraft.role}
                                    onChange={(e) => setEditingUserDraft((prev) => ({ ...prev, role: e.target.value }))}
                                    className="px-3 py-1.5 text-sm rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                                  >
                                    <option value="member">{t('common.roles.member', 'Member')}</option>
                                    <option value="department_lead">{t('common.roles.departmentLead', 'Department Lead')}</option>
                                    <option value="admin">{t('common.roles.admin', 'Admin')}</option>
                                    <option value="owner">{t('common.roles.owner', 'Owner')}</option>
                                  </select>
                                  <select
                                    value={editingUserDraft.departmentId}
                                    onChange={(e) => setEditingUserDraft((prev) => ({ ...prev, departmentId: e.target.value }))}
                                    className="px-3 py-1.5 text-sm rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                                  >
                                    <option value="">{t('common.fields.noDepartment', 'No Department')}</option>
                                    {departments.map((dept) => (
                                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={() => handleUpdateRole(user.id, editingUserDraft.role, editingUserDraft.departmentId)}
                                    className="px-3 py-1.5 text-sm bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button"
                                  >
                                    {t('common.actions.save', 'Save')}
                                  </button>
                                  <button
                                    onClick={cancelEditUser}
                                    className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark"
                                  >
                                    {t('common.actions.cancel', 'Cancel')}
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 whitespace-nowrap">
                                  <button
                                    onClick={() => startEditUser(user)}
                                    className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark flex items-center gap-1"
                                  >
                                    <Edit3 className="w-3 h-3" /> {t('common.fields.role', 'Role')}
                                  </button>
                                  <button
                                    onClick={() => setChangePasswordModal({ isOpen: true, userId: user.id, isOwnerChangingUser: true })}
                                    className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark flex items-center gap-1"
                                  >
                                    <Lock className="w-3 h-3" /> {t('common.fields.password', 'Password')}
                                  </button>
                                  <button
                                    onClick={() => openRenameCardIdModal(user)}
                                    disabled={!Array.isArray(user.cards) || user.cards.length === 0}
                                    className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark flex items-center gap-1 disabled:opacity-50"
                                  >
                                    <Edit3 className="w-3 h-3" /> {t('common.fields.cardId', 'Card ID')}
                                  </button>
                                  <button
                                    onClick={() => handleRemoveUser(user.id, user.email)}
                                    className="px-3 py-1.5 text-sm bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark rounded-badge hover:bg-error-bg dark:hover:bg-error-bg-dark flex items-center gap-1"
                                  >
                                    <Trash2 className="w-3 h-3" /> {t('common.actions.remove', 'Remove')}
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>

            <div className="bg-card dark:bg-card-dark rounded-input shadow-sm border border-border dark:border-border-dark overflow-hidden">
              <div className="p-6 border-b border-border dark:border-border-dark">
                <h2 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark">{t('users.sections.departments.title', 'Departments')}</h2>
                <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">{t('users.sections.departments.description', 'Department management has moved to its own workspace.')}</p>
              </div>
              <div className="p-6">
                <button
                  onClick={() => navigate('/departments')}
                  className="px-4 py-2 bg-action dark:bg-action-dark text-white rounded-button font-bold"
                >
                  {t('users.sections.departments.primaryAction', 'Open department management')}
                </button>
              </div>
            </div>

            {/* Pending Invitations Section */}
            <div className="bg-card dark:bg-card-dark rounded-input shadow-sm border border-border dark:border-border-dark overflow-hidden">
              <div className="p-6 border-b border-border dark:border-border-dark">
                <h2 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark">{t('users.sections.invitations.title', 'Pending Invitations')}</h2>
                <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">{t('users.sections.invitations.description', 'Invitations sent but not yet accepted')}</p>
              </div>
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {invitations.filter(inv => inv.status !== 'accepted' && !inv.accepted_at).length === 0 ? (
                  <div className="p-6 text-center text-text-muted dark:text-text-muted-dark">
                    {t('users.sections.invitations.empty', 'No pending invitations')}
                  </div>
                ) : (
                  invitations.filter(inv => inv.status !== 'accepted' && !inv.accepted_at).map(inv => (
                    <div key={inv.id} className="p-6 flex items-center justify-between hover:bg-surface dark:hover:bg-surface-dark/50 transition-colors">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div>
                            <div className="font-medium text-text-primary dark:text-text-primary-dark">
                              {inv.email}
                            </div>
                            <div className="text-sm text-text-muted dark:text-text-muted-dark flex items-center gap-2 mt-1">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium ${inv.status === 'sent' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                                inv.status === 'failed' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                                  inv.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                                    inv.status === 'expired' ? 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300' :
                                      'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                }`}>
                                {inv.status === 'sent' && t('common.status.sent', 'Sent')}
                                {inv.status === 'failed' && t('common.status.failed', 'Failed')}
                                {inv.status === 'pending' && t('common.status.pending', 'Pending')}
                                {inv.status === 'expired' && t('common.status.expired', 'Expired')}
                              </span>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium ${inv.role === 'owner'
                                ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                                : inv.role === 'admin'
                                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                                  : inv.role === 'department_lead'
                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                    : 'bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark'
                                }`}>
                                {inv.role === 'owner'
                                  ? t('common.roles.owner', 'Owner')
                                  : inv.role === 'admin'
                                    ? t('common.roles.admin', 'Admin')
                                    : inv.role === 'department_lead'
                                      ? t('common.roles.departmentLead', 'Department Lead')
                                      : t('common.roles.member', 'Member')}
                              </span>
                              {inv.invited_by_email && (
                                <span className="text-xs">
                                  {t('users.invitations.invitedBy', 'invited by {{email}}', { email: inv.invited_by_email })}
                                </span>
                              )}
                              <span className="text-xs">• {new Date(inv.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {(inv.status === 'failed' || inv.status === 'pending') && (
                          <button
                            onClick={() => handleRetryInvitation(inv.id)}
                            className="px-3 py-1.5 text-sm bg-action dark:bg-action-dark text-white rounded-button hover:bg-action-hover dark:hover:bg-action-hover-dark flex items-center gap-1"
                            title={t('users.invitations.retryTitle', 'Retry sending email')}
                          >
                            <RefreshCw className="w-3 h-3" /> {t('common.actions.retry', 'Retry')}
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteInvitation(inv.id)}
                          className="px-3 py-1.5 text-sm bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark rounded-badge hover:bg-error-bg dark:hover:bg-error-bg-dark flex items-center gap-1"
                          title={t('users.invitations.deleteTitle', 'Delete invitation')}
                        >
                          <Trash2 className="w-3 h-3" /> {t('common.actions.delete', 'Delete')}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">
              {t('users.modals.createUser.title', 'Create New User')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.email', 'Email')}</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                  placeholder={t('users.placeholders.email', 'user@example.com')}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.password', 'Password')}</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                  placeholder={t('users.placeholders.password', 'Minimum 8 characters')}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.role', 'Role')}</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                >
                  <option value="member">{t('common.roles.member', 'Member')}</option>
                  <option value="department_lead">{t('common.roles.departmentLead', 'Department Lead')}</option>
                  <option value="admin">{t('common.roles.admin', 'Admin')}</option>
                  <option value="owner">{t('common.roles.owner', 'Owner')}</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.department', 'Department')}</label>
                <select
                  value={newUser.departmentId || ''}
                  onChange={(e) => setNewUser({ ...newUser, departmentId: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                >
                  <option value="">{t('common.fields.noDepartment', 'No Department')}</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateUserModal(false);
                  setNewUser({ email: '', password: '', role: 'member', departmentId: '' });
                }}
                className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
              >
                {t('common.actions.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleCreateUser}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button font-bold hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : isSuccess ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isSaving
                  ? t('users.actions.creatingUser', 'Creating...')
                  : t('users.actions.createUser', 'Create User')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {renameCardIdModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-2">
              {t('users.modals.renameCardId.title', 'Rename Card ID')}
            </h3>
            <p className="text-sm text-text-secondary dark:text-text-muted-dark mb-4 break-all">
              {t('users.modals.renameCardId.userLabel', 'User: {{email}}', { email: renameCardIdModal.userEmail })}
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">
                  {t('users.modals.renameCardId.selectCard', 'Select card')}
                </label>
                <select
                  value={renameCardIdModal.selectedSlug}
                  onChange={(e) => handleSelectedCardChange(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                >
                  {renameCardIdModal.cards.map((card) => (
                    <option key={card.slug} value={card.slug}>
                      {card.slug} ({card.shortCode || t('users.modals.renameCardId.withoutId', 'without ID')})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">
                  {t('users.modals.renameCardId.newLabel', 'New Card ID (7 characters)')}
                </label>
                <input
                  type="text"
                  maxLength={7}
                  value={renameCardIdModal.newShortCode}
                  onChange={(e) => setRenameCardIdModal((prev) => ({ ...prev, newShortCode: e.target.value.trim() }))}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark font-mono"
                  placeholder={t('users.modals.renameCardId.placeholder', 'e.g. a1B2c3D')}
                />
                <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">
                  {t('users.modals.renameCardId.helper', 'Letters and numbers only, exactly 7 characters.')}
                </p>
              </div>
              {renameCardIdModal.newShortCode && (
                <p className="text-xs text-text-secondary dark:text-text-muted-dark break-all">
                  {t('users.modals.renameCardId.newShortLink', 'New short link: /{{shortCode}}', {
                    shortCode: renameCardIdModal.newShortCode
                  })}
                </p>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setRenameCardIdModal({ isOpen: false, userId: null, userEmail: '', cards: [], selectedSlug: '', newShortCode: '' })}
                className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
              >
                {t('common.actions.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleRenameCardId}
                className="flex-1 px-4 py-2.5 bg-action dark:bg-action-dark text-white rounded-button font-bold hover:bg-action-hover dark:hover:bg-action-hover-dark"
              >
                {t('common.actions.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">
              {t('users.modals.inviteUser.title', 'Invite User')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.email', 'Email')}</label>
                <input
                  type="email"
                  value={newInvitation.email}
                  onChange={(e) => setNewInvitation({ ...newInvitation, email: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                  placeholder={t('users.placeholders.email', 'user@example.com')}
                />
                <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">
                  {t('users.modals.inviteUser.emailHelp', 'An invitation email will be sent to this address')}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.role', 'Role')}</label>
                <select
                  value={newInvitation.role}
                  onChange={(e) => setNewInvitation({ ...newInvitation, role: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                >
                  <option value="member">{t('common.roles.member', 'Member')}</option>
                  <option value="department_lead">{t('common.roles.departmentLead', 'Department Lead')}</option>
                  <option value="admin">{t('common.roles.admin', 'Admin')}</option>
                  <option value="owner">{t('common.roles.owner', 'Owner')}</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setNewInvitation({ email: '', role: 'member' });
                }}
                className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
              >
                {t('common.actions.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleSendInvitation}
                disabled={isSaving || isSuccess}
                className={`flex-1 px-4 py-2.5 rounded-button font-bold flex items-center justify-center gap-2 transition-all ${isSuccess
                  ? 'bg-green-500 dark:bg-green-600 text-white hover:bg-green-600 dark:hover:bg-green-700'
                  : 'bg-action dark:bg-action-dark text-white hover:bg-action-hover dark:hover:bg-action-hover-dark disabled:opacity-50'
                  }`}
              >
                {isSaving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    {t('users.actions.sendingInvitation', 'Sending...')}
                  </>
                ) : isSuccess ? (
                  <>
                    <Check className="w-4 h-4" />
                    {t('common.status.sentShort', 'Sent!')}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {t('users.actions.sendInvitation', 'Send Invitation')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {moveUserModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-2">
              {t('users.modals.moveUser.title', 'Move user to another organisation')}
            </h3>
            <p className="text-sm text-text-secondary dark:text-text-muted-dark mb-4 break-all">
              {t('users.modals.moveUser.userLabel', 'User: {{email}}', { email: moveUserModal.userEmail })}
            </p>
            <p className="text-xs text-text-muted dark:text-text-muted-dark mb-4">
              {t('users.modals.moveUser.currentOrganisation', 'Current organisation: {{organisation}}', {
                organisation: moveUserModal.currentOrganisationName || t('common.fields.unknown', 'Unknown')
              })}
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.organisation', 'Organisation')}</label>
                <select
                  value={moveUserModal.organisationId}
                  onChange={(e) => handleMoveOrganisationChange(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                >
                  <option value="">{t('users.modals.moveUser.selectOrganisation', 'Select target organisation')}</option>
                  {organisations.filter((org) => org.id !== moveUserModal.currentOrganisationId).map((org) => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.role', 'Role')}</label>
                <select
                  value={moveUserModal.role}
                  onChange={(e) => setMoveUserModal((prev) => ({ ...prev, role: e.target.value, departmentId: e.target.value === 'department_lead' ? prev.departmentId : '' }))}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                >
                  <option value="member">{t('common.roles.member', 'Member')}</option>
                  <option value="department_lead">{t('common.roles.departmentLead', 'Department Lead')}</option>
                  <option value="admin">{t('common.roles.admin', 'Admin')}</option>
                  <option value="owner">{t('common.roles.owner', 'Owner')}</option>
                </select>
              </div>
              {moveUserModal.role === 'department_lead' && (
                <div>
                  <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.department', 'Department')}</label>
                  <select
                    value={moveUserModal.departmentId}
                    onChange={(e) => setMoveUserModal((prev) => ({ ...prev, departmentId: e.target.value }))}
                    disabled={moveUserModal.loadingDepartments}
                    className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark disabled:opacity-60"
                  >
                    <option value="">{moveUserModal.loadingDepartments ? t('common.states.loading', 'Loading...') : t('users.modals.moveUser.selectDepartment', 'Select department')}</option>
                    {moveUserModal.departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setMoveUserModal({ isOpen: false, userId: null, userEmail: '', currentOrganisationName: '', currentOrganisationId: '', organisationId: '', role: 'member', departmentId: '', departments: [], loadingDepartments: false })}
                className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
              >
                {t('common.actions.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleMoveUser}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 bg-action dark:bg-action-dark text-white rounded-button font-bold hover:bg-action-hover dark:hover:bg-action-hover-dark disabled:opacity-50"
              >
                {isSaving ? t('common.states.saving', 'Saving...') : t('users.actions.moveUser', 'Move user')}
              </button>
            </div>
          </div>
        </div>
      )}

      {copyUserModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-2">
              {t('users.modals.copyUser.title', 'Copy profile to another organisation')}
            </h3>
            <p className="text-sm text-text-secondary dark:text-text-muted-dark mb-4 break-all">
              {t('users.modals.copyUser.userLabel', 'Source user: {{email}}', { email: copyUserModal.userEmail })}
            </p>
            <p className="text-xs text-text-muted dark:text-text-muted-dark mb-4">
              {t('users.modals.copyUser.currentOrganisation', 'Source organisation: {{organisation}}', {
                organisation: copyUserModal.currentOrganisationName || t('common.fields.unknown', 'Unknown')
              })}
            </p>
            <p className="text-xs text-text-muted dark:text-text-muted-dark mb-4">
              {t('users.modals.copyUser.helper', 'A new organisation-specific user copy will be created. New card IDs will be generated automatically and can be edited afterwards.')}
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.organisation', 'Organisation')}</label>
                <select
                  value={copyUserModal.organisationId}
                  onChange={(e) => handleCopyOrganisationChange(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                >
                  <option value="">{t('users.modals.copyUser.selectOrganisation', 'Select target organisation')}</option>
                  {organisations.filter((org) => org.id !== copyUserModal.currentOrganisationId).map((org) => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.role', 'Role')}</label>
                <select
                  value={copyUserModal.role}
                  onChange={(e) => setCopyUserModal((prev) => ({ ...prev, role: e.target.value, departmentId: e.target.value === 'department_lead' ? prev.departmentId : '' }))}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                >
                  <option value="member">{t('common.roles.member', 'Member')}</option>
                  <option value="department_lead">{t('common.roles.departmentLead', 'Department Lead')}</option>
                  <option value="admin">{t('common.roles.admin', 'Admin')}</option>
                  <option value="owner">{t('common.roles.owner', 'Owner')}</option>
                </select>
              </div>
              {copyUserModal.role === 'department_lead' && (
                <div>
                  <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.fields.department', 'Department')}</label>
                  <select
                    value={copyUserModal.departmentId}
                    onChange={(e) => setCopyUserModal((prev) => ({ ...prev, departmentId: e.target.value }))}
                    disabled={copyUserModal.loadingDepartments}
                    className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark disabled:opacity-60"
                  >
                    <option value="">{copyUserModal.loadingDepartments ? t('common.states.loading', 'Loading...') : t('users.modals.copyUser.selectDepartment', 'Select department')}</option>
                    {copyUserModal.departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setCopyUserModal({ isOpen: false, userId: null, userEmail: '', currentOrganisationName: '', currentOrganisationId: '', organisationId: '', role: 'member', departmentId: '', departments: [], loadingDepartments: false })}
                className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
              >
                {t('common.actions.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleCopyUser}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 bg-action dark:bg-action-dark text-white rounded-button font-bold hover:bg-action-hover dark:hover:bg-action-hover-dark disabled:opacity-50"
              >
                {isSaving ? t('common.states.saving', 'Saving...') : t('users.actions.copyUser', 'Copy profile')}
              </button>
            </div>
          </div>
        </div>
      )}

      <BulkUserImport
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        apiCall={apiCall}
        organisationId={effectiveSelectedOrganisationId}
        organisationName={currentOrganisationName}
        showAlert={showAlert}
        onImported={() => {
          fetchUsers();
          fetchInvitations();
          fetchDepartments();
          if (isAdmin) {
            fetchGlobalUsers();
          }
        }}
      />
    </div>
  );
}

function DepartmentManagementView({ apiCall, userRole, selectedOrganisationId, onBack, showAlert, showConfirm }) {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const safeBack = withSafeHandler(onBack, () => navigate('/people'), 'department-management-back');
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [departmentFilterOpen, setDepartmentFilterOpen] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all');
  const [addDialog, setAddDialog] = useState({ isOpen: false, departmentId: '', role: 'member', userIds: [] });
  const [createDepartmentDialog, setCreateDepartmentDialog] = useState({ isOpen: false, name: '' });
  const filterRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (departments.length === 0) return;
    setSelectedDepartmentIds((prev) => {
      if (prev.length === 0) {
        return departments.map((d) => d.id);
      }
      const validIds = prev.filter((id) => departments.some((d) => d.id === id));
      return validIds.length > 0 ? validIds : departments.map((d) => d.id);
    });
  }, [departments]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setDepartmentFilterOpen(false);
      }
    };
    if (departmentFilterOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [departmentFilterOpen]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [departmentsRes, usersRes] = await Promise.all([
        apiCall(`${API_ENDPOINT}/admin/departments`),
        apiCall(`${API_ENDPOINT}/admin/users`)
      ]);

      if (departmentsRes.ok) {
        const depData = await departmentsRes.json();
        setDepartments(depData.departments || []);
      }
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData || []);
      }
    } catch (e) {
      showAlert?.(t('departments.errors.loadFailed', 'Failed to load department data'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const patchUser = async (userId, role, departmentId) => {
    const res = await apiCall(`${API_ENDPOINT}/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role, departmentId })
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || t('departments.errors.userUpdateFailed', 'User could not be updated'));
    }
  };

  const patchDepartmentLeads = async (departmentId, leadUserIds) => {
    const res = await apiCall(`${API_ENDPOINT}/admin/departments/${departmentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ leadUserIds: Array.from(new Set((leadUserIds || []).filter(Boolean))) })
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || t('departments.errors.leadsUpdateFailed', 'Leads could not be updated'));
    }
  };

  const openAddPersonDialog = () => {
    setAddDialog({
      isOpen: true,
      departmentId: selectedDepartmentIds[0] || departments[0]?.id || '',
      role: 'member',
      userIds: []
    });
  };

  const handleCreateDepartment = async () => {
    const name = createDepartmentDialog.name.trim();
    if (!name) {
      showAlert?.(t('departments.errors.nameRequired', 'Please enter a department name'), 'error');
      return;
    }
    setIsSaving(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/departments`, {
        method: 'POST',
        body: JSON.stringify({ name, leadUserIds: [] })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || t('departments.errors.createFailed', 'Department could not be created'));
      }
      const createdDepartmentId = data.departmentId || null;
      setCreateDepartmentDialog({ isOpen: false, name: '' });
      showAlert?.(t('departments.alerts.created', 'Department created'), 'success');
      await fetchData();
      if (createdDepartmentId) {
        setSelectedDepartmentIds((prev) => Array.from(new Set([...prev, createdDepartmentId])));
      }
    } catch (e) {
      showAlert?.(e.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const getPersonInitials = (user) => {
    const first = (user.firstName || '').trim();
    const last = (user.lastName || '').trim();
    if (first || last) {
      return `${first.slice(0, 1)}${last.slice(0, 1)}`.toUpperCase() || 'U';
    }
    return (user.email || 'U').slice(0, 2).toUpperCase();
  };

  const handleAddPersons = async () => {
    const departmentId = addDialog.departmentId;
    const selectedUserIds = addDialog.userIds || [];
    const targetRole = addDialog.role === 'department_lead' ? 'department_lead' : 'member';
    const department = departments.find((d) => d.id === departmentId);

    if (!department) {
      showAlert?.(t('departments.errors.selectDepartment', 'Please select a department'), 'error');
      return;
    }
    if (selectedUserIds.length === 0) {
      showAlert?.(t('departments.errors.selectPeople', 'Please select at least one person'), 'error');
      return;
    }

    setIsSaving(true);
    try {
      let addedCount = 0;
      const nextLeadIds = new Set(department.leadUserIds || []);
      for (const userId of selectedUserIds) {
        const user = users.find((u) => u.id === userId);
        if (!user || user.role === 'owner' || user.role === 'admin') {
          continue;
        }
        await patchUser(user.id, targetRole, department.id);
        if (targetRole === 'department_lead') {
          nextLeadIds.add(user.id);
        }
        addedCount += 1;
      }

      if (targetRole === 'department_lead') {
        await patchDepartmentLeads(department.id, Array.from(nextLeadIds));
      }

      setAddDialog({ isOpen: false, departmentId: '', role: 'member', userIds: [] });
      showAlert?.(
        addedCount > 0
          ? t('departments.alerts.peopleAdded', '{{count}} people added', { count: addedCount })
          : t('departments.alerts.noneAdded', 'No people added'),
        addedCount > 0 ? 'success' : 'info'
      );
      await fetchData();
    } catch (e) {
      showAlert?.(e.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveMember = (department, user) => {
    const executeRemove = async () => {
      setIsSaving(true);
      try {
        const existingLeadIds = Array.from(new Set(department.leadUserIds || []));
        if (existingLeadIds.includes(user.id)) {
          await patchDepartmentLeads(department.id, existingLeadIds.filter((id) => id !== user.id));
        }
        const nextRole = user.role === 'department_lead' ? 'member' : user.role;
        await patchUser(user.id, nextRole, null);
        showAlert?.(t('departments.alerts.memberRemoved', 'Employee removed from department'), 'success');
        await fetchData();
      } catch (e) {
        showAlert?.(e.message, 'error');
      } finally {
        setIsSaving(false);
      }
    };

    if (showConfirm) {
      showConfirm(
        t('departments.dialogs.removeMember.message', 'Remove {{email}} from {{department}}?', { email: user.email, department: department.name }),
        executeRemove,
        t('departments.dialogs.removeMember.title', 'Remove employee'),
        t('departments.dialogs.removeMember.confirmAction', 'Remove'),
        t('common.actions.cancel', 'Cancel')
      );
      return;
    }
    executeRemove();
  };

  const handleRemoveLead = (department, user) => {
    const executeRemove = async () => {
      setIsSaving(true);
      try {
        const nextLeadIds = (department.leadUserIds || []).filter((id) => id !== user.id);
        await patchDepartmentLeads(department.id, nextLeadIds);
        showAlert?.(t('departments.alerts.leadRemoved', 'Lead removed'), 'success');
        await fetchData();
      } catch (e) {
        showAlert?.(e.message, 'error');
      } finally {
        setIsSaving(false);
      }
    };

    if (showConfirm) {
      showConfirm(
        t('departments.dialogs.removeLead.message', 'Remove {{email}} as lead from {{department}}?', { email: user.email, department: department.name }),
        executeRemove,
        t('departments.dialogs.removeLead.title', 'Remove lead'),
        t('departments.dialogs.removeLead.confirmAction', 'Remove'),
        t('common.actions.cancel', 'Cancel')
      );
      return;
    }
    executeRemove();
  };

  const handlePromoteToLead = async (department, user) => {
    setIsSaving(true);
    try {
      await patchUser(user.id, 'department_lead', department.id);
      await patchDepartmentLeads(department.id, [...(department.leadUserIds || []), user.id]);
      await fetchData();
      showAlert?.(t('departments.alerts.leadAdded', 'Lead added'), 'success');
    } catch (e) {
      showAlert?.(e.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDepartment = (department) => {
    const executeDelete = async () => {
      setIsSaving(true);
      try {
        const res = await apiCall(`${API_ENDPOINT}/admin/departments/${department.id}`, {
          method: 'DELETE'
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || t('departments.errors.deleteFailed', 'Failed to delete department'));
        }
        setSelectedDepartmentIds((prev) => prev.filter((id) => id !== department.id));
        showAlert?.(t('departments.alerts.deleted', 'Department deleted'), 'success');
        await fetchData();
      } catch (e) {
        showAlert?.(e.message, 'error');
      } finally {
        setIsSaving(false);
      }
    };

    if (showConfirm) {
      showConfirm(
        t('departments.dialogs.delete.confirmMessage', 'Delete department "{{name}}"?', { name: department.name }),
        executeDelete,
        t('departments.dialogs.delete.title', 'Delete department'),
        t('departments.dialogs.delete.confirmAction', 'Delete'),
        t('departments.dialogs.delete.cancelAction', 'Cancel')
      );
      return;
    }

    executeDelete();
  };

  if (userRole !== 'owner' && userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark flex items-center justify-center p-6">
        <div className="bg-card dark:bg-card-dark rounded-card p-8 shadow-lg max-w-md w-full">
          <h2 className="text-xl font-bold text-text-primary dark:text-text-primary-dark mb-4">{t('departments.accessDeniedTitle', 'Access Denied')}</h2>
          <p className="text-text-secondary dark:text-text-muted-dark mb-6">{t('departments.accessDeniedMessage', "You don't have permission to manage departments.")}</p>
          <button onClick={safeBack} className="px-4 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-full text-sm font-bold">
            {t('departments.backToDashboard', 'Back to Dashboard')}
          </button>
        </div>
      </div>
    );
  }

  if (userRole === 'admin' && !selectedOrganisationId) {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark flex items-center justify-center p-6">
        <div className="bg-card dark:bg-card-dark rounded-card p-8 shadow-lg max-w-md w-full">
          <h2 className="text-xl font-bold text-text-primary dark:text-text-primary-dark mb-4">{t('departments.selectOrganisationTitle', 'Select organisation')}</h2>
          <p className="text-text-secondary dark:text-text-muted-dark mb-6">{t('departments.selectOrganisationMessage', 'Choose an organisation first in organisation management.')}</p>
          <button onClick={safeBack} className="px-4 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-full text-sm font-bold">
            {t('common.actions.back', 'Back')}
          </button>
        </div>
      </div>
    );
  }

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const visibleDepartments = departments
    .filter((dept) => selectedDepartmentIds.includes(dept.id))
    .map((department) => {
      const people = users
        .filter((u) => u.departmentId === department.id)
        .filter((u) => {
          const isLead = (department.leadUserIds || []).includes(u.id);
          if (roleFilter === 'leads' && !isLead) return false;
          if (roleFilter === 'members' && isLead) return false;
          if (!normalizedSearch) return true;
          const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
          const email = (u.email || '').toLowerCase();
          return fullName.includes(normalizedSearch) || email.includes(normalizedSearch);
        })
        .sort((a, b) => {
          const aLead = (department.leadUserIds || []).includes(a.id) ? 1 : 0;
          const bLead = (department.leadUserIds || []).includes(b.id) ? 1 : 0;
          if (aLead !== bLead) return bLead - aLead;
          const aName = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || '';
          const bName = `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.email || '';
          return aName.localeCompare(bName, 'de', { sensitivity: 'base' });
        });

      return { department, people };
    })
    .filter((entry) => {
      if (!normalizedSearch) return true;
      return entry.people.length > 0;
    });

  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex flex-col">
      <div className="flex-1 p-4 md:p-8 w-full max-w-7xl mx-auto">
        {isLoading ? (
          <div className="text-center py-12 text-text-secondary dark:text-text-muted-dark">{t('departments.loading', 'Loading departments...')}</div>
        ) : departments.length === 0 ? (
          <div className="bg-card dark:bg-card-dark rounded-input border border-border dark:border-border-dark p-6 text-text-secondary dark:text-text-muted-dark">
            {t('departments.empty', 'No departments available yet. Create a department first in user management.')}
          </div>
        ) : (
          <div className="space-y-8">
            <div className="flex items-center">
              <button
                onClick={safeBack}
                className="px-4 py-2 rounded-button border border-border dark:border-border-dark bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark font-medium"
              >
                {t('departments.actions.backToPeople', '← Back to People')}
              </button>
            </div>
            <div className="bg-card dark:bg-card-dark rounded-card border border-border dark:border-border-dark p-4 md:p-5 flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px_auto] gap-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('departments.searchPlaceholder', 'Search name or email...')}
                  className="w-full px-4 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                />
                <div className="relative" ref={filterRef}>
                  <button
                    onClick={() => setDepartmentFilterOpen((prev) => !prev)}
                    className="w-full px-4 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark flex items-center justify-between"
                  >
                    <span>{t('departments.filters.departments', 'Departments')}</span>
                    <span className="text-sm text-text-secondary dark:text-text-secondary-dark">▼</span>
                  </button>
                  {departmentFilterOpen && (
                    <div className="absolute z-30 mt-2 w-full bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-input shadow-lg p-2 max-h-72 overflow-auto">
                      {departments.map((dept) => (
                        <label key={dept.id} className="flex items-center gap-2 px-2 py-2 rounded hover:bg-surface dark:hover:bg-surface-dark">
                          <input
                            type="checkbox"
                            checked={selectedDepartmentIds.includes(dept.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDepartmentIds((prev) => Array.from(new Set([...prev, dept.id])));
                              } else {
                                setSelectedDepartmentIds((prev) => {
                                  const next = prev.filter((id) => id !== dept.id);
                                  return next.length > 0 ? next : prev;
                                });
                              }
                            }}
                          />
                          <span className="text-sm text-text-primary dark:text-text-primary-dark">{dept.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div className="inline-flex items-center gap-1 rounded-input bg-surface dark:bg-surface-dark p-1">
                  {[
                    { key: 'all', label: t('departments.filters.all', 'All') },
                    { key: 'leads', label: t('departments.filters.leads', 'Leads') },
                    { key: 'members', label: t('departments.filters.members', 'Employees') }
                  ].map((f) => (
                    <button
                      key={f.key}
                      onClick={() => setRoleFilter(f.key)}
                      className={`px-3 py-2 rounded-button text-sm font-semibold ${roleFilter === f.key ? 'bg-card dark:bg-card-dark text-action dark:text-action-dark shadow-sm' : 'text-text-secondary dark:text-text-secondary-dark'}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setCreateDepartmentDialog({ isOpen: true, name: '' })}
                  className="px-5 py-3 rounded-button border border-border dark:border-border-dark bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark font-bold whitespace-nowrap"
                >
                  {t('departments.actions.addDepartment', '+ Department')}
                </button>
                <button
                  onClick={openAddPersonDialog}
                  className="px-6 py-3 rounded-button bg-action dark:bg-action-dark text-white font-bold whitespace-nowrap"
                >
                  {t('departments.actions.addPerson', '+ Add person')}
                </button>
              </div>
            </div>

            {visibleDepartments.map(({ department, people }) => {
              return (
                <div key={department.id} className="bg-card dark:bg-card-dark rounded-card border border-border dark:border-border-dark overflow-hidden">
                  <div className="px-5 py-4 bg-surface dark:bg-surface-dark border-b border-border dark:border-border-dark flex items-center justify-between gap-3">
                    <h2 className="font-bold text-xl text-text-primary dark:text-text-primary-dark">{department.name}</h2>
                    <button
                      onClick={() => handleDeleteDepartment(department)}
                      disabled={isSaving}
                      className="px-3 py-1.5 rounded-button text-xs font-semibold bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark disabled:opacity-50"
                    >
                      {t('departments.actions.deleteDepartment', 'Delete department')}
                    </button>
                  </div>
                  <div className="divide-y divide-border dark:divide-border-dark">
                    {people.length === 0 ? (
                      <div className="px-5 py-5 text-sm text-text-muted dark:text-text-muted-dark">
                        {t('departments.empty.matches', 'No matching people in this department.')}
                      </div>
                    ) : (
                      people.map((u) => {
                        const isLead = (department.leadUserIds || []).includes(u.id);
                        const displayName = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
                        return (
                          <div key={u.id} className="px-5 py-3.5 flex items-center gap-3">
                            <div className="w-11 h-11 rounded-xl bg-surface dark:bg-surface-dark border border-border dark:border-border-dark flex items-center justify-center font-bold text-base text-text-secondary dark:text-text-secondary-dark shrink-0">
                              {getPersonInitials(u)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-3 flex-wrap">
                                <span className="font-semibold text-lg leading-tight text-text-primary dark:text-text-primary-dark">{displayName}</span>
                                {isLead && (
                                  <span className="px-3 py-1 rounded-full text-xs font-bold tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                    {t('departments.badges.lead', 'LEAD')}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-text-muted dark:text-text-muted-dark truncate">{u.email}</div>
                            </div>
                            <div className="hidden md:flex items-center gap-2">
                              {isLead ? (
                                <button
                                  onClick={() => handleRemoveLead(department, u)}
                                  className="px-2 py-1 text-xs rounded-badge bg-surface dark:bg-surface-dark border border-border dark:border-border-dark"
                                  disabled={isSaving}
                                >
                                  {t('departments.actions.removeLead', 'Remove lead')}
                                </button>
                              ) : (
                                <button
                                  onClick={() => handlePromoteToLead(department, u)}
                                  className="px-2 py-1 text-xs rounded-badge bg-surface dark:bg-surface-dark border border-border dark:border-border-dark"
                                  disabled={isSaving}
                                >
                                  {t('departments.actions.makeLead', 'Set as lead')}
                                </button>
                              )}
                              <button
                                onClick={() => handleRemoveMember(department, u)}
                                className="px-2 py-1 text-xs rounded-badge bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark"
                                disabled={isSaving}
                              >
                                {t('departments.actions.remove', 'Remove')}
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
            {visibleDepartments.length === 0 && (
              <div className="bg-card dark:bg-card-dark rounded-card border border-border dark:border-border-dark p-6 text-text-secondary dark:text-text-muted-dark">
                {t('departments.empty.filtered', 'No departments or people match the current filter.')}
              </div>
            )}
          </div>
        )}
      </div>

      {addDialog.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">{t('departments.dialogs.addPeople.title', 'Add people')}</h3>
            <div className="space-y-3 mb-4">
              <select
                value={addDialog.departmentId}
                onChange={(e) => setAddDialog((prev) => ({ ...prev, departmentId: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark"
              >
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
              <select
                value={addDialog.role}
                onChange={(e) => setAddDialog((prev) => ({ ...prev, role: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark"
              >
                <option value="member">{t('departments.dialogs.addPeople.role.member', 'Employee')}</option>
                <option value="department_lead">{t('departments.dialogs.addPeople.role.lead', 'Lead')}</option>
              </select>
            </div>
            <div className="max-h-72 overflow-auto border border-border dark:border-border-dark rounded-input p-2 space-y-1">
              {users.length === 0 ? (
                <div className="text-sm text-text-muted dark:text-text-muted-dark p-2">{t('departments.dialogs.addPeople.emptyUsers', 'No users found.')}</div>
              ) : (
                users.map((u) => {
                  const isDisabled = u.role === 'owner' || u.role === 'admin';
                  return (
                    <label key={u.id} className={`flex items-center gap-2 px-2 py-1 rounded ${isDisabled ? 'opacity-60' : 'hover:bg-surface dark:hover:bg-surface-dark'}`}>
                      <input
                        type="checkbox"
                        checked={addDialog.userIds.includes(u.id)}
                        disabled={isDisabled}
                        onChange={(e) => setAddDialog((prev) => ({
                          ...prev,
                          userIds: e.target.checked
                            ? [...prev.userIds, u.id]
                            : prev.userIds.filter((id) => id !== u.id)
                        }))}
                      />
                      <span className="text-sm text-text-primary dark:text-text-primary-dark break-all">{u.email}</span>
                      {(u.role === 'owner' || u.role === 'admin') ? (
                        <span className="text-xs text-text-muted dark:text-text-muted-dark">{t('departments.dialogs.addPeople.unassignable', '(cannot be assigned)')}</span>
                      ) : null}
                    </label>
                  );
                })
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setAddDialog({ isOpen: false, departmentId: '', role: 'member', userIds: [] })}
                className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium"
              >
                {t('common.actions.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleAddPersons}
                disabled={isSaving || addDialog.userIds.length === 0}
                className="flex-1 px-4 py-2.5 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button font-bold disabled:opacity-50"
              >
                {t('common.actions.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {createDepartmentDialog.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">{t('departments.dialogs.createDepartment.title', 'Add department')}</h3>
            <input
              type="text"
              value={createDepartmentDialog.name}
              onChange={(e) => setCreateDepartmentDialog((prev) => ({ ...prev, name: e.target.value }))}
              placeholder={t('departments.dialogs.createDepartment.placeholder', 'e.g. Marketing')}
              className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
            />
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setCreateDepartmentDialog({ isOpen: false, name: '' })}
                className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium"
              >
                {t('common.actions.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleCreateDepartment}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button font-bold disabled:opacity-50"
              >
                {t('common.actions.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InvitationAcceptance({ apiCall, showAlert, API_ENDPOINT }) {
  const { t } = useAppTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [hasAccepted, setHasAccepted] = useState(false);

  // Fetch invitation details on mount
  useEffect(() => {
    // Don't fetch if invitation has already been accepted
    if (hasAccepted) return;

    const fetchInvitation = async () => {
      try {
        const res = await apiCall(`${API_ENDPOINT}/invitations/${token}`, { method: 'GET' });
        if (res.ok) {
          const data = await res.json();
          setInvitation(data);
          setError(null);
        } else {
          const errorData = await res.json().catch(() => ({}));
          // If already accepted, treat as success and redirect
          if (errorData.error && errorData.error.includes('already been accepted')) {
            if (showAlert) showAlert(t('invitation.success.accepted', 'Welcome! Your account has been created.'), 'success');
            setTimeout(() => navigate('/'), 500);
          } else {
            setError(errorData.error || t('invitation.errors.notFound', 'Invitation not found or has expired'));
            setInvitation(null);
          }
        }
      } catch (e) {
        setError(t('invitation.errors.loadError', 'Error loading invitation'));
        setInvitation(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchInvitation();
    }
  }, [token, apiCall, API_ENDPOINT, hasAccepted, showAlert, navigate]);

  const validatePasswords = () => {
    setPasswordError(null);

    if (!password) {
      setPasswordError(t('invitation.validation.passwordRequired', 'Password is required'));
      return false;
    }

    if (password.length < 8) {
      setPasswordError(t('invitation.validation.passwordMinLength', 'Password must be at least 8 characters'));
      return false;
    }

    if (password !== confirmPassword) {
      setPasswordError(t('invitation.validation.passwordMismatch', 'Passwords do not match'));
      return false;
    }

    return true;
  };

  const handleAcceptInvitation = async (e) => {
    e.preventDefault();

    if (!validatePasswords()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/invitations/${token}/accept`, {
        method: 'POST',
        body: JSON.stringify({ password })
      });

      if (res.ok) {
        await res.json();
        // Set flag immediately to prevent re-fetching during redirect window
        setHasAccepted(true);
        if (showAlert) showAlert(t('invitation.success.accepted', 'Welcome! Your account has been created.'), 'success');
        // Redirect to dashboard
        setTimeout(() => navigate('/'), 1000);
      } else {
        const errorData = await res.json().catch(() => ({}));
        setPasswordError(errorData.error || t('invitation.errors.acceptFailed', 'Failed to accept invitation'));
      }
    } catch (e) {
      setPasswordError(t('invitation.errors.acceptError', 'Error accepting invitation'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-bg dark:bg-bg-dark">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-action dark:text-action-dark mx-auto mb-4" />
          <p className="text-text-primary dark:text-text-primary-dark">{t('invitation.loading', 'Loading invitation...')}</p>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-bg dark:bg-bg-dark p-4">
        <div className="max-w-md w-full bg-card dark:bg-card-dark rounded-card shadow-lg p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-text-primary dark:text-text-primary-dark mb-2">{t('invitation.invalidTitle', 'Invitation Invalid')}</h1>
          <p className="text-text-secondary dark:text-text-secondary-dark mb-6">{error || t('invitation.invalidMessage', 'This invitation link is not valid or has expired.')}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full px-4 py-2.5 bg-action dark:bg-action-dark text-white rounded-button font-medium hover:bg-action-hover dark:hover:bg-action-hover-dark"
          >
            {t('invitation.actions.goToLogin', 'Go to Login')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-bg dark:bg-bg-dark p-4">
      <div className="max-w-md w-full">
        <div className="bg-card dark:bg-card-dark rounded-card shadow-lg p-8 mb-4">
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark mb-2">
            {t('invitation.joinTitle', 'Join {{organisationName}}', { organisationName: invitation.organisationName })}
          </h1>
          <p className="text-text-secondary dark:text-text-secondary-dark mb-6">
            {t('invitation.joinSubtitle', "You've been invited to join as a {{role}}", { role: invitation.role })}
          </p>

          <form onSubmit={handleAcceptInvitation} className="space-y-4">
            {/* Email display */}
            <div>
              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark block mb-2">{t('editor.fields.email', 'Email')}</label>
              <input
                type="email"
                value={invitation.email}
                disabled
                className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark text-text-secondary dark:text-text-secondary-dark"
              />
            </div>

            {/* Password input */}
            <div>
              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark block mb-2">{t('invitation.fields.password', 'Password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(null);
                }}
                placeholder={t('invitation.fields.passwordPlaceholder', 'Enter password (min 8 characters)')}
                className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
              />
            </div>

            {/* Confirm password input */}
            <div>
              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark block mb-2">{t('invitation.fields.confirmPassword', 'Confirm Password')}</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setPasswordError(null);
                }}
                placeholder={t('invitation.fields.confirmPasswordPlaceholder', 'Confirm password')}
                className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
              />
            </div>

            {/* Error message */}
            {passwordError && (
              <div className="p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-input text-red-700 dark:text-red-200 text-sm">
                {passwordError}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-4 py-2.5 bg-action dark:bg-action-dark text-white rounded-button font-bold hover:bg-action-hover dark:hover:bg-action-hover-dark disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {t('invitation.states.creatingAccount', 'Creating Account...')}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {t('invitation.actions.accept', 'Accept Invitation')}
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-text-muted dark:text-text-muted-dark text-center mt-4">
            {t('invitation.footerAgreement', 'By accepting this invitation, you agree to join the organization')}
          </p>
        </div>
      </div>
    </div>
  );
}

function SettingsView({ settings, setSettings, onBack, onSave, apiCall, showAlert, showConfirm, userRole, selectedOrganisationId }) {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const safeBack = withSafeHandler(onBack, () => navigate('/people'), 'settings-back');
  const effectiveSelectedOrganisationId = selectedOrganisationId || localStorage.getItem('selectedOrganisationId') || '';
  const hasValidSelectedOrganisationId = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(effectiveSelectedOrganisationId);
  // Initialize local settings with extracted base colors from existing data
  const initializeColorData = (colors) => {
    return colors.map(color => {
      let hexBase, baseColor, colorType;

      // Determine if this is a standard color (has Tailwind gradient or baseColor) or custom hex
      const hasValidTailwindGradient = color.gradient && typeof color.gradient === 'string' && color.gradient.startsWith('from-');
      const hasHexBase = color.hexBase && typeof color.hexBase === 'string' && color.hexBase.startsWith('#');

      if (hasValidTailwindGradient) {
        // Convert from Tailwind gradient to hex
        const extracted = extractBaseColorFromGradient(color.gradient);
        if (extracted) {
          baseColor = extracted.baseColor;
          hexBase = getTailwindColorHex(extracted.baseColor, 600); // Always use shade 600
          colorType = 'standard';
        } else {
          // Fallback if extraction fails
          baseColor = color.baseColor || 'indigo';
          hexBase = hasHexBase ? color.hexBase : getTailwindColorHex(baseColor, 600);
          colorType = color.colorType === 'custom' ? 'custom' : 'standard';
        }
      } else if (hasHexBase) {
        // Has hexBase - determine if standard or custom
        if (color.baseColor && color.colorType !== 'custom') {
          // Standard color with hexBase
          baseColor = color.baseColor;
          hexBase = color.hexBase;
          colorType = 'standard';
        } else {
          // Custom hex color
          baseColor = null;
          hexBase = color.hexBase;
          colorType = 'custom';
        }
      } else if (color.gradientStyle) {
        // Has gradientStyle but no hexBase - extract from gradientStyle or use default
        baseColor = null;
        hexBase = '#4f46e5'; // Default
        colorType = 'custom';
      } else {
        // Fallback - treat as standard with default
        baseColor = color.baseColor || 'indigo';
        hexBase = getTailwindColorHex(baseColor, 600);
        colorType = color.colorType === 'custom' ? 'custom' : 'standard';
      }

      // Always auto-generate complementary secondary color
      const complementaryColor = baseColor ? getComplementaryColor(baseColor) : null;
      const hexSecondary = color.hexSecondary || (complementaryColor ? getTailwindColorHex(complementaryColor, 600) : hexBase);

      // Generate all inline styles
      const gradientStyle = `linear-gradient(135deg, ${hexBase}, ${hexSecondary})`;
      const buttonStyle = hexBase;
      const linkStyle = hexBase;
      const textStyle = hexBase;

      // Build clean hex-only color object
      return {
        name: color.name,
        colorType: colorType,
        baseColor: baseColor, // null for custom, Tailwind name for standard
        hexBase: hexBase,
        hexSecondary: hexSecondary,
        gradientStyle: gradientStyle,
        buttonStyle: buttonStyle,
        linkStyle: linkStyle,
        textStyle: textStyle
      };
    });
  };

  const [localSettings, setLocalSettings] = useState({
    ...settings,
    theme_colors: initializeColorData(settings.theme_colors || []),
    app_theme_overrides: settings.app_theme_overrides && typeof settings.app_theme_overrides === 'object' ? settings.app_theme_overrides : {},
    theme_variant: normalizeThemeVariantClient(settings.theme_variant || 'vcard'),
    // Initialize override toggles - default to true if undefined, but preserve false
    allow_theme_customisation: settings.allow_theme_customisation !== undefined ? Boolean(settings.allow_theme_customisation) : true,
    allow_image_customisation: settings.allow_image_customisation !== undefined ? Boolean(settings.allow_image_customisation) : true,
    allow_links_customisation: settings.allow_links_customisation !== undefined ? Boolean(settings.allow_links_customisation) : true,
    allow_privacy_customisation: settings.allow_privacy_customisation !== undefined ? Boolean(settings.allow_privacy_customisation) : true
  });
  const [editingColorIndex, setEditingColorIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isOrganisationNameOpen, setIsOrganisationNameOpen] = useState(false);
  const [isCustomizationControlsOpen, setIsCustomizationControlsOpen] = useState(false);
  const [isThemeColorsOpen, setIsThemeColorsOpen] = useState(false);
  const [isAppThemesOpen, setIsAppThemesOpen] = useState(false);
  const [isAppColorsOpen, setIsAppColorsOpen] = useState(false);
  const [isDataBackupOpen, setIsDataBackupOpen] = useState(false);
  const [isExportingBackup, setIsExportingBackup] = useState(false);
  const [isImportingBackup, setIsImportingBackup] = useState(false);
  const [backupFile, setBackupFile] = useState(null);
  const backupFileInputRef = useRef(null);

  const buildScopedSettingsApiUrl = useCallback((url) => {
    if (userRole !== 'admin' || !effectiveSelectedOrganisationId) {
      return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}organisationId=${encodeURIComponent(effectiveSelectedOrganisationId)}`;
  }, [effectiveSelectedOrganisationId, userRole]);

  const getSettingsCacheKey = (organisationId = '') => (
    organisationId ? `organisationSettingsCache:${organisationId}` : 'organisationSettingsCache:global'
  );

  const applyThemePreset = (variant) => {
    const normalizedVariant = normalizeThemeVariantClient(variant);
    setEditingColorIndex(null);
    const preset = THEME_PRESETS?.[normalizedVariant];
    setLocalSettings(prev => ({
      ...prev,
      theme_variant: preset ? normalizedVariant : 'custom',
      theme_colors: preset ? initializeColorData(preset) : prev.theme_colors
    }));
  };

  const getAppThemeTokenEntries = () => {
    const variant = normalizeThemeVariantClient(localSettings.theme_variant || 'vcard');
    const theme = THEME_FILES[variant] || THEME_FILES.vcard || vcardTheme;
    return APP_THEME_OVERRIDE_TOKENS
      .map((token) => [token.key, t(token.labelKey, token.defaultLabel), theme.colors?.[token.key]])
      .filter(([, , tokenValue]) => tokenValue && typeof tokenValue === 'object');
  };

  const updateAppThemeOverride = (tokenKey, mode, value) => {
    const normalized = typeof value === 'string' ? value.trim() : '';
    setLocalSettings((prev) => {
      const next = { ...(prev.app_theme_overrides || {}) };
      if (!APP_THEME_OVERRIDE_TOKENS.some((token) => token.key === tokenKey)) {
        return prev;
      }
      const current = { ...(next[tokenKey] || {}) };
      current[mode] = normalized;
      if (!/^#[0-9A-Fa-f]{6}$/.test(current.light || '') && !/^#[0-9A-Fa-f]{6}$/.test(current.dark || '')) {
        delete next[tokenKey];
      } else {
        next[tokenKey] = {
          light: /^#[0-9A-Fa-f]{6}$/.test(current.light || '') ? current.light : null,
          dark: /^#[0-9A-Fa-f]{6}$/.test(current.dark || '') ? current.dark : null
        };
      }
      return { ...prev, app_theme_overrides: next };
    });
  };

  const clearAppThemeOverrides = () => {
    setLocalSettings((prev) => ({ ...prev, app_theme_overrides: {} }));
  };

  // Re-initialize when settings prop changes
  useEffect(() => {
    setLocalSettings({
      ...settings,
      theme_colors: initializeColorData(settings.theme_colors || []),
      app_theme_overrides: settings.app_theme_overrides && typeof settings.app_theme_overrides === 'object' ? settings.app_theme_overrides : {},
      theme_variant: normalizeThemeVariantClient(settings.theme_variant || localSettings.theme_variant || 'vcard'),
      // Initialize override toggles - default to true if undefined, but preserve false
      allow_theme_customisation: settings.allow_theme_customisation !== undefined ? Boolean(settings.allow_theme_customisation) : true,
      allow_image_customisation: settings.allow_image_customisation !== undefined ? Boolean(settings.allow_image_customisation) : true,
      allow_links_customisation: settings.allow_links_customisation !== undefined ? Boolean(settings.allow_links_customisation) : true,
      allow_privacy_customisation: settings.allow_privacy_customisation !== undefined ? Boolean(settings.allow_privacy_customisation) : true
    });
  }, [settings]);

  // Apply app theme variant class to body for CSS overrides (vcard|minimal|custom)
  useEffect(() => {
    const variant = normalizeThemeVariantClient(localSettings.theme_variant || settings.theme_variant || 'vcard');
    document.body.classList.remove('theme-vcard', 'theme-minimal', 'theme-mint', 'theme-nagarro', 'theme-custom');
    document.body.classList.add(`theme-${variant}`);
    applyThemeCssVars(variant, localSettings.app_theme_overrides || {});
  }, [localSettings.theme_variant, localSettings.app_theme_overrides, settings.theme_variant]);

  const handleSave = async () => {
    if (userRole === 'admin' && !hasValidSelectedOrganisationId) {
      if (showAlert) showAlert(t('settings.validation.organisationRequired', 'Please select a valid organisation first'), 'error');
      return;
    }

    setIsSaving(true);
    const startTime = Date.now();
    try {
      // Save hex-only color structure
      const colorsToSave = localSettings.theme_colors.map(color => {
        return {
          name: color.name,
          colorType: color.colorType || 'standard',
          baseColor: color.baseColor || null, // null for custom, Tailwind name for standard
          hexBase: color.hexBase || '#4f46e5',
          hexSecondary: color.hexSecondary || color.hexBase || '#4f46e5',
          gradientStyle: color.gradientStyle || `linear-gradient(135deg, ${color.hexBase || '#4f46e5'}, ${color.hexSecondary || color.hexBase || '#4f46e5'})`,
          buttonStyle: color.buttonStyle || color.hexBase || '#4f46e5',
          linkStyle: color.linkStyle || color.hexBase || '#4f46e5',
          textStyle: color.textStyle || color.hexBase || '#4f46e5'
        };
      });
      const allowedAppColorKeys = new Set(APP_THEME_OVERRIDE_TOKENS.map((token) => token.key));
      const appThemeOverridesToSave = {};
      Object.entries(localSettings.app_theme_overrides || {}).forEach(([key, value]) => {
        if (!allowedAppColorKeys.has(key) || !value || typeof value !== 'object') return;
        const light = typeof value.light === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value.light.trim()) ? value.light.trim() : null;
        const dark = typeof value.dark === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value.dark.trim()) ? value.dark.trim() : null;
        if (!light && !dark) return;
        appThemeOverridesToSave[key] = { light, dark };
      });

      const res = await apiCall(buildScopedSettingsApiUrl(`${API_ENDPOINT}/admin/settings`), {
        method: 'POST',
        body: JSON.stringify({
          ...(userRole === 'admin' && hasValidSelectedOrganisationId ? { organisationId: effectiveSelectedOrganisationId } : {}),
          default_organisation: localSettings.default_organisation,
          theme_colors: colorsToSave,
          theme_variant: normalizeThemeVariantClient(localSettings.theme_variant || 'vcard'),
          app_theme_overrides: appThemeOverridesToSave,
          allow_theme_customisation: Boolean(localSettings.allow_theme_customisation),
          allow_image_customisation: Boolean(localSettings.allow_image_customisation),
          allow_links_customisation: Boolean(localSettings.allow_links_customisation),
          allow_privacy_customisation: Boolean(localSettings.allow_privacy_customisation)
        })
      });

      // Ensure at least 500ms has passed
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < 500) {
        await new Promise(resolve => setTimeout(resolve, 500 - elapsedTime));
      }

      if (res.ok) {
        const normalizedLocalSettings = {
          ...localSettings,
          theme_variant: normalizeThemeVariantClient(localSettings.theme_variant || 'vcard'),
          app_theme_overrides: appThemeOverridesToSave,
          theme_colors: colorsToSave
        };
        setSettings(normalizedLocalSettings);
        try {
          localStorage.setItem(getSettingsCacheKey(effectiveSelectedOrganisationId), JSON.stringify(normalizedLocalSettings));
        } catch (_) {
          // Ignore cache write failures
        }
        // Refetch from server to get the latest saved data
        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 2000);
        await onSave(effectiveSelectedOrganisationId);
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error('Save failed:', errorData);
        if (showAlert) showAlert(errorData.error || t('settings.alerts.saveFailed', 'Failed to save settings'), 'error');
      }
    } catch (e) {
      if (showAlert) showAlert(t('settings.alerts.saveError', 'Error saving settings'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportBackup = async () => {
    setIsExportingBackup(true);
    try {
      const res = await apiCall(buildScopedSettingsApiUrl(`${API_ENDPOINT}/admin/backup/export`));
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || t('settings.backup.exportFailed', 'Failed to export backup'), 'error');
        return;
      }

      const payload = await res.json();
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const link = document.createElement('a');
      link.href = url;
      link.download = `vcard-backup-${stamp}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      if (showAlert) showAlert(t('settings.backup.exportSuccess', 'Backup exported successfully'), 'success');
    } catch (e) {
      if (showAlert) showAlert(t('settings.backup.exportError', 'Error exporting backup'), 'error');
    } finally {
      setIsExportingBackup(false);
    }
  };

  const handleImportBackup = async () => {
    if (!backupFile) {
      if (showAlert) showAlert(t('settings.backup.selectFileFirst', 'Please choose a backup file first'), 'error');
      return;
    }

    const runImport = async () => {
      setIsImportingBackup(true);
      try {
        const raw = await backupFile.text();
        const payload = JSON.parse(raw);
        const res = await apiCall(buildScopedSettingsApiUrl(`${API_ENDPOINT}/admin/backup/import`), {
          method: 'POST',
          body: JSON.stringify(payload)
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          if (showAlert) showAlert(errorData.error || t('settings.backup.importFailed', 'Failed to import backup'), 'error');
          return;
        }

        await onSave();
        setBackupFile(null);
        if (backupFileInputRef.current) {
          backupFileInputRef.current.value = '';
        }
        if (showAlert) {
          showAlert(
            t('settings.backup.importSuccess', 'Backup imported successfully. Images are not included in JSON backups. You will be redirected to login.'),
            'success',
            '',
            () => {
              window.location.href = '/login';
            }
          );
        }
      } catch (e) {
        if (showAlert) showAlert(t('settings.backup.importInvalid', 'Invalid backup file. Please upload a valid JSON export.'), 'error');
      } finally {
        setIsImportingBackup(false);
      }
    };

    if (showConfirm) {
      showConfirm(
        t('settings.backup.importConfirmMessage', 'Importing will replace all users/cards/settings of your current organization. Continue?'),
        runImport,
        t('settings.backup.importConfirmTitle', 'Import Backup'),
        t('settings.backup.importConfirmAction', 'Import'),
        t('common.actions.cancel', 'Cancel')
      );
      return;
    }

    await runImport();
  };

  const addColor = () => {
    const baseColor = 'indigo';
    const hexBase = getTailwindColorHex(baseColor, 600);
    const complementary = getComplementaryColor(baseColor);
    const hexSecondary = getTailwindColorHex(complementary, 600);

    const newColor = {
      name: `color${localSettings.theme_colors.length + 1}`,
      colorType: 'standard',
      baseColor: baseColor,
      hexBase: hexBase,
      hexSecondary: hexSecondary,
      gradientStyle: `linear-gradient(135deg, ${hexBase}, ${hexSecondary})`,
      buttonStyle: hexBase,
      linkStyle: hexBase,
      textStyle: hexBase
    };
    setLocalSettings(prev => {
      const newColors = [...prev.theme_colors, newColor];
      setEditingColorIndex(newColors.length - 1);
      return {
        ...prev,
        theme_colors: newColors,
        theme_variant: 'custom'
      };
    });
  };

  const updateColor = (colorIndex, field, value) => {
    setLocalSettings(prev => {
      const updated = prev.theme_colors.map((c, idx) => {
        if (idx === colorIndex) {
          const updatedColor = { ...c, [field]: value };
          const currentColorType = updatedColor.colorType || c.colorType || 'standard';

          // Handle colorType changes
          if (field === 'colorType') {
            if (value === 'standard' && !updatedColor.baseColor) {
              // Switching to standard - set default baseColor if missing
              updatedColor.baseColor = c.baseColor || 'indigo';
              updatedColor.hexBase = c.hexBase || getTailwindColorHex(updatedColor.baseColor, 600);
            } else if (value === 'custom') {
              // Switching to custom - clear baseColor
              updatedColor.baseColor = null;
            }
          }

          // For standard colors: when baseColor changes, update hexBase
          if (currentColorType === 'standard' && field === 'baseColor') {
            updatedColor.hexBase = getTailwindColorHex(value, 600);
          }

          // For custom colors: when hexBase changes, ensure colorType is custom
          if (field === 'hexBase') {
            if (currentColorType !== 'custom') {
              updatedColor.colorType = 'custom';
              updatedColor.baseColor = null;
            }
          }

          // Handle hexBase
          let hexBase = updatedColor.hexBase || c.hexBase;
          let baseColor = updatedColor.baseColor || c.baseColor;

          // If hexBase is missing, generate it
          if (!hexBase) {
            if (baseColor) {
              hexBase = getTailwindColorHex(baseColor, 600);
              updatedColor.hexBase = hexBase;
            } else {
              hexBase = '#4f46e5'; // Default
              updatedColor.hexBase = hexBase;
            }
          }

          // Handle hexSecondary
          let hexSecondary;

          if (currentColorType === 'standard') {
            // Standard colors always use auto-complementary
            const complementaryColor = baseColor ? getComplementaryColor(baseColor) : null;
            hexSecondary = complementaryColor ? getTailwindColorHex(complementaryColor, 600) : hexBase;
            updatedColor.hexSecondary = hexSecondary;
          } else if (currentColorType === 'custom') {
            // Custom colors: allow manual setting
            if (field === 'hexSecondary') {
              // User is manually setting hexSecondary
              hexSecondary = value || null;
              updatedColor.hexSecondary = hexSecondary;
            } else if (field === 'baseColor' || field === 'hexBase' || field === 'colorType') {
              // When baseColor/hexBase changes, only auto-generate if hexSecondary is not manually set
              const existingHexSecondary = c.hexSecondary;
              if (!existingHexSecondary || existingHexSecondary === '') {
                // Auto-generate complementary
                const complementaryColor = baseColor ? getComplementaryColor(baseColor) : null;
                hexSecondary = complementaryColor ? getTailwindColorHex(complementaryColor, 600) : hexBase;
                updatedColor.hexSecondary = hexSecondary;
              } else {
                // Keep existing manual value
                hexSecondary = existingHexSecondary;
                updatedColor.hexSecondary = hexSecondary;
              }
            } else {
              // Keep existing value
              hexSecondary = updatedColor.hexSecondary || c.hexSecondary || hexBase;
              updatedColor.hexSecondary = hexSecondary;
            }
          } else {
            // Fallback
            hexSecondary = updatedColor.hexSecondary || c.hexSecondary || hexBase;
            updatedColor.hexSecondary = hexSecondary;
          }

          // Always regenerate inline styles when relevant fields change
          if (field === 'baseColor' || field === 'colorType' || field === 'hexBase' || field === 'hexSecondary') {
            const finalHexSecondary = hexSecondary || hexBase;
            updatedColor.gradientStyle = `linear-gradient(135deg, ${hexBase}, ${finalHexSecondary})`;
            updatedColor.buttonStyle = hexBase;
            updatedColor.linkStyle = hexBase;
            updatedColor.textStyle = hexBase;
          }

          return updatedColor;
        }
        return c;
      });
      return { ...prev, theme_colors: updated, theme_variant: 'custom' };
    });
  };

  const removeColor = (colorIndex) => {
    if (localSettings.theme_colors.length <= 1) {
      if (showAlert) showAlert(t('settings.profileColors.minimumOneColor', 'You must have at least one color'), 'error');
      return;
    }
    const colorName = localSettings.theme_colors[colorIndex]?.name;
    if (showConfirm) {
      showConfirm(
        t('settings.profileColors.deleteColorMessage', 'Delete color "{{name}}"?', { name: colorName }),
        () => {
          setLocalSettings(prev => ({
            ...prev,
            theme_colors: prev.theme_colors.filter((c, idx) => idx !== colorIndex),
            theme_variant: 'custom'
          }));
          if (editingColorIndex === colorIndex) {
            setEditingColorIndex(null);
          } else if (editingColorIndex !== null && editingColorIndex > colorIndex) {
            // Adjust index if we deleted a color before the one being edited
            setEditingColorIndex(editingColorIndex - 1);
          }
        },
        t('settings.profileColors.deleteColorTitle', 'Delete Color'),
        t('common.actions.delete', 'Delete'),
        t('common.actions.cancel', 'Cancel')
      );
    }
  };

  if (userRole === 'admin' && !effectiveSelectedOrganisationId) {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark flex items-center justify-center p-6">
        <div className="bg-card dark:bg-card-dark rounded-card p-8 shadow-lg max-w-md w-full">
          <h2 className="text-xl font-bold text-text-primary dark:text-text-primary-dark mb-4">{t('settings.selectOrganisation.title', 'Select organisation')}</h2>
          <p className="text-text-secondary dark:text-text-muted-dark mb-6">{t('settings.selectOrganisation.message', 'Choose an organisation first in organisation management.')}</p>
          <button onClick={safeBack} className="px-4 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-full text-sm font-bold">
            {t('common.actions.back', 'Back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex flex-col lg:flex-row">
      <div className="w-full lg:w-1/2 bg-card dark:bg-card-dark border-r border-border dark:border-border-dark h-auto lg:h-screen overflow-y-auto flex flex-col">
        <div className="p-6 border-b border-border-subtle dark:border-border-dark flex items-center justify-between bg-card dark:bg-card-dark sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <button onClick={safeBack} className="p-2 hover:bg-surface dark:hover:bg-surface-dark rounded-full text-text-muted dark:text-text-muted-dark">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-text-primary dark:text-text-primary-dark">{t('settings.title', 'Organisation Settings')}</h1>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-5 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-full text-sm font-bold flex items-center gap-2 hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark transition-colors disabled:opacity-50"
          >
            {isSaving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : isSuccess ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isSaving ? t('common.states.saving', 'Saving...') : t('common.actions.save', 'Save')}
          </button>
        </div>

        <div className="flex-1 p-6 space-y-6">
          <div className="space-y-6">
            {/* Organisation Name Section */}
            <div>
              <button
                onClick={() => setIsOrganisationNameOpen(!isOrganisationNameOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">{t('settings.organisationName.title', 'Organisation Name')}</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">{t('settings.organisationName.description', 'This organisation name will be applied to all cards in your organisation. Users cannot change this.')}</p>
                </div>
                {isOrganisationNameOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>

              {isOrganisationNameOpen && (
                <div className="mt-4 p-4 bg-surface dark:bg-card-dark/50 rounded-input">
                  <input
                    type="text"
                    value={localSettings.default_organisation || ''}
                    onChange={(e) => setLocalSettings(prev => ({ ...prev, default_organisation: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                    placeholder={t('settings.organisationName.placeholder', 'Organisation Name')}
                  />
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* Organization Override Toggles */}
            <div className="space-y-6">
              <button
                onClick={() => setIsCustomizationControlsOpen(!isCustomizationControlsOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">{t('settings.customisation.title', 'User Customisation Controls')}</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">{t('settings.customisation.description', 'Control what users in your organisation can customise on their cards.')}</p>
                </div>
                {isCustomizationControlsOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>

              {isCustomizationControlsOpen && (
                <div className="mt-4 space-y-4">
                  {/* Theme Customization Group */}
                  <div className="bg-surface dark:bg-card-dark/50 rounded-input p-5 space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-2">{t('settings.customisation.theme.title', 'Theme Customisation')}</h3>
                      <p className="text-xs text-text-secondary dark:text-text-muted-dark mb-4">{t('settings.customisation.theme.description', 'Control whether users can choose theme colors for their cards.')}</p>
                    </div>
                    <Toggle
                      label={t('settings.customisation.theme.toggleLabel', 'Allow users to choose theme colors')}
                      description={t('settings.customisation.theme.toggleDescription', "When enabled, users can select from your organisation's theme colour palette. When disabled, cards will use the first colour in your palette.")}
                      checked={localSettings.allow_theme_customisation === true}
                      onChange={(checked) => setLocalSettings(prev => ({ ...prev, allow_theme_customisation: checked }))}
                    />
                  </div>

                  {/* Image Customization Group */}
                  <div className="bg-surface dark:bg-card-dark/50 rounded-input p-5 space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-2">{t('settings.customisation.images.title', 'Image Customisation')}</h3>
                      <p className="text-xs text-text-secondary dark:text-text-muted-dark mb-4">{t('settings.customisation.images.description', 'Control whether users can upload custom avatars and banner images.')}</p>
                    </div>
                    <Toggle
                      label={t('settings.customisation.images.toggleLabel', 'Allow users to upload custom avatars and banners')}
                      description={t('settings.customisation.images.toggleDescription', 'When enabled, users can upload their own images. When disabled, image uploads will be blocked.')}
                      checked={localSettings.allow_image_customisation === true}
                      onChange={(checked) => setLocalSettings(prev => ({ ...prev, allow_image_customisation: checked }))}
                    />
                  </div>

                  {/* Links Customization Group */}
                  <div className="bg-surface dark:bg-card-dark/50 rounded-input p-5 space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-2">{t('settings.customisation.links.title', 'Links Customisation')}</h3>
                      <p className="text-xs text-text-secondary dark:text-text-muted-dark mb-4">{t('settings.customisation.links.description', 'Control whether users can add custom links to their cards.')}</p>
                    </div>
                    <Toggle
                      label={t('settings.customisation.links.toggleLabel', 'Allow users to add custom links')}
                      description={t('settings.customisation.links.toggleDescription', 'When enabled, users can add custom links to their cards. When disabled, the links section will be locked.')}
                      checked={localSettings.allow_links_customisation === true}
                      onChange={(checked) => setLocalSettings(prev => ({ ...prev, allow_links_customisation: checked }))}
                    />
                  </div>

                  {/* Privacy Settings Group */}
                  <div className="bg-surface dark:bg-card-dark/50 rounded-input p-5 space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-2">{t('settings.customisation.privacy.title', 'Privacy Settings')}</h3>
                      <p className="text-xs text-text-secondary dark:text-text-muted-dark mb-4">{t('settings.customisation.privacy.description', 'Control whether users can change privacy settings on their cards.')}</p>
                    </div>
                    <Toggle
                      label={t('settings.customisation.privacy.toggleLabel', 'Allow users to change privacy settings')}
                      description={t('settings.customisation.privacy.toggleDescription', 'When enabled, users can modify privacy options (require interaction, obfuscation, block robots). When disabled, privacy settings will be locked to organisation defaults.')}
                      checked={localSettings.allow_privacy_customisation === true}
                      onChange={(checked) => setLocalSettings(prev => ({ ...prev, allow_privacy_customisation: checked }))}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* App Themes */}
            <div>
              <button
                onClick={() => setIsAppThemesOpen(!isAppThemesOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">{t('settings.appTheme.title', 'App Themes')}</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">{t('settings.appTheme.description', 'Choose the look for the app UI (backgrounds, chrome, buttons).')}</p>
                </div>
                {isAppThemesOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>

              {isAppThemesOpen && (
                <div className="mt-4 space-y-3">
                  {[
                    { id: 'vcard', titleKey: 'settings.appTheme.options.vcard.title', defaultTitle: 'VCARD', descKey: 'settings.appTheme.options.vcard.description', defaultDesc: 'Original VCARD theme with colors and textures.' },
                    { id: 'minimal', titleKey: 'settings.appTheme.options.minimal.title', defaultTitle: 'Minimal', descKey: 'settings.appTheme.options.minimal.description', defaultDesc: 'Black/white, few grays, no textures, border-only accents.' },
                    { id: 'mint', titleKey: 'settings.appTheme.options.mint.title', defaultTitle: 'Mint', descKey: 'settings.appTheme.options.mint.description', defaultDesc: 'Mint Green + Petrol Blue palette with Nightfall/Limestone accents.' },
                  ].map(opt => (
                    <label
                      key={opt.id}
                      className="flex items-start gap-3 p-3 rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark hover:bg-card dark:hover:bg-card-dark transition-colors cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="app-theme"
                        className="mt-1"
                        value={opt.id}
                        checked={normalizeThemeVariantClient(localSettings.theme_variant || 'vcard') === opt.id}
                        onChange={(e) => applyThemePreset(e.target.value)}
                      />
                      <div>
                        <div className="font-medium text-text-primary dark:text-text-primary-dark">{t(opt.titleKey, opt.defaultTitle)}</div>
                        <p className="text-sm text-text-secondary dark:text-text-muted-dark">{t(opt.descKey, opt.defaultDesc)}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* App Colors */}
            <div>
              <button
                onClick={() => setIsAppColorsOpen(!isAppColorsOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">{t('settings.appColors.title', 'App Colors')}</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">{t('settings.appColors.description', 'Customize all app colors per organisation (light and dark mode).')}</p>
                </div>
                {isAppColorsOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>

              {isAppColorsOpen && (
                <div className="mt-4 space-y-4">
                  <div className="flex justify-end">
                    <button
                      onClick={clearAppThemeOverrides}
                      className="px-4 py-2 rounded-button bg-surface dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-secondary-dark text-sm font-medium"
                    >
                      {t('settings.appColors.actions.reset', 'Reset App Colors')}
                    </button>
                  </div>
                  <div className="space-y-3">
                    {getAppThemeTokenEntries().map(([tokenKey, tokenLabel, tokenValue]) => {
                      const override = localSettings.app_theme_overrides?.[tokenKey] || {};
                      const baseLight = tokenValue?.light || '#000000';
                      const baseDark = tokenValue?.dark || '#000000';
                      const pickerLight = /^#[0-9A-Fa-f]{6}$/.test(override.light || '') ? override.light : (/^#[0-9A-Fa-f]{6}$/.test(baseLight || '') ? baseLight : '#000000');
                      const pickerDark = /^#[0-9A-Fa-f]{6}$/.test(override.dark || '') ? override.dark : (/^#[0-9A-Fa-f]{6}$/.test(baseDark || '') ? baseDark : '#000000');
                      return (
                        <div key={tokenKey} className="bg-surface dark:bg-card-dark/50 rounded-input p-4 border border-border dark:border-border-dark">
                          <div className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-1">{tokenLabel}</div>
                          <div className="text-xs text-text-muted dark:text-text-muted-dark mb-3">{tokenKey}</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-text-secondary dark:text-text-muted-dark mb-1 block">{t('settings.appColors.light', 'Light')}</label>
                              <div className="flex gap-2 items-center">
                                <input
                                  type="color"
                                  value={pickerLight}
                                  onChange={(e) => updateAppThemeOverride(tokenKey, 'light', e.target.value)}
                                  className="w-12 h-10 rounded-input border border-border dark:border-border-dark cursor-pointer"
                                />
                                <input
                                  type="text"
                                  value={override.light || baseLight}
                                  onChange={(e) => updateAppThemeOverride(tokenKey, 'light', e.target.value)}
                                  className="flex-1 px-3 py-2 rounded-input border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-primary dark:text-text-primary-dark font-mono text-sm"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-text-secondary dark:text-text-muted-dark mb-1 block">{t('settings.appColors.dark', 'Dark')}</label>
                              <div className="flex gap-2 items-center">
                                <input
                                  type="color"
                                  value={pickerDark}
                                  onChange={(e) => updateAppThemeOverride(tokenKey, 'dark', e.target.value)}
                                  className="w-12 h-10 rounded-input border border-border dark:border-border-dark cursor-pointer"
                                />
                                <input
                                  type="text"
                                  value={override.dark || baseDark}
                                  onChange={(e) => updateAppThemeOverride(tokenKey, 'dark', e.target.value)}
                                  className="flex-1 px-3 py-2 rounded-input border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-primary dark:text-text-primary-dark font-mono text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* Profile Colors */}
            <div>
              <button
                onClick={() => setIsDataBackupOpen(!isDataBackupOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">{t('settings.backup.title', 'Data Backup')}</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">{t('settings.backup.description', 'Export/import all organization data (users, cards, settings, short IDs). Images are excluded.')}</p>
                </div>
                {isDataBackupOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>

              {isDataBackupOpen && (
                <div className="mt-4 space-y-4">
                  <div className="bg-surface dark:bg-card-dark/50 rounded-input p-5 space-y-4">
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={handleExportBackup}
                        disabled={isExportingBackup || isImportingBackup}
                        className="px-4 py-2 rounded-button bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark font-medium disabled:opacity-50 flex items-center gap-2"
                      >
                        {isExportingBackup ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        {isExportingBackup ? t('common.states.exporting', 'Exporting...') : t('settings.backup.actions.export', 'Export JSON Backup')}
                      </button>
                    </div>
                    <div className="space-y-3">
                      <input
                        ref={backupFileInputRef}
                        type="file"
                        accept=".json,application/json"
                        onChange={(e) => setBackupFile(e.target.files?.[0] || null)}
                        className="block w-full text-sm text-text-primary dark:text-text-primary-dark file:mr-4 file:py-2 file:px-4 file:rounded-button file:border-0 file:bg-surface file:text-text-primary dark:file:bg-surface-dark dark:file:text-text-secondary-dark"
                      />
                      {backupFile && (
                        <p className="text-xs text-text-secondary dark:text-text-muted-dark">{t('settings.backup.selected', 'Selected: {{name}}', { name: backupFile.name })}</p>
                      )}
                      <button
                        onClick={handleImportBackup}
                        disabled={!backupFile || isImportingBackup || isExportingBackup}
                        className="px-4 py-2 rounded-button bg-action dark:bg-action-dark text-white font-medium disabled:opacity-50 flex items-center gap-2"
                      >
                        {isImportingBackup ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {isImportingBackup ? t('common.states.importing', 'Importing...') : t('settings.backup.actions.import', 'Import JSON Backup')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* Profile Colors */}
            <div>
              <button
                onClick={() => setIsThemeColorsOpen(!isThemeColorsOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">{t('settings.profileColors.title', 'Profile Colors')}</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">{t('settings.profileColors.description', 'Manage the color palette available for user cards.')}</p>
                </div>
                {isThemeColorsOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>

              {isThemeColorsOpen && (
                <div className="mt-4 space-y-4">
                  <div className="flex justify-end">
                    <button
                      onClick={addColor}
                      className="text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-badge hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                    >
                      <Plus className="w-4 h-4" /> {t('settings.profileColors.actions.add', 'Add Color')}
                    </button>
                  </div>

                  <div className="space-y-4">
                    {localSettings.theme_colors?.map((color, index) => (
                      <div key={index} className="bg-surface dark:bg-surface-dark p-4 rounded-input border border-border dark:border-border-dark">
                        {editingColorIndex === index ? (
                          <div className="space-y-4">
                            <div className="flex justify-between items-center">
                              <h4 className="font-medium text-text-primary dark:text-text-primary-dark">{t('settings.profileColors.editor.editing', 'Editing: {{name}}', { name: color.name })}</h4>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setEditingColorIndex(null)}
                                  className="px-3 py-1 text-sm bg-card dark:bg-surface-dark border border-border dark:border-border-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark text-text-primary dark:text-text-primary-dark"
                                >
                                  {t('common.actions.done', 'Done')}
                                </button>
                                <button
                                  onClick={() => removeColor(index)}
                                  className="px-3 py-1 text-sm bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark border border-error-border dark:border-error-border-dark rounded-badge hover:bg-error-bg dark:hover:bg-error-bg-dark"
                                >
                                  {t('common.actions.delete', 'Delete')}
                                </button>
                              </div>
                            </div>

                            <Input
                              label={t('settings.profileColors.editor.colorName', 'Color Name')}
                              value={color.name}
                              onChange={(v) => updateColor(index, 'name', v)}
                            />

                            <div>
                              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('settings.profileColors.editor.colorType', 'Color Type')}</label>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => updateColor(index, 'colorType', 'standard')}
                                  className={`flex-1 px-4 py-2 rounded-button border transition-all ${(color.colorType || 'standard') === 'standard'
                                    ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-400 text-indigo-700 dark:text-indigo-300 font-medium'
                                    : 'bg-card dark:bg-surface-dark border-border dark:border-border-dark text-text-secondary dark:text-text-secondary-dark hover:bg-surface dark:hover:bg-surface-dark'
                                    }`}
                                >
                                  {t('settings.profileColors.editor.standardColors', 'Standard Colors')}
                                </button>
                                <button
                                  onClick={() => updateColor(index, 'colorType', 'custom')}
                                  className={`flex-1 px-4 py-2 rounded-button border transition-all ${color.colorType === 'custom'
                                    ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-400 text-indigo-700 dark:text-indigo-300 font-medium'
                                    : 'bg-card dark:bg-surface-dark border-border dark:border-border-dark text-text-secondary dark:text-text-secondary-dark hover:bg-surface dark:hover:bg-surface-dark'
                                    }`}
                                >
                                  {t('settings.profileColors.editor.customColors', 'Custom Colours')}
                                </button>
                              </div>
                            </div>

                            {(color.colorType || 'standard') === 'standard' ? (
                              <>
                                <ColorSelector
                                  label={t('settings.profileColors.editor.baseColor', 'Base Color')}
                                  selectedColor={color.baseColor || 'indigo'}
                                  onSelect={(selected) => updateColor(index, 'baseColor', selected)}
                                />
                                <p className="text-xs text-text-muted dark:text-text-muted-dark">{t('settings.profileColors.editor.secondaryAuto', 'Secondary: Auto (complementary)')}</p>
                              </>
                            ) : (
                              <>
                                <div>
                                  <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('settings.profileColors.editor.baseColor', 'Base Color')}</label>
                                  <div className="flex gap-2 items-center">
                                    <input
                                      type="color"
                                      value={color.hexBase || '#4f46e5'}
                                      onChange={(e) => updateColor(index, 'hexBase', e.target.value)}
                                      className="w-16 h-10 rounded-input border border-border dark:border-border-dark cursor-pointer"
                                    />
                                    <input
                                      type="text"
                                      value={color.hexBase || '#4f46e5'}
                                      onChange={(e) => updateColor(index, 'hexBase', e.target.value)}
                                      placeholder="#4f46e5"
                                      className="flex-1 px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark font-mono text-sm"
                                    />
                                  </div>
                                </div>

                                <div>
                                  <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('settings.profileColors.editor.secondaryColorOptional', 'Secondary Color (optional)')}</label>
                                  <div className="flex gap-2 items-center">
                                    <input
                                      type="color"
                                      value={color.hexSecondary || color.hexBase || '#7c3aed'}
                                      onChange={(e) => updateColor(index, 'hexSecondary', e.target.value)}
                                      className="w-16 h-10 rounded-input border border-border dark:border-border-dark cursor-pointer"
                                    />
                                    <input
                                      type="text"
                                      value={color.hexSecondary || ''}
                                      onChange={(e) => updateColor(index, 'hexSecondary', e.target.value || null)}
                                      placeholder={t('settings.profileColors.editor.secondaryPlaceholder', 'Leave blank for auto (complementary)')}
                                      className="flex-1 px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark font-mono text-sm"
                                    />
                                  </div>
                                  <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">{t('settings.profileColors.editor.secondaryHelper', 'Leave blank to use auto (complementary) color')}</p>
                                </div>
                              </>
                            )}

                            {/* Preview of generated gradient */}
                            <div>
                              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{t('common.preview', 'Preview')}</label>
                              <div
                                className="w-full h-16 rounded-container overflow-hidden"
                                style={{ background: color.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                              />
                            </div>

                            {/* Collapsible advanced view */}
                            <details className="text-sm">
                              <summary className="cursor-pointer text-text-secondary dark:text-text-muted-dark hover:text-text-primary dark:hover:text-text-primary-dark font-medium mb-2">
                                {t('settings.profileColors.editor.advancedGeneratedStyles', 'Advanced: Generated Styles')}
                              </summary>
                              <div className="bg-card dark:bg-surface-dark p-3 rounded-container border border-border dark:border-border-dark space-y-2 text-xs font-mono">
                                <div><span className="text-text-muted dark:text-text-muted-dark">{t('settings.profileColors.editor.gradientStyle', 'Gradient Style:')}</span> <span className="text-text-primary dark:text-text-primary-dark">{color.gradientStyle || t('common.states.notAvailable', 'N/A')}</span></div>
                                <div><span className="text-text-muted dark:text-text-muted-dark">{t('settings.profileColors.editor.buttonStyle', 'Button Style:')}</span> <span className="text-text-primary dark:text-text-primary-dark">{color.buttonStyle || t('common.states.notAvailable', 'N/A')}</span></div>
                                <div><span className="text-text-muted dark:text-text-muted-dark">{t('settings.profileColors.editor.linkStyle', 'Link Style:')}</span> <span className="text-text-primary dark:text-text-primary-dark">{color.linkStyle || t('common.states.notAvailable', 'N/A')}</span></div>
                                <div><span className="text-text-muted dark:text-text-muted-dark">{t('settings.profileColors.editor.textStyle', 'Text Style:')}</span> <span className="text-text-primary dark:text-text-primary-dark">{color.textStyle || t('common.states.notAvailable', 'N/A')}</span></div>
                              </div>
                            </details>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div
                                className="w-12 h-12 rounded-full"
                                style={{ background: color.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                              />
                              <div>
                                <div className="font-medium text-text-primary dark:text-text-primary-dark">
                                  {color.baseColor ? (color.baseColor.charAt(0).toUpperCase() + color.baseColor.slice(1)) : color.name}
                                </div>
                                <div className="text-xs text-text-muted dark:text-text-muted-dark">
                                  {t(color.baseColor ? 'settings.profileColors.summary.standard' : 'settings.profileColors.summary.custom', color.baseColor ? 'Standard' : 'Custom')} ({t('settings.profileColors.summary.secondaryAuto', 'Secondary: Auto')})
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => setEditingColorIndex(index)}
                              className="px-3 py-1 text-sm bg-card dark:bg-surface-dark border border-border dark:border-border-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark flex items-center gap-1 text-text-primary dark:text-text-primary-dark"
                            >
                              <Edit3 className="w-3 h-3" /> {t('common.actions.edit', 'Edit')}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex w-1/2 bg-main dark:bg-main-dark items-center justify-center p-10">
        <div className="bg-card dark:bg-card-dark rounded-card p-8 shadow-lg max-w-md w-full border border-border dark:border-border-dark relative z-10 isolate">
          <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">{t('common.preview', 'Preview')}</h3>
          <div className="space-y-4">
            <div>
              <div className="text-sm text-text-secondary dark:text-text-muted-dark mb-2">{t('settings.preview.defaultOrganisation', 'Default Organisation:')}</div>
              <div className="font-medium text-text-primary dark:text-text-primary-dark">{localSettings.default_organisation || t('common.states.notSet', 'Not set')}</div>
            </div>
            <div>
              <div className="text-sm text-text-secondary dark:text-text-muted-dark mb-3">{t('settings.preview.colorEffects', 'Color Effects:')}</div>
              {(() => {
                const previewColor = editingColorIndex !== null && localSettings.theme_colors?.[editingColorIndex]
                  ? localSettings.theme_colors[editingColorIndex]
                  : localSettings.theme_colors?.[0];

                return (
                  <div className="space-y-3">
                    {/* Text example */}
                    <div>
                      <div className="text-xs text-text-muted dark:text-text-muted-dark mb-1">{t('settings.preview.textLabel', 'Text')}</div>
                      <div
                        className="text-lg font-medium"
                        style={{ color: previewColor?.textStyle || '#4f46e5' }}
                      >
                        {t('settings.preview.exampleText', 'Example Text')}
                      </div>
                    </div>

                    {/* Link example */}
                    <div>
                      <div className="text-xs text-text-muted dark:text-text-muted-dark mb-1">{t('settings.preview.linkLabel', 'Link')}</div>
                      <a
                        href="#"
                        className="inline-block px-4 py-2 rounded-input border border-border dark:border-border-dark transition-all hover:opacity-80"
                        style={{ color: previewColor?.linkStyle || '#4f46e5' }}
                        onClick={(e) => e.preventDefault()}
                      >
                        {t('settings.preview.exampleLink', 'Example Link')}
                      </a>
                    </div>

                    {/* Button example */}
                    <div>
                      <div className="text-xs text-text-muted dark:text-text-muted-dark mb-1">{t('settings.preview.buttonLabel', 'Button')}</div>
                      <button
                        className="px-4 py-2 rounded-full font-medium text-white shadow-md transition-transform active:scale-[0.98]"
                        style={{ backgroundColor: previewColor?.buttonStyle || '#4f46e5' }}
                      >
                        {t('settings.preview.exampleButton', 'Example Button')}
                      </button>
                    </div>

                    {/* Gradient background example */}
                    <div>
                      <div className="text-xs text-text-muted dark:text-text-muted-dark mb-1">{t('settings.preview.gradientLabel', 'Gradient Background')}</div>
                      <div
                        className="w-full h-12 rounded-container overflow-hidden"
                        style={{ background: previewColor?.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      <div className="fixed bottom-4 right-4 z-10 text-center group">
        <div className="flex justify-center">
          <img src="/graphics/VCARD_Brand_Logo.svg" alt={t('common.alt.brandLogo', 'VCARD logo')} className="h-8 md:h-10 w-auto dark:hidden brand-logo" />
          <img src="/graphics/VCARD_Brand_Logo_DarkBg.svg" alt={t('common.alt.brandLogo', 'VCARD logo')} className="h-8 md:h-10 w-auto hidden dark:block brand-logo" />
        </div>
      </div>
    </div>
  );
}
